// product-grid-utils.js

window.utils = {
  scrollHandler: null,
  filterParams: "",
  sortParam: "",
  globalUrl: (baseUrl, page, productsPerPage) => {
    return `${baseUrl}&page=${page}&resultsPerPage=${productsPerPage}`;
  },
  paginationData: {},
  currentWrapperData: {},
  gidUrlCache: {},
  infiniteScrollControllers: {},
  parsedMarketingBlocks: {},
  commonFilterFacets: [], // For commonly used filter layout - cache facets for dropdown rendering
  commonFilterDropdownsInitialized: false, // For commonly used filter layout - avoid duplicate event initialization


  // Cached trade program customer status (null = not checked, true/false = cached)
  _isTradeProgramCustomer: null,

  getMarketingContentPosition: (
    alignment = "top_left",
    customHorizontal = 0,
    customVertical = 0
  ) => {
    const positions = {
      top_left: {
        top: "0%",
        right: "auto",
        bottom: "auto",
        left: "0%",
        translateX: "0%",
        translateY: "0%",
      },
      top_center: {
        top: "0%",
        right: "auto",
        bottom: "auto",
        left: "50%",
        translateX: "-50%",
        translateY: "0%",
      },
      top_right: {
        top: "0%",
        right: "0%",
        bottom: "auto",
        left: "auto",
        translateX: "0%",
        translateY: "0%",
      },
      middle_left: {
        top: "50%",
        right: "auto",
        bottom: "auto",
        left: "0%",
        translateX: "0%",
        translateY: "-50%",
      },
      middle_center: {
        top: "50%",
        right: "auto",
        bottom: "auto",
        left: "50%",
        translateX: "-50%",
        translateY: "-50%",
      },
      middle_right: {
        top: "50%",
        right: "0%",
        bottom: "auto",
        left: "auto",
        translateX: "0%",
        translateY: "-50%",
      },
      bottom_left: {
        top: "auto",
        right: "auto",
        bottom: "0%",
        left: "0%",
        translateX: "0%",
        translateY: "0%",
      },
      bottom_center: {
        top: "auto",
        right: "auto",
        bottom: "0%",
        left: "50%",
        translateX: "-50%",
        translateY: "0%",
      },
      bottom_right: {
        top: "auto",
        right: "0%",
        bottom: "0%",
        left: "auto",
        translateX: "0%",
        translateY: "0%",
      },
    };

    if (alignment !== "custom") {
      return positions[alignment] || positions.top_left;
    }

    const horizontal = Math.min(Math.max(Number(customHorizontal) || 0, 0), 100);
    const vertical = Math.min(Math.max(Number(customVertical) || 0, 0), 100);

    return {
      top: `${vertical}%`,
      right: "auto",
      bottom: "auto",
      left: `${horizontal}%`,
      translateX: `${horizontal * -1}%`,
      translateY: `${vertical * -1}%`,
    };
  },

  getMarketingContentStyle: (settings = {}) => {
    const desktop = window.utils.getMarketingContentPosition(
      settings.content_alignment_desktop || "top_left",
      settings.custom_content_horizontal_desktop,
      settings.custom_content_vertical_desktop
    );
    const mobile = window.utils.getMarketingContentPosition(
      settings.content_alignment_mobile || "top_left",
      settings.custom_content_horizontal_mobile,
      settings.custom_content_vertical_mobile
    );

    return `
      --marketing-content-desktop-top: ${desktop.top};
      --marketing-content-desktop-right: ${desktop.right};
      --marketing-content-desktop-bottom: ${desktop.bottom};
      --marketing-content-desktop-left: ${desktop.left};
      --marketing-content-desktop-translate-x: ${desktop.translateX};
      --marketing-content-desktop-translate-y: ${desktop.translateY};
      --marketing-content-mobile-top: ${mobile.top};
      --marketing-content-mobile-right: ${mobile.right};
      --marketing-content-mobile-bottom: ${mobile.bottom};
      --marketing-content-mobile-left: ${mobile.left};
      --marketing-content-mobile-translate-x: ${mobile.translateX};
      --marketing-content-mobile-translate-y: ${mobile.translateY};
    `;
  },

  decodeMarketingRichText(html) {
    if (!html || typeof html !== "string") return html;
    if (!/&lt;|&gt;|&amp;|&quot;|&#\d+;|&#x[\da-f]+;/i.test(html)) {
      return html;
    }
    const textarea = document.createElement("textarea");
    textarea.innerHTML = html;
    return textarea.value;
  },

  // Function to generate marketing block HTML based on type
  generateMarketingBlock: (block) => {
    const { type, settings } = block || {};
    if (!type || typeof type !== "string" || !settings) {
      console.warn("[PLP] Skipping marketing block with invalid type or settings", block);
      return "";
    }
    const {
      background_color = "transparent",
      text_color = "#ffffff",
      image,
      video,
      overlay_color = "rgba(0,0,0,0.3)",
      overlay_opacity = 30,
      overline,
      heading,
      heading_tag = "h3",
      text,
      text_mobile,
      link,
      link_2,
      link_text = "Shop Now",
      // Lifestyle block specific settings
      featured_product,
      row_start_desktop = 3,
      column_start_desktop = 1,
      tooltip_placement = "bottom",
      tooltip_offset = 16,
      focus_point_x = 0,
      focus_point_y = 0,
    } = settings;

    const richText = window.utils.decodeMarketingRichText(text);
    const richTextMobile = window.utils.decodeMarketingRichText(text_mobile);

    // Generate media content
    let mediaContent;
    if (video.url) {
      mediaContent = `
      <div class="video-container">
        <video class="marketing-video" autoplay loop muted playsinline webkit-playsinline>
          <source src="${video.url}" type="video/mp4">
        </video>
      </div>
    `;
    } else {
      mediaContent = `<img src="${
        image || "//via.placeholder.com/800x800"
      }" loading="lazy" alt="${heading || ""}">`;
    }

    // Base classes and attributes
    let blockClasses = [
      "marketing-block",
      `marketing-block--${type.replace("marketing-block--", "")}`,
    ];
    let blockAttributes = "";
    let blockStyles = `background-color: ${background_color}; color: ${text_color}; ${window.utils.getMarketingContentStyle(
      settings
    )}`;

    // Type-specific modifications
    if (type === "marketing-block--wide") {
      blockClasses.push("marketing-block--span-2");
    } else if (type === "marketing-block--life") {
      blockAttributes = `
      data-tooltip-placement="${tooltip_placement}"
      data-tooltip-offset="${tooltip_offset}"
      data-focus-point-x="${focus_point_x}"
      data-focus-point-y="${focus_point_y}"
      data-row-start-desktop="${row_start_desktop}"
      data-column-start-desktop="${column_start_desktop}"
    `;
    }

    // Content with conditional rendering
    const contentHtml = `
    ${
      overline ? `<div class="marketing-block__overline">${overline}</div>` : ""
    }
    ${
      heading
        ? `<${heading_tag} class="marketing-block__heading">${heading}</${heading_tag}>`
        : ""
    }
    ${
      richTextMobile &&
      richTextMobile.trim() !== "" &&
      richTextMobile !== "null"
        ? `<div class="marketing-block__text marketing-block__text--desktop">${richText}</div>
           <div class="marketing-block__text marketing-block__text--mobile">${richTextMobile}</div>`
        : richText
        ? `<div class="marketing-block__text">${richText}</div>`
        : ""
    }
    ${link_text ? `<span class="marketing-block__cta">${link_text}</span>` : ""}
    ${
      featured_product
        ? `<div class="marketing-block__product" data-product-id="${featured_product}"></div>`
        : ""
    }
  `;

    return `
    <div class="${blockClasses.join(
      " "
    )}" style="${blockStyles}" ${blockAttributes}>
      ${
        link
          ? `<a href="${link}" class="marketing-block__link">`
          : '<div class="marketing-block__link">'
      }
        ${
          link_2
            ? `<a href="${link_2}" class="marketing-block__media">`
            : '<div class="marketing-block__media">'
        }
          ${mediaContent}
          ${
            overlay_color
              ? `<div class="marketing-block__overlay" style="background-color: ${overlay_color}; opacity: ${overlay_opacity}%"></div>`
              : ""
          }
        ${link_2 ? "</a>" : "</div>"}
        <div class="marketing-block__content">
          ${contentHtml}
        </div>
      ${link ? "</a>" : "</div>"}
    </div>
  `;
  },

  preventArrowScroll: (e) => {
    const keys = [32, 33, 34, 35, 36, 37, 38, 39, 40]; // space, page up/down, arrows
    if (keys.includes(e.keyCode)) {
      e.preventDefault();
    }
  },

  getSwatchStatus: (() => {
    let lastDataset = null;
    let lastResult = null;

    return function (dataset) {
      // Check if we've already processed this exact dataset object
      if (dataset === lastDataset) {
        return lastResult;
      }

      // Store the new dataset for future checks
      lastDataset = dataset;

      // Find the first truthy showSwatches value in the dataset
      for (const val of Object.values(dataset)) {
        const isShowSwatch = val?.showSwatches?.toLowerCase?.();
        if (isShowSwatch === "true" || isShowSwatch === "false") {
          lastResult = isShowSwatch === "true";
          return lastResult;
        }
      }

      // Default return if no valid value found
      lastResult = null;
      return lastResult;
    };
  })(),

  getAspectRatioFromAnyDataset: (() => {
    let lastValue = null;
    let lastRatio = null;

    const ratios = {
      natural: 1,
      square: 1,
      landscape: 16 / 9,
      portrait: 9 / 16,
      widescreen: 16 / 9,
      ultrawide: 21 / 9,
      standard: 4 / 3,
      cinematic: 2.39 / 1,
      instagram: 4 / 5,
      reels: 9 / 16,
      stories: 9 / 16,
      facebook: 1.91 / 1,
      linkedin: 1.91 / 1,
      youtube: 16 / 9,
      tiktok: 9 / 16,
      pinterest: 2 / 3,
      banner: 3 / 1,
      cover: 2.63 / 1,
    };

    return function (dataset) {
      for (const val of Object.values(dataset)) {
        const aspect = val?.aspectRatio?.toLowerCase?.();
        if (ratios[aspect]) {
          if (aspect === lastValue) return lastRatio;
          lastValue = aspect;
          lastRatio = ratios[aspect];
          return lastRatio;
        }
      }
      return null;
    };
  })(),

  toggleScroll: (freeze = true) => {
    if (freeze) {
      window.utils.scrollHandler = function (e) {
        e.preventDefault();
      };

      window.addEventListener("scroll", window.utils.scrollHandler, {
        passive: false,
      });
      window.addEventListener("wheel", window.utils.scrollHandler, {
        passive: false,
      });
      window.addEventListener("touchmove", window.utils.scrollHandler, {
        passive: false,
      });
      window.addEventListener(
        "keydown",
        window.utils.preventArrowScroll,
        false
      );
    } else {
      window.removeEventListener("scroll", window.utils.scrollHandler);
      window.removeEventListener("wheel", window.utils.scrollHandler);
      window.removeEventListener("touchmove", window.utils.scrollHandler);
      window.removeEventListener("keydown", window.utils.preventArrowScroll);
      window.utils.scrollHandler = null;
    }
  },

  formatPrice: (price) => {
    if (!price) return "";

    const numericPrice = typeof price === "string" ? parseFloat(price) : price;

    // Check if price is a whole number
    if (Number.isInteger(numericPrice)) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(numericPrice);
    }

    // For prices with cents
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericPrice);
  },

  // Determine if a product is a rug
  isRugProduct: function (product) {
    return (
      product?.product_type_unigram?.toLowerCase() === "rug" ||
      product?.name?.toLowerCase().includes("rug")
    );
  },

  // Extract size from product title
  extractSizeFromTitle: function (title) {
    const sizeMatch = title.match(
      /(\d{1,2}(?:'|\u0026quot;)\d{0,2}(?:\u0026quot;)?\s*x\s*\d{1,2}(?:'|\u0026quot;)\d{0,2}(?:\u0026quot;)?)/i
    );
    if (sizeMatch) {
      return sizeMatch[0].replace(/\\u0026quot;/g, '"').replace(/"/g, '"');
    }
    return null;
  },

  // Check and cache trade program customer status (runs only once per page load)
  checkAndCacheTradeProgramCustomer: function () {
    // Return cached value if already checked
    if (window.utils._isTradeProgramCustomer !== null) {
      return window.utils._isTradeProgramCustomer;
    }

    // B2B tag from Theme Settings (blank = B2B logic off)
    const b2bTag = (window.ShopifyThemeSettings && window.ShopifyThemeSettings.b2bCustomerTag) ? String(window.ShopifyThemeSettings.b2bCustomerTag).trim() : '';
    if (!b2bTag) {
      window.utils._isTradeProgramCustomer = false;
      return false;
    }

    const b2bTagLower = b2bTag.toLowerCase();

    // Initialize as false (not a trade customer)
    let isTradeProgramCustomer = false;
    const wrapperData = this.currentWrapperData;

    if (wrapperData) {
      // Check all wrapper data entries for B2B tag flag
      const wrapperKeys = Object.keys(wrapperData);
      for (let i = 0; i < wrapperKeys.length; i++) {
        const key = wrapperKeys[i];
        const data = wrapperData[key];

        if (data) {
          // ========== CHECK 1: Is Customer Logged In? ==========
          const customerId = data.customerId || data.customer_id;
          const isCustomerLoggedIn = customerId && customerId !== '' && customerId !== 'null' && customerId !== 'undefined';

          // Early exit if customer is not logged in
          if (!isCustomerLoggedIn) {
            window.utils._isTradeProgramCustomer = false;
            return false;
          }

          // ========== CHECK 2: Does Customer Have B2B Tag? ==========
          const customerTags = data.customerTags || data.customer_tags || '';
          const tagsArray = customerTags ? String(customerTags).split(',').map(t => t.trim()) : [];
          const hasTradeProgramTag = tagsArray.some(tag => tag.toLowerCase() === b2bTagLower);

          // Check multiple possible property name formats
          const tradeprogramValue =
            data.isTradeprogramCustomer ||
            data.isTradeprogramcustomer ||
            data['is-tradeprogram-customer'];

          // Check if value indicates B2B customer
          const isTradeProgram =
            tradeprogramValue === "true" ||
            tradeprogramValue === true ||
            tradeprogramValue === "True" ||
            String(tradeprogramValue).toLowerCase() === "true";

          // Also check customer tags directly if available
          if (!isTradeProgram && data.customerTags) {
            const tags = String(data.customerTags).split(',').map(t => t.trim().toLowerCase());
            const hasTradeProgramTag = tags.includes(b2bTagLower);
            if (hasTradeProgramTag) {
              isTradeProgramCustomer = true;
              break; // Found trade customer, exit loop
            }
          } else if (isTradeProgram) {
            isTradeProgramCustomer = true;
            break; // Found trade customer, exit loop
          }
        }
      }
    }

    // Cache the result
    window.utils._isTradeProgramCustomer = isTradeProgramCustomer;

    return isTradeProgramCustomer;
  },

  isVariantPurchasable: function (variant) {
    if (!variant) return false;
    const qty = Number(variant?.inventory_quantity);
    if (Number.isFinite(qty) && qty > 0) return true;
    const policy = String(variant?.inventory_policy || "").toLowerCase();
    return policy === "continue";
  },

  isVariantInStock: function (variant) {
    return this.isVariantPurchasable(variant);
  },

  /**
   * Compare variant price / compare_at_price in one unit (dollars).
   * SearchSpring sends decimal dollar strings (e.g. "1465.00"); Storefront JSON
   * often uses integer cents (e.g. 146500). The old `> 1000 ? / 100` rule wrongly
   * treated dollar amounts over $1000 as cents, hiding sales on the PLP.
   */
  variantMoneyToNumber: function (value) {
    if (value == null || value === "") return 0;
    const s = String(value).trim();
    if (!s) return 0;
    const num = parseFloat(s);
    if (!Number.isFinite(num) || num < 0) return 0;
    if (/\.\d/.test(s)) return num;
    if (num > 1000 && num % 1 === 0) return num / 100;
    return num;
  },

  parseProductPrice: function (priceStr) {
    if (priceStr == null || String(priceStr).trim() === "") return null;
    const cleaned = String(priceStr).trim().replace(/\.00$/, "");
    const parsed = Number(cleaned.replace(/[^0-9.]/g, ""));
    return isNaN(parsed) ? null : parsed;
  },

  getProductSaleInfo: function (product, displayPrice, inStockVariants) {
    const safeVariants = inStockVariants || [];

    const variantComparePrices = safeVariants
      .map((v) => this.variantMoneyToNumber(v.compare_at_price))
      .filter((p) => p > 0);

    const compareAtPrice =
      this.parseProductPrice(product?.msrp) ||
      this.parseProductPrice(product?.variant_compare_at_price) ||
      (variantComparePrices.length > 0
        ? Math.min(...variantComparePrices)
        : null);

    const isOnSaleFromVariants = safeVariants.some(
      (v) =>
        this.variantMoneyToNumber(v.compare_at_price) >
        this.variantMoneyToNumber(v.price)
    );

    const price =
      typeof displayPrice === "number" && displayPrice > 0
        ? displayPrice
        : this.parseProductPrice(product?.price) || 0;

    const isOnSaleFromProduct =
      compareAtPrice && price > 0 && compareAtPrice > price;

    return {
      isOnSale: isOnSaleFromVariants || isOnSaleFromProduct,
      compareAtPrice,
    };
  },

  getInStockVariants: function (variants) {
    if (!Array.isArray(variants)) return [];
    return variants.filter((variant) => this.isVariantInStock(variant));
  },

  getVariantDisplayImage: function (variant) {
    if (!variant) return null;
    const directImage = variant?.image ?? variant?.variant_image;
    if (directImage) {
      const normalized = String(directImage).trim().replace(/^["']|["']$/g, "");
      if (
        normalized &&
        !/^gid:\/\//.test(normalized) &&
        (/^https?:\/\//.test(normalized) ||
          normalized.startsWith("//") ||
          normalized.includes("/"))
      ) {
        return normalized;
      }
    }

    const galleryRaw = variant?.mfield_custom_media_gallery;
    if (!galleryRaw) return null;

    const entries = String(galleryRaw)
      .split(",")
      .map((s) => s.trim().replace(/^["']|["']$/g, ""))
      .filter(Boolean);

    const galleryCandidate = entries.find(
      (entry) =>
        !/^gid:\/\//.test(entry) &&
        (/^https?:\/\//.test(entry) ||
          entry.startsWith("//") ||
          entry.includes("/"))
    );
    return galleryCandidate || null;
  },

  getPreferredInStockVariantImage: function (product, preferredVariantId) {
    if (!product) return null;

    let combinedVariantsString = "";
    if (Array.isArray(product?.variants)) {
      if (product.variants.length > 1) {
        combinedVariantsString = product.variants.join("");
      } else if (product.variants.length === 1) {
        combinedVariantsString = product.variants[0] || "";
      }
    } else if (product?.variants?.[0]) {
      combinedVariantsString = product.variants[0];
    }
    if (!combinedVariantsString) return null;

    const parsedVariants =
      this.parseHtmlEncodedJsonString(combinedVariantsString) || [];
    const inStockVariants = this.getInStockVariants(parsedVariants);
    if (!inStockVariants.length) return null;

    const normalizedPreferredId =
      preferredVariantId != null ? String(preferredVariantId).trim() : "";
    if (normalizedPreferredId) {
      const preferredVariant = inStockVariants.find((variant) => {
        const variantIdRaw = variant?.id ?? variant?.variant_id ?? "";
        const variantId = String(variantIdRaw).trim();
        const normalizedVariantDigits = variantId.replace(/\D/g, "");
        const normalizedPreferredDigits = normalizedPreferredId.replace(/\D/g, "");

        return (
          variantId === normalizedPreferredId ||
          (normalizedVariantDigits &&
            normalizedPreferredDigits &&
            normalizedVariantDigits === normalizedPreferredDigits)
        );
      });

      const preferredImage = this.getVariantDisplayImage(preferredVariant);
      if (preferredImage) return preferredImage;
    }

    for (let i = 0; i < inStockVariants.length; i++) {
      const image = this.getVariantDisplayImage(inStockVariants[i]);
      if (image) return image;
    }

    return null;
  },

  // Generate price HTML
  generatePriceHTML: function (product, options = {}) {
    const variants = this.parseHtmlEncodedJsonString(product?.variants?.[0]);
    const safeVariants = Array.isArray(variants) ? variants : [];
    const inStockVariants = this.getInStockVariants(safeVariants);

    // Helper function to safely parse and clean prices
    const parsePrice = (priceStr) => {
      if (!priceStr || priceStr.trim() === "") return null;
      const cleaned = priceStr.trim().replace(/\.00$/, "");
      const parsed = Number(cleaned.replace(/[^0-9.]/g, ""));
      return isNaN(parsed) ? null : parsed;
    };

    // Check trade program customer status (cached - runs only once)
    const isTradeProgramCustomer = this.checkAndCacheTradeProgramCustomer();

    // Keep legacy variant_price source as fallback when in-stock variant prices are missing.
    const variantsPrice = product?.variant_price
      ?.split("|")
      .map((item) => parsePrice(item))
      .filter((price) => price !== null && price > 0);
    const inStockVariantPrices = inStockVariants
      .map((variant) => parsePrice(String(variant?.price ?? "")))
      .filter((price) => price !== null && price > 0);
    const distinctInStockPriceCount = new Set(inStockVariantPrices).size;
    const effectiveVariantPrices =
      inStockVariantPrices.length > 0 ? inStockVariantPrices : variantsPrice;

    const currentPrice = parsePrice(product.price);
    const fallbackDisplayPrice =
      effectiveVariantPrices?.length > 0
        ? Math.min(...effectiveVariantPrices)
        : currentPrice;
    const { isOnSale, compareAtPrice: originalPrice } = this.getProductSaleInfo(
      product,
      fallbackDisplayPrice,
      inStockVariants
    );

    const isCustomRugFlow = product.mfield_custom_custom_rug_flow;

    // B2B Price Logic (only if customer is trade program customer)
    let b2bPrice = null;
    if (isTradeProgramCustomer) {
      // Try to get B2B price from product level variant metafield
      const productB2BPrice = product.variant_mfield_custom_b2b_price;

      if (productB2BPrice) {
        const parsedB2BPrice = parsePrice(productB2BPrice);
        if (parsedB2BPrice && parsedB2BPrice > 0) {
          b2bPrice = parsedB2BPrice;
        }
      }

      // If not found at product level, check variants array for lowest B2B price
      if (!b2bPrice && inStockVariants.length > 0) {
        const b2bPrices = inStockVariants
          .map((v) => {
            const variantB2BPrice = v.mfield_custom_b2b_price || v.variant_mfield_custom_b2b_price;
            if (variantB2BPrice) {
              return parsePrice(variantB2BPrice);
            }
            return null;
          })
          .filter((p) => p !== null && p > 0);

        if (b2bPrices.length > 0) {
          b2bPrice = Math.min(...b2bPrices);
        }
      }
    }

    const freeShippingHTML = options?.showMobileFreeShipping
      ? `<p class="product-card__free-shipping-mobile" style="margin:4px 0; line-height: 1; font-size: 11px;">Free Shipping</p>`
      : "";

    // Use B2B price if available, otherwise use normal price logic
    if (b2bPrice && b2bPrice > 0) {
      // Show MSRP as strikeout if it exists and is higher than B2B price
      let msrpHTML = '';
      if (originalPrice && originalPrice > b2bPrice) {
        msrpHTML = `<span class="original-price" style="text-decoration: line-through; margin-left: 5px;">${utils.formatPrice(originalPrice)}</span>`;
      }
      
      // Return B2B price with Trade Price label and MSRP strikeout
      return `<div class="product-card__price">
        <div class="product-card__price">
          <span style="color: #fd619c;">${utils.formatPrice(b2bPrice)}</span>
          ${msrpHTML}
        </div>
        <p style="margin: 0; line-height: 1; color: #fd619c; font-size: 11px;">Trade Price</p>
        ${freeShippingHTML}
      </div>`;
    }

    // Case 1: No valid prices available
    if ((!effectiveVariantPrices || effectiveVariantPrices.length === 0) && !currentPrice && !originalPrice) {
      return freeShippingHTML;
    }

    // Case 2: No variant prices - show original price
    if (!effectiveVariantPrices || effectiveVariantPrices.length === 0) {
      return `<span class="${isOnSale ? "sale-price" : ""}">${utils.formatPrice(
        originalPrice
      )}</span>${freeShippingHTML}`;
    }

    // Calculate min/max prices from variants
    const minPrice = Math.min(...effectiveVariantPrices);
    const hasMultipleInStockDisplayPrices = distinctInStockPriceCount > 1;
    const fromText = hasMultipleInStockDisplayPrices ? "from " : "";

    // Case 3: Custom rug flow - special pricing calculation
    if (isCustomRugFlow) {
      const rugLength = 2;
      const rugWidth = 5;
      const customRugPrice = minPrice * rugLength * rugWidth;
      return `<span class="${isOnSale ? "sale-price" : ""}">${
        hasMultipleInStockDisplayPrices ? "from " : ""
      }${utils.formatPrice(customRugPrice)}</span>${freeShippingHTML}`;
    }

    // Case 4: Standard price display
    // const isRug = this.isRugProduct(product);
    const isRug = product.product_type_unigram === "rug";
    const formattedPrice = utils.formatPrice(minPrice);
    const safePrice =
      formattedPrice === "$0" ? `$${effectiveVariantPrices[0]}` : formattedPrice;

    let priceHTML = "";

    if (isOnSale && originalPrice && minPrice < originalPrice) {
      priceHTML = `<span class="original-price" style="text-decoration: line-through; margin-right: 5px;">${utils.formatPrice(
        originalPrice
      )}</span>`;
    }

    priceHTML += `<span class="${
      isOnSale ? "sale-price" : ""
    }">${fromText}${safePrice}</span>`;

    return `${priceHTML}${freeShippingHTML}`;
  },

  validateSwatchImages: async function () {
    // Helper function to get dominant color from image
    const getHexColorFromImage = (imageUrl) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.src = imageUrl;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          const targetSize = 50; // Resize image for performance
          canvas.width = targetSize;
          canvas.height = targetSize;
          ctx.drawImage(img, 0, 0, targetSize, targetSize);

          const imageData = ctx.getImageData(0, 0, targetSize, targetSize);
          const data = imageData.data;

          let rTotal = 0,
            gTotal = 0,
            bTotal = 0,
            count = 0;

          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const alpha = data[i + 3];

            // Ignore transparent and near-white pixels (likely background)
            if (alpha < 128 || (r > 240 && g > 240 && b > 240)) continue;

            rTotal += r;
            gTotal += g;
            bTotal += b;
            count++;
          }

          if (count === 0) {
            reject("No valid pixels found");
            return;
          }

          const avgR = Math.round(rTotal / count);
          const avgG = Math.round(gTotal / count);
          const avgB = Math.round(bTotal / count);

          const rgbToHex = (r, g, b) =>
            "#" +
            [r, g, b].map((val) => val.toString(16).padStart(2, "0")).join("");

          resolve(rgbToHex(avgR, avgG, avgB));
        };

        img.onerror = () => reject("Image load error");
      });
    };

    // Helper function to validate CSS color strings
    function isColorValid(color) {
      if (!color || typeof color !== "string") return false;

      const tempElement = document.createElement("div");
      tempElement.style.color = "transparent";
      tempElement.style.color = color;

      return tempElement.style.color !== "transparent";
    }

    // Helper function to check if image loads successfully
    async function isImageValid(imgUrl) {
      return new Promise((resolve) => {
        const testImg = new Image();
        testImg.crossOrigin = "Anonymous";
        testImg.src = imgUrl;

        testImg.onload = () => resolve(true);
        testImg.onerror = () => resolve(false);
      });
    }

    const swatches = document.querySelectorAll(".new-swatch");
    if (!swatches.length) return;

    const processSwatch = async (swatch) => {
      const imageUrl = swatch.style.backgroundImage.replace(
        /url\(['"]?(.*?)['"]?\)/i,
        "$1"
      );
      const fallbackImage = swatch.dataset.fallbackImage;
      const actualColor = swatch.dataset.actualColor || "";
      const configImage = swatch.dataset.configImage || "";
      const titles = swatch.dataset.variantTitles || "";

      if (!imageUrl && !fallbackImage) return;

      // First check if the image loads successfully
      const imageLoads = await new Promise((resolve) => {
        const testImg = new Image();
        testImg.crossOrigin = "Anonymous";
        testImg.src = imageUrl;
        testImg.onload = () => resolve(true);
        testImg.onerror = () => resolve(false);
      });

      if (imageLoads) return; // Image is valid - no action needed

      let firstValidImage = null;
      if (titles) {
        const cleanTitles = titles.replace(/"/g, "").replace(/\s*=\s*/g, "");

        const imgTitles = cleanTitles
          .split("|")
          .map((title) => title.trim())
          .filter((title) => title && title !== "");

        const formattedTitles = Array.isArray(imgTitles)
          ? imgTitles
              .map((title) =>
                title && typeof title === "string"
                  ? title
                      .toLowerCase()
                      .replace(/[,/]/g, "") // remove ',' and '/'
                      .split(" ")
                      .join("-") // replace spaces with '-'
                      .replace(/-+/g, "-") // replace multiple '-' with single '-'
                  : ""
              )
              .filter((title) => title)
          : [];

        // Generate potential image URLs
        const potentialImageUrls = formattedTitles.map(
          (title) =>
            `//abchome.com/cdn/shop/files/${title
              .toLowerCase()
              .replace(/\s+/g, "-")}_64x64.png`
        );

        // Find the first valid image
        for (const url of potentialImageUrls) {
          if (await isImageValid(url)) {
            firstValidImage = url;
            break;
          }
        }
      }

      // Only proceed with fallback logic if image fails to load
      try {
        const actualColorValid = isColorValid(actualColor);
        const fallbackHex = await getHexColorFromImage(fallbackImage);

        // Customize this condition to reject unwanted colors
        const fallbackColorValid =
          fallbackHex.toLowerCase() !== "#ffffff" &&
          fallbackHex.toLowerCase() !== "#000000";

        if (fallbackColorValid) {
          if (actualColorValid) {
            swatch.style.backgroundColor = actualColor;
            swatch.style.backgroundImage = firstValidImage
              ? `url(${firstValidImage})`
              : configImage;
          } else {
            swatch.style.backgroundImage = "none";
            swatch.style.backgroundColor = fallbackHex;
          }
        } else {
          swatch.style.backgroundImage = `url(${fallbackImage})`;
        }
      } catch (error) {
        console.error("Error processing swatch:", error);
        swatch.style.backgroundImage = `url(${fallbackImage})`;
      }
    };

    // Modern browsers: lazy-validate swatches as they enter the viewport
    if ("IntersectionObserver" in window) {
      const processed = new WeakSet();
      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            const swatch = entry.target;
            if (processed.has(swatch)) {
              obs.unobserve(swatch);
              return;
            }

            processed.add(swatch);
            obs.unobserve(swatch);
            // Fire-and-forget; no need to block scroll/paint
            processSwatch(swatch);
          });
        },
        {
          rootMargin: "100px",
          threshold: 0.01,
        }
      );

      Array.from(swatches).forEach((swatch) => observer.observe(swatch));
      return;
    }

    // Fallback: batch processing to avoid flooding image requests
    const swatchArray = Array.from(swatches);
    const batchSize = 20;
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    for (let i = 0; i < swatchArray.length; i += batchSize) {
      const batch = swatchArray.slice(i, i + batchSize);
      // Limit concurrent work per frame
      await Promise.all(batch.map((swatch) => processSwatch(swatch)));
      await delay(16); // ~1 frame
    }
  },

  // Generate swatches HTML
  generateSwatchesHTML: function (variantData, product, variantId) {
    const { options, map, images, swatchImages, colors, urls, titles } =
      variantData;
    const isRug = this.isRugProduct(product);
    const isCustomRugFlow = product.mfield_custom_custom_rug_flow;
    // const isCylindo = product.mfield_custom_cylindo_code;
    const sidebarButton = document.querySelector(".filter-toggle-btn");
    const isSidebarActive =
      sidebarButton && sidebarButton.className.includes("active");

    const wrapperDataset = this.currentWrapperData;
    const isShowSwatch = this.getSwatchStatus(wrapperDataset);

    const isFurniture = Object.values(this.currentWrapperData)?.at(0)?.collectionHandle === "furniture" || product.template_suffix === "furniture_new"

    if (
      options.length <= 1 ||
      isRug ||
      isCustomRugFlow ||
      // isCylindo ||
      !isShowSwatch
    ) {
      return "";
    }

    const swatchesData =
      this.parseHtmlEncodedJsonString(product.ss_swatches) || [];

    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    const swatchCount = isMobile ? 3 : 5;

    return `
      <div class="product-card__swatches" onmouseleave="window.utils.handleVariantSwatchLeave(event, this)" >
        ${options
          .slice(0, swatchCount)
          .map((variant, index) => {
            const swatch =
              swatchesData.find((s) =>
                variant.toLowerCase().includes(s.color.toLowerCase())
              ) || {};
            let colorCode =
              (() => {
                try {
                  return JSON.parse(colors?.[variant])[0];
                } catch {
                  return colors?.[variant];
                }
              })() || "";
            const imageUrl = isFurniture ? variantData?.images[variant] : swatchImages[variant] || "";
            const fallbackImage = variantData?.images[variant];
            const areFirstTwoSame = (() => {
              const entries = Object.values(titles);
              if (entries.length === 0) return true; // Empty object = trivially true
              const [first, second] = entries[0]; // reference values

              return entries.every(([a, b]) => a === first && b === second);
            })();

            const variantTitles = areFirstTwoSame ? [] : titles[variant] || [];

            const hasImageMoreThanThreeTimes = Object.values(variantData.swatchImages).some((img, _, arr) => arr.filter(i => i === img).length > 3);

            return `
<span 
  class="new-swatch ${isSidebarActive ? "active" : ""}" 
  style="background-color: ${colorCode}; background-image: url('${hasImageMoreThanThreeTimes ? "" : swatchImages?.[variant]}'); background-size: cover; background-position: center; background-repeat: no-repeat;" 
  data-variant="${variant}"
  data-variant-id="${map[variant] || variantId}"
  data-image="${swatch.image || imageUrl}"
  data-hover-image="${swatch.image || imageUrl}"
  data-url="${urls[variant] || ""}"
  data-fallback-image="${fallbackImage}"
  data-actual-color="${colorCode}"
  data-config-image="${imageUrl || ""}"
  data-variant-titles="${(variantTitles || [])
    .map((title) => (title ? title.replace(/"/g, "&quot;") : ""))
    .join("|")}"
  onmouseenter="window.utils.handleVariantSwatchHover(event, this)"
  onclick="window.utils.handleVariantSwatchClick(event, this)">
</span>
          `;
          })
          .join("")}
        ${
          options.length > swatchCount
            ? `
          <div style="position: relative;">
            <div style="width: 23px; height: 23px; overflow: hidden; border-radius: 50%; position: relative; visibility: hidden;">
              <span style="background-image:url(${
                product.thumbnailImageUrl || ""
              }); background-size: cover; background-position: center; background-repeat: no-repeat;" class=" swatch--more"></span>
            </div>
            <span class="swatch_more_text">+${
              options.length - swatchCount 
            } Options</span>
          </div>
        `
            : ""
        }
      </div>
    `;
  },

  // Generate customize button HTML
  generateCustomizeButton: function (product) {
    const isCustomRugFlow = product.mfield_custom_custom_rug_flow;
    const isCylindo = product.mfield_custom_cylindo_code;

    if (!isCustomRugFlow) {
      return "";
    }
    const wrapper = document.querySelector("body");

    if (wrapper.getAttribute("handle-template") == "furniture_2024") {
      return "";
    }
    return `
      <div class="customize-button" ${isCylindo ? 'data-cylindo="true"' : ""}>
        <span class="customize-icon">
          ${
            isCustomRugFlow
              ? `<svg class="maximize-icon" xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <polyline points="17 3 21 3 21 7"></polyline>
  <line x1="21" y1="3" x2="14" y2="10"></line>
  <polyline points="7 21 3 21 3 16"></polyline>
  <line x1="3" y1="21" x2="10" y2="14"></line>
  <polyline points="3 8 3 3 8 3"></polyline>
  <line x1="3" y1="3" x2="10" y2="10"></line>
  <polyline points="21 16 21 21 16 21"></polyline>
  <line x1="21" y1="21" x2="14" y2="14"></line>
</svg>
          `
              : `
            <svg style="transform: rotate(-76deg);" class="color-wheel-icon" width="120" height="120" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
              <g transform="translate(60,60)">
                <defs>
                  <mask id="donut-mask">
                    <rect x="-60" y="-60" width="120" height="120" fill="white"/>
                    <circle cx="0" cy="0" r="20" fill="black"/>
                  </mask>
                </defs>
                <g mask="url(#donut-mask)">
                 <path d="M0,0 L0,-50 A50,50 0 0,1 38.27,-38.27 Z" fill="#F980A1"/> <!-- Pink -->
                <path d="M0,0 L38.27,-38.27 A50,50 0 0,1 50,0 Z" fill="#F44336"/> <!-- Red -->
                <path d="M0,0 L50,0 A50,50 0 0,1 38.27,38.27 Z" fill="#F9A825"/> <!-- Orange -->
                <path d="M0,0 L38.27,38.27 A50,50 0 0,1 0,50 Z" fill="#FDD180"/> <!-- Peach -->
                <path d="M0,0 L0,50 A50,50 0 0,1 -38.27,38.27 Z" fill="#8BC34A"/> <!-- Light Green -->
                <path d="M0,0 L-38.27,38.27 A50,50 0 0,1 -50,0 Z" fill="#4CAF50"/> <!-- Green -->
                <path d="M0,0 L-50,0 A50,50 0 0,1 -38.27,-38.27 Z" fill="#4B7C83"/> <!-- Teal -->
                <path d="M0,0 L-38.27,-38.27 A50,50 0 0,1 0,-50 Z" fill="#B3DCE3"/> <!-- Light Blue -->
                </g>
              </g>
            </svg>
          `
          }
        </span>
        <span class="customize-text">Customize your selection</span>
      </div>
    `;
  },

  generateSizeForFurniture: function (product) {
    const wrapper = document.querySelector("body");

    if (
      (!wrapper ||
      wrapper.getAttribute("handle-template") !== "furniture_2024") && product.template_suffix !== "furniture_new"
    ) {
      return "";
    }

    const sizesRaw = product.variant_size || "";
    const sizes = sizesRaw.includes("|")
      ? sizesRaw.split("|")
      : [sizesRaw.trim()];

    if (sizes.length <= 1) {
      return "";
    }

    return `
      <div class="customize-button">
        <span class="customize-text">Multiple Sizes Available</span>
      </div>
    `;
  },

  // Generate size text
  generateSizeText: function (product) {
    const sizesRaw = product.variant_size || "";
    const sizes = sizesRaw.includes("|")
      ? sizesRaw.split("|")
      : [sizesRaw.trim()];

    let sizeText =
      sizes.length > 1
        ? "Multiple sizes available"
        : this.extractSizeFromTitle(product.name) || sizes[0];

    return sizeText ? `<div class="product-card__size">${sizeText}</div>` : "";
  },

  /**
   * Resolve Shopify MediaImage GID to CDN URL via Storefront API.
   * Uses 800x800 for crisp display on product cards.
   * @param {string} gid - e.g. "gid://shopify/MediaImage/30625247199370"
   * @returns {Promise<string|null>} CDN URL or null
   */
  resolveMediaGidToUrl: async function(gid) {
    if (!gid || typeof gid !== "string" || !/^gid:\/\/shopify\/MediaImage\//.test(gid)) {
      return null;
    }
    if (this.gidUrlCache[gid]) {
      return this.gidUrlCache[gid];
    }
    const escapedGid = String(gid).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    const tryVersion = async (version, useTransform) => {
      const urlField = useTransform
        ? "url(transform: { maxWidth: 800, maxHeight: 800 })"
        : "url";
      const query = `{ node(id: "${escapedGid}") { ... on MediaImage { image { ${urlField} } } } }`;
      const res = await fetch(`/api/${version}/graphql.json`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": "79bd06cd08b4eb7c99c9d14513eade1e" },
        body: JSON.stringify({ query }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data?.errors) return null;
      return data?.data?.node?.image?.url || null;
    };
    try {
      let url = await tryVersion("2024-10", true);
      if (!url) url = await tryVersion("2024-01", false);
      if (url) this.gidUrlCache[gid] = url;
      return url || null;
    } catch (e) {
      return null;
    }
  },

  /**
   * Find all img[data-pending-media-gid] in container, resolve GIDs via API, update src.
   */
  resolvePendingMediaGids: async function(container) {
    if (!container) return;
    const imgs = container.querySelectorAll(
      "img[data-pending-media-gid], img[data-pending-hover-gid]"
    );
    if (!imgs.length) return;
    const gids = [
      ...imgs
        .map((img) => img.getAttribute("data-pending-media-gid"))
        .filter(Boolean),
      ...imgs
        .map((img) => img.getAttribute("data-pending-hover-gid"))
        .filter(Boolean),
    ];
    const seen = new Set();
    const uniqueGids = gids.filter((g) => {
      if (seen.has(g)) return false;
      seen.add(g);
      return true;
    });
    const gidToUrl = {};
    await Promise.all(
      uniqueGids.map(async (gid) => {
        const url = await this.resolveMediaGidToUrl(gid);
        if (url) gidToUrl[gid] = url;
      })
    );
    imgs.forEach((img) => {
      const mainGid = img.getAttribute("data-pending-media-gid");
      const hoverGid = img.getAttribute("data-pending-hover-gid");
      const mainUrl = mainGid ? gidToUrl[mainGid] : null;
      const hoverUrl = hoverGid ? gidToUrl[hoverGid] : null;

      if (mainUrl) {
        img.src = mainUrl;
        img.setAttribute("data-original-image", mainUrl);
        img.removeAttribute("data-pending-media-gid");
      }
      if (hoverUrl) {
        img.setAttribute("data-hover-image", hoverUrl);
        img.removeAttribute("data-pending-hover-gid");
      }
    });
  },

  // Helper function to fetch product handle from GID using Storefront API
  fetchProductHandleFromGid: async function(gid) {
    try {
      // Validate GID format
      if (!gid || typeof gid !== 'string' || !gid.includes('gid://shopify/Product/')) {
        return null;
      }

      const query = `{
        product(id: "${gid}") {
          handle
          availableForSale
          publishedAt
        }
      }`;
      
      const response = await fetch('https://abchome.com/api/2023-10/graphql.json', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': '79bd06cd08b4eb7c99c9d14513eade1e'
        },
        body: JSON.stringify({ query })
      });
      
      if (!response.ok) {
        return null;
      }
      
      const data = await response.json();

      // Check for GraphQL errors or missing product
      if (data.errors || !data?.data?.product) {
        return null;
      }
      
      return data.data.product.handle;
    } catch (error) {
      return null;
    }
  },

  // Generate Add Sample button — custom-sized-rugs PLP only
  generateAddSampleButton: function (product) {
    const wrapper = document.querySelector('.searchspring-wrapper');
    const collectionHandle = wrapper?.getAttribute("data-collection-handle") || "";

    if (collectionHandle !== "custom-sized-rugs") {
      return "";
    }

    // Get sample product GID from metafields (indexed by SearchSpring)
    const sampleGid = product.mfield_custom_rug_swatch || product.mfield_custom_upsell_swatch;
    
    // Validate GID format before showing button
    if (!sampleGid || typeof sampleGid !== 'string') {
      return "";
    }
    
    // Basic GID validation
    if (!sampleGid.includes('gid://shopify/Product/')) {
      return "";
    }
    
    const currentColor = product.ss_id || product.name.split(' - ').pop(); // Get current color variant
  
    return `
      <div class="product-card__add-sample">
        <button 
          type="button" 
          class="custom-sample-button"
          data-sample-gid="${sampleGid}"
          data-product-handle="${product.handle}"
          data-product-color="${currentColor}">
          + Add Sample
        </button>
      </div>
    `;
  },

  generateProductCard: function (product) {
    const isCustomRugFlow = product.mfield_custom_custom_rug_flow;
    const isSlicedProduct = product.mfield_new_my_fields_product_slice;

    // console.log(isCustomRugFlow && JSON.parse(product?.mfield_custom_custom_rug_flow.replace(/&quot;/g, '"')));
    // console.log(isCustomRugFlow && product);

    // If it's a custom rug flow product, generate cards for each variant

    if ((isCustomRugFlow || isSlicedProduct === "true") && product?.ss_swatches) {
      return this.generateCustomRugFlowCards(product);
    }

    // Otherwise generate the regular product card
    return this.generateRegularProductCard(product);
  },

  decodeHtmlEntities: function (value) {
    const str = String(value || "");
    if (!str) return "";
    return str
      .replace(/&gt;/gi, ">")
      .replace(/&lt;/gi, "<")
      .replace(/&amp;/gi, "&")
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'");
  },

  normalizeWord: function (word) {
    const w = String(word || "").toLowerCase().trim();
    if (!w) return "";
    return w.length > 1 && w.endsWith("s") ? w.slice(0, -1) : w;
  },

  normalizeQueryWords: function (query) {
    return String(query || "")
      .toLowerCase()
      .split(/[\s,]+/)
      .map((word) => this.normalizeWord(word))
      .filter(Boolean);
  },

  normalizeProductTypeTokens: function (productType) {
    return this.decodeHtmlEntities(productType)
      .toLowerCase()
      .split(/[>+]/)
      .map((token) => token.trim())
      .filter(Boolean);
  },

  queryMatchesProductType: function (queryWords, productTypeTokens) {
    if (!Array.isArray(queryWords) || !Array.isArray(productTypeTokens)) return false;
    for (let i = 0; i < queryWords.length; i++) {
      const queryWord = this.normalizeWord(queryWords[i]);
      if (!queryWord) continue;
      for (let j = 0; j < productTypeTokens.length; j++) {
        const normalizedToken = this.normalizeWord(productTypeTokens[j]);
        if (!normalizedToken) continue;
        if (
          normalizedToken.includes(queryWord) ||
          queryWord.includes(normalizedToken)
        ) {
          return true;
        }
      }
    }
    return false;
  },

  /**
   * Derive a variant image based on a "color + product type" search query.
   * - Read-only: does not change search / filter logic or API calls.
   * - Only active on Search pages where `data-search-terms` is present.
   * - Always falls back safely to the default image on any issue.
   */
  getVariantImageFromSearchQuery: function (product, defaultImageUrl) {
    try {
      const wrapperDataAny = Object.values(this.currentWrapperData)?.[0] || {};
      const rawSearchTerms = wrapperDataAny?.searchTerms || "";

      if (!rawSearchTerms || typeof rawSearchTerms !== "string") {
        return defaultImageUrl;
      }

      const typeSynonyms = {
        sectional: "sofa",
        sectionals: "sofa",
      };
      const queryWords = this.normalizeQueryWords(rawSearchTerms).map(
        (word) => typeSynonyms[word] || word
      );

      // Only attempt enhanced behavior when the query is at least "color + type"
      if (queryWords.length < 2) return defaultImageUrl;

      const productTypeTokens = this.normalizeProductTypeTokens(
        product?.product_type || ""
      ).map((token) => typeSynonyms[this.normalizeWord(token)] || token);

      if (!productTypeTokens.length) {
        return defaultImageUrl;
      }

      const hasTypeToken = this.queryMatchesProductType(
        queryWords,
        productTypeTokens
      );

      // If the query does not reference this product type, bail out
      if (!hasTypeToken) {
        return defaultImageUrl;
      }

      // Mirror the variant JSON combining logic from processVariants
      let combinedVariantsString = "";
      if (Array.isArray(product?.variants)) {
        if (product.variants.length > 1) {
          combinedVariantsString = product.variants.join("");
        } else if (product.variants.length === 1) {
          combinedVariantsString = product.variants[0] || "";
        }
      } else if (product?.variants?.[0]) {
        combinedVariantsString = product.variants[0];
      }

      if (!combinedVariantsString) {
        return defaultImageUrl;
      }

      const allVariants =
        this.parseHtmlEncodedJsonString(combinedVariantsString) || [];

      if (!Array.isArray(allVariants) || allVariants.length === 0) {
        return defaultImageUrl;
      }

      const matchColorToken = (colorName) => {
        if (!colorName) return false;
        const colorLower = colorName.toLowerCase().trim();
        if (!colorLower) return false;
        return queryWords.some((t) => {
          if (!t) return false;
          return (
            t === colorLower ||
            colorLower.includes(t) ||
            t.includes(colorLower)
          );
        });
      };

      let matchedColorLabel = "";
      const matchedVariant = allVariants.find((variant) => {
        const rawColor = variant?.mfield_new_my_fields_filter_color;
        if (!rawColor) return false;

        let colorLabel = "";
        try {
          let normalized = String(rawColor).replace(/&quot;/g, '"');
          const parsed = JSON.parse(normalized);
          if (Array.isArray(parsed)) {
            colorLabel = parsed[0] != null ? String(parsed[0]) : "";
          } else if (parsed != null) {
            colorLabel = String(parsed);
          }
        } catch (e) {
          colorLabel = String(rawColor);
        }

        const isMatch = matchColorToken(colorLabel);
        if (isMatch) {
          matchedColorLabel = colorLabel;
        }
        return isMatch;
      });

      if (!matchedVariant) {
        // No color match: still prefer first image from gallery (index 0) when available
        // so products show gallery[0] instead of SearchSpring default (which may be 2nd image)
        const anyWithGallery = allVariants.find(
          (v) => v?.mfield_custom_media_gallery
        );
        if (anyWithGallery) {
          const entries = String(anyWithGallery.mfield_custom_media_gallery)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
          const firstEntry = entries[1] ?? entries[0];
          if (firstEntry) {
            if (/^https?:\/\//.test(firstEntry) || firstEntry.startsWith("//") || firstEntry.includes("/")) {
              return firstEntry;
            }
            if (/^gid:\/\/shopify\/MediaImage\//.test(firstEntry)) {
              const fallback = (anyWithGallery?.image ?? anyWithGallery?.variant_image) && /^https?:\/\//.test(String((anyWithGallery?.image ?? anyWithGallery?.variant_image)).trim())
                ? (anyWithGallery?.image ?? anyWithGallery?.variant_image)
                : defaultImageUrl;
              return { url: fallback, pendingGid: firstEntry };
            }
          }
        }
        return defaultImageUrl;
      }

      // Helper: get variant image from image or variant_image (SearchSpring may use either)
      const getVariantImageUrl = (v) => {
        const val = v?.image ?? v?.variant_image;
        if (!val) return null;
        let str = String(val).trim().replace(/^["']|["']$/g, "");
        if (!str) return null;
        return /^https?:\/\//.test(str) || str.startsWith("//") || str.includes("/") ? str : null;
      };

      const variantImageUrl = getVariantImageUrl(matchedVariant);
      const matchedVariantIdForUrl = matchedVariant?.id || null;

      // 1) Prefer mfield_custom_media_gallery (index 0 = first image)
      const galleryRaw = matchedVariant?.mfield_custom_media_gallery;
      if (galleryRaw) {
        const entries = String(galleryRaw)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        const firstEntry = entries[0] ?? entries[1];

        if (firstEntry) {
          // GID first: never use as img src, resolve via Storefront API
          if (/^gid:\/\/shopify\/MediaImage\//.test(firstEntry)) {
            const fallbackUrl = variantImageUrl || defaultImageUrl;
            return { url: fallbackUrl, pendingGid: firstEntry, variantId: matchedVariantIdForUrl };
          }
          const isLikelyUrl =
            /^https?:\/\//.test(firstEntry) ||
            firstEntry.startsWith("//") ||
            firstEntry.includes("/");

          if (isLikelyUrl) {
            return { url: firstEntry, variantId: matchedVariantIdForUrl };
          }
          // Gallery has GID: use variant.image as placeholder, resolve via Storefront API
          if (/^gid:\/\/shopify\/MediaImage\//.test(firstEntry)) {
            const fallbackUrl = variantImageUrl || defaultImageUrl;
            return { url: fallbackUrl, pendingGid: firstEntry, variantId: matchedVariantIdForUrl };
          }
        }
      }

      // 2) Fallback: variant.image (custom.media_gallery GID resolve failed or no gallery)
      if (variantImageUrl) {
        return { url: variantImageUrl, variantId: matchedVariantIdForUrl };
      }

      return defaultImageUrl;
    } catch (e) {
      // Never break the PLP on malformed data
      return defaultImageUrl;
    }
  },

  getCustomPlpImage: function (product) {
    const raw = product?.mfield_custom_plp_image;
    if (!raw) return "";

    const normalize = (value) => {
      if (value == null) return "";
      let str = String(value).trim();
      if (!str) return "";
      str = str
        .replace(/^["']|["']$/g, "")
        .replace(/&quot;/g, '"')
        .trim();
      return str;
    };

    try {
      const normalizedRaw = normalize(raw);
      if (!normalizedRaw) return "";

      if (normalizedRaw.startsWith("[") || normalizedRaw.startsWith("{")) {
        const parsed = JSON.parse(normalizedRaw);
        if (Array.isArray(parsed)) {
          const first = normalize(parsed[0]);
          return first;
        }
        if (parsed && typeof parsed === "object") {
          const candidate = normalize(
            parsed.url || parsed.src || parsed.image || parsed.value
          );
          return candidate;
        }
      }

      return normalizedRaw;
    } catch (e) {
      return normalize(raw);
    }
  },

  getProductBadge: function (product) {
    const raw = product?.mfield_custom_badge;
    if (!raw) return null;
    try {
      let parsed = raw;
      if (typeof raw === "string") {
        const normalized = raw.trim().replace(/&quot;/g, '"');
        parsed = JSON.parse(normalized);
      }
      const value = parsed?.value;
      if (!value?.title) return null;
      return {
        title: String(value.title),
        backgroundColor: String(value.background_color || "#000000"),
        textColor: String(value.text_color || "#ffffff"),
      };
    } catch (e) {
      return null;
    }
  },
  // ── PLP Variant Sync from Color Filter (enhancement layer) ─────────────────

  normalizeString: function (str) {
    if (str == null || typeof str !== "string") return "";
    let s = String(str).trim().toLowerCase().replace(/\s+/g, " ");
    if (!s) return "";
    if ((s.includes("[") || s.startsWith('"')) && (s.includes("]") || s.endsWith('"'))) {
      try {
        const parsed = JSON.parse(s.replace(/&quot;/g, '"'));
        if (Array.isArray(parsed) && parsed[0] != null) return String(parsed[0]).trim().toLowerCase();
        if (parsed != null && typeof parsed === "string") return parsed.trim().toLowerCase();
      } catch (e) {
        s = s.replace(/[\[\]"]/g, "");
      }
    }
    return s;
  },

  getActiveColorFromURL: function () {
    try {
      const params = new URLSearchParams(window.location.search);
      const key = "filter.variant_mfield_new_my_fields_filter_color";
      const val = params.get(key);
      if (!val) return null;
      const normalized = this.normalizeString(decodeURIComponent(val));
      return normalized || null;
    } catch (e) {
      return null;
    }
  },

  getActiveColorFromDOM: function () {
    try {
      const swatch = document.querySelector(
        ".filter-item__swatch.selected, .filter-item__swatch.active"
      );
      if (!swatch) return null;
      const val = swatch.dataset?.value ?? swatch.dataset?.color ?? swatch.getAttribute("data-value") ?? swatch.getAttribute("data-color");
      if (!val) return null;
      const normalized = this.normalizeString(val);
      return normalized || null;
    } catch (e) {
      return null;
    }
  },

  getActiveColorFilter: function () {
    const fromUrl = this.getActiveColorFromURL();
    if (fromUrl) return fromUrl;
    return this.getActiveColorFromDOM();
  },

  findMatchingVariant: function (product, selectedColor) {
    if (!product || !selectedColor || typeof selectedColor !== "string") return null;
    const colorNorm = selectedColor.trim().toLowerCase();
    if (!colorNorm) return null;

    let combinedVariantsString = "";
    if (Array.isArray(product?.variants)) {
      if (product.variants.length > 1) {
        combinedVariantsString = product.variants.join("");
      } else if (product.variants.length === 1) {
        combinedVariantsString = product.variants[0] || "";
      }
    } else if (product?.variants?.[0]) {
      combinedVariantsString = product.variants[0];
    }
    if (!combinedVariantsString) return null;

    const allVariants = this.parseHtmlEncodedJsonString(combinedVariantsString);
    if (!Array.isArray(allVariants) || allVariants.length === 0) return null;

    const matchColor = (colorName) => {
      if (!colorName) return false;
      const c = this.normalizeString(colorName);
      return c && (c === colorNorm || c.includes(colorNorm) || colorNorm.includes(c));
    };

    for (let i = 0; i < allVariants.length; i++) {
      const v = allVariants[i];
      if (!v) continue;

      let colorLabel = "";
      const rawColor = v?.mfield_new_my_fields_filter_color ?? v?.variant_mfield_new_my_fields_filter_color;
      if (rawColor) {
        try {
          const normalized = String(rawColor).replace(/&quot;/g, '"');
          const parsed = JSON.parse(normalized);
          if (Array.isArray(parsed)) colorLabel = parsed[0] != null ? String(parsed[0]) : "";
          else if (parsed != null) colorLabel = String(parsed);
          else colorLabel = String(rawColor);
        } catch (e) {
          colorLabel = String(rawColor);
        }
      }
      if (matchColor(colorLabel)) {
        let imageUrl = null;
        const val = v?.image ?? v?.variant_image;
        if (val) {
          const str = String(val).trim().replace(/^["']|["']$/g, "");
          if (str && (/^https?:\/\//.test(str) || str.startsWith("//") || str.includes("/"))) imageUrl = str;
        }
        const galleryRaw = v?.mfield_custom_media_gallery;
        if (!imageUrl && galleryRaw) {
          const entries = String(galleryRaw).split(",").map((s) => s.trim()).filter(Boolean);
          const first = entries[0] ?? entries[1];
          if (first && !/^gid:\/\//.test(first) && (/^https?:\/\//.test(first) || first.startsWith("//") || first.includes("/"))) {
            imageUrl = first;
          }
        }
        const variantId = v?.id ?? v?.variant_id ?? null;
        const numericId = variantId != null ? String(variantId).replace(/\D/g, "") || null : null;
        return { variant: v, variantId: numericId || variantId, imageUrl };
      }

      if (v?.option1 && matchColor(v.option1)) {
        let imageUrl = null;
        const val = v?.image ?? v?.variant_image;
        if (val) {
          const str = String(val).trim().replace(/^["']|["']$/g, "");
          if (str && (/^https?:\/\//.test(str) || str.startsWith("//") || str.includes("/"))) imageUrl = str;
        }
        const galleryRaw = v?.mfield_custom_media_gallery;
        if (!imageUrl && galleryRaw) {
          const entries = String(galleryRaw).split(",").map((s) => s.trim()).filter(Boolean);
          const first = entries[0] ?? entries[1];
          if (first && !/^gid:\/\//.test(first) && (/^https?:\/\//.test(first) || first.startsWith("//") || first.includes("/"))) {
            imageUrl = first;
          }
        }
        const variantId = v?.id ?? v?.variant_id ?? null;
        const numericId = variantId != null ? String(variantId).replace(/\D/g, "") || null : null;
        return { variant: v, variantId: numericId || variantId, imageUrl };
      }
      if (v?.option2 && matchColor(v.option2)) {
        let imageUrl = null;
        const val = v?.image ?? v?.variant_image;
        if (val) {
          const str = String(val).trim().replace(/^["']|["']$/g, "");
          if (str && (/^https?:\/\//.test(str) || str.startsWith("//") || str.includes("/"))) imageUrl = str;
        }
        const galleryRaw = v?.mfield_custom_media_gallery;
        if (!imageUrl && galleryRaw) {
          const entries = String(galleryRaw).split(",").map((s) => s.trim()).filter(Boolean);
          const first = entries[0] ?? entries[1];
          if (first && !/^gid:\/\//.test(first) && (/^https?:\/\//.test(first) || first.startsWith("//") || first.includes("/"))) {
            imageUrl = first;
          }
        }
        const variantId = v?.id ?? v?.variant_id ?? null;
        const numericId = variantId != null ? String(variantId).replace(/\D/g, "") || null : null;
        return { variant: v, variantId: numericId || variantId, imageUrl };
      }
    }
    return null;
  },

  updateProductCard: function (cardElement, match, baseUrl) {
    if (!cardElement || !match) return;
    try {
      const link = cardElement.querySelector(".product-card__link");
      const img = cardElement.querySelector(".product-card__imagenew img");
      const urlBase = (baseUrl || "").split("?")[0];
      const variantId = match.variantId;

      if (link && urlBase && variantId) {
        link.href = `${urlBase}?variant=${variantId}`;
      }
      if (img && match.imageUrl) {
        img.src = match.imageUrl;
        img.setAttribute("data-hover-image", match.imageUrl);
        img.setAttribute("data-original-image", match.imageUrl);
      }
    } catch (e) {
      // Fallback: no update on error
    }
  },

  initPLPVariantSync: function (productGrid, results) {
    if (!productGrid || !results || !Array.isArray(results) || results.length === 0) return;
    const color = this.getActiveColorFilter();
    if (!color) return;

    const productMap = new Map();
    results.forEach((p) => {
      const ids = [
        p?.variant_product_id,
        p?.product_id,
        p?.id,
        p?.uid,
        p?.ss_id
      ].filter((x) => x != null && String(x).trim() !== "");
      ids.forEach((id) => productMap.set(String(id), p));
    });

    const cards = productGrid.querySelectorAll(".main-product-card");
    cards.forEach((card) => {
      try {
        const productId = card.dataset?.productId;
        if (!productId) return;
        const product = productMap.get(productId);
        if (!product) return;

        const match = this.findMatchingVariant(product, color);
        if (!match) return;

        const baseUrl = (product.url || "").split("?")[0];
        this.updateProductCard(card, match, baseUrl || product.url);
      } catch (e) {
        // Skip on error
      }
    });
  },

  // Generate regular product card
  generateRegularProductCard: function (product) {
    const title = product.name.replace(/\s?-\s?\d.*$/, "").trim();
    const productId = product.variant_product_id;
    const variantId = product.ss_id;
    const baseUrl = product.url || "#";
    const productHandle = product.handle;
    const varinatid = product.uid;
    const isRug = this.isRugProduct(product);
    const productPrice = product.variant_price;
    const wrapperDataset = this.currentWrapperData;
    const isPriceUponRequest =
      product?.template_suffix === "rugs_price-upon-request";
    const variants = this.parseHtmlEncodedJsonString(product?.variants?.[0]);
    const safeVariants = Array.isArray(variants) ? variants : [];
    const inStockVariants = this.getInStockVariants(safeVariants);

    // Process variant data
    const variantData = this.processVariants(product, variantId, baseUrl);

    // console.log(variantData);

    const parsePrice = (priceStr) => {
      if (!priceStr || priceStr.trim() === "") return null;
      const cleaned = priceStr.trim().replace(/\.00$/, "");
      const parsed = Number(cleaned.replace(/[^0-9.]/g, ""));
      return isNaN(parsed) ? null : parsed;
    };

    const currentPrice = parsePrice(product.price);
    const { isOnSale: isOnSaleAndAvailable } = this.getProductSaleInfo(
      product,
      currentPrice,
      inStockVariants
    );

    // Promo badge config from wrapper dataset
    const wrapperDataAny = Object.values(wrapperDataset)?.[0] || {};
    const isPortraitModeEnabled = String(wrapperDataAny?.portraitMode).toLowerCase() === "true";
    const showPromoBadge = String(wrapperDataAny?.showPromoBadge).toLowerCase() === "true";
    const promoBadgeText = wrapperDataAny?.promoBadgeText || "Buy More, Save Moreeee";
    const promoBadgeBg = wrapperDataAny?.promoBadgeBackgroundColor || "#FFFFFF";
    const promoBadgeColor = wrapperDataAny?.promoBadgeColor || "#8F88EE";
    const promoBadgeCollection = wrapperDataAny?.promoBadgeCollection;

    // Normalize promo collection ids from wrapper dataset (handles JSON string, array, or CSV)
    const promoCollectionIds = (() => {
      if (!promoBadgeCollection) return [];
      if (Array.isArray(promoBadgeCollection)) {
        return promoBadgeCollection.map((x) => String(x).trim()).filter(Boolean);
      }
      let raw = String(promoBadgeCollection).trim();
      try {
        if (/^\s*\[/.test(raw)) {
          const parsed = JSON.parse(raw);
          return (Array.isArray(parsed) ? parsed : []).map((x) => String(x).trim()).filter(Boolean);
        }
      } catch (e) {
        // fall through to CSV parsing
      }
      raw = raw.replace(/^[\[\(\{\s]+|[\]\)\}\s]+$/g, "");
      return raw
        .split(",")
        .map((s) => s.replace(/["']/g, "").trim())
        .filter(Boolean);
    })();

    // Normalize product collection ids from Searchspring product
    const productCollectionIds = Array.isArray(product?.collection_id)
      ? product.collection_id.map((x) => String(x).trim()).filter(Boolean)
      : (product?.collection_id ? [String(product.collection_id).trim()] : []);

    const isPromoEligible = showPromoBadge && promoCollectionIds.length > 0
      ? productCollectionIds.some((id) => promoCollectionIds.includes(id))
      : false;

    // Show promo badge if eligible, or "excluded from sale" if promo is active but product is not eligible
    const promoBadgeHtml = isPromoEligible
      ? `<div class="product-badges product-card__badges product-badges--promo"><span class="product-badge product-badge--promo" style="\n        --color-text: ${promoBadgeColor};\n        --color-bg: ${promoBadgeBg};\n      ">${promoBadgeText}</span></div>`
      : "";
  // Custom metaobject badge (custom.badge metafield via Searchspring mfield_custom_badge)
  const customBadge = this.getProductBadge(product);
  const customBadgeHtml = customBadge
    ? `<span class="product-card__badge product-card__badge--custom" style="background-color:${customBadge.backgroundColor};color:${customBadge.textColor};">${customBadge.title}</span>`
    : "";

    // Resolve base images from SearchSpring
    const defaultMainImageRaw =
      product.thumbnailImageUrl || "//via.placeholder.com/300";
    const defaultHoverImage = product.ss_image_hover || defaultMainImageRaw;

    // If Searchspring gave us a GID as the main image but a real CDN URL
    // as the hover image, prefer the hover image as the visual main.
    const defaultMainImage =
      /^gid:\/\//.test(defaultMainImageRaw) &&
      defaultHoverImage &&
      !/^gid:\/\//.test(defaultHoverImage)
        ? defaultHoverImage
        : defaultMainImageRaw;

    // Enhanced behavior: if the search query includes a color that matches
    // a variant's `mfield_new_my_fields_filter_color` AND the product type
    // matches `product_type_unigram`, use the first image from that variant's
    // `mfield_custom_media_gallery` as the main product image.
    const searchResult = this.getVariantImageFromSearchQuery(
      product,
      defaultMainImage
    );
    const mainImageFromSearch =
      searchResult && typeof searchResult === "object" && searchResult.url !== undefined
        ? searchResult.url
        : searchResult;
    const variantIdFromSearch =
      searchResult && typeof searchResult === "object" && searchResult.variantId
        ? searchResult.variantId
        : null;
    const pendingMediaGid =
      searchResult && typeof searchResult === "object" && searchResult.pendingGid
        ? searchResult.pendingGid
        : null;
    const pendingHoverGid = product._customCarpetPlpHoverImage?.pendingGid || null;
    const inStockVariantImage = this.getPreferredInStockVariantImage(
      product,
      variantIdFromSearch
    );

    const shippingRate = String(product.mfield_new_my_fields_shipping_rate || "")
      .toLowerCase()
      .trim();
    const isFreeShipping = shippingRate === "ethnicraft free";

    const customPlpImage = isPortraitModeEnabled
      ? this.getCustomPlpImage(product)
      : null;
    const hasMetafieldImageData =
      isPortraitModeEnabled &&
      (!!customPlpImage ||
        (searchResult && typeof searchResult === "object"));
    const defaultCardAspectRatio = this.getAspectRatioFromAnyDataset(wrapperDataset);
    const cardAspectRatio = hasMetafieldImageData
      ? "2 / 3"
      : defaultCardAspectRatio;
    const imageAspectRatioStyle = cardAspectRatio
      ? ` style="aspect-ratio: ${cardAspectRatio};"`
      : "";
    const isRenderableImageUrl = (url) => {
      const value = String(url || "").trim();
      return !!value && !/^gid:\/\//.test(value);
    };
    const normalizeImageKey = (url) =>
      String(url || "")
        .trim()
        .replace(/^https?:/i, "")
        .replace(/^\/\//, "")
        .replace(/\?.*$/, "")
        .replace(/_(\d+)x(\d+)(?=\.[a-zA-Z]+$)/, "")
        .toLowerCase();

    const thumbnailMainImage = isRenderableImageUrl(product.thumbnailImageUrl)
      ? String(product.thumbnailImageUrl).trim()
      : isRenderableImageUrl(defaultMainImage)
        ? String(defaultMainImage).trim()
        : null;
    const thumbnailMainKey = normalizeImageKey(thumbnailMainImage);
    const inStockVariantKey = normalizeImageKey(inStockVariantImage);
    let hasMatchingInStockThumbnail = false;
    let hasMatchingOutOfStockThumbnail = false;
    if (thumbnailMainKey) {
      safeVariants.forEach((variant) => {
        const variantImageKey = normalizeImageKey(this.getVariantDisplayImage(variant));
        if (!variantImageKey || variantImageKey !== thumbnailMainKey) return;
        if (this.isVariantInStock(variant)) {
          hasMatchingInStockThumbnail = true;
        } else {
          hasMatchingOutOfStockThumbnail = true;
        }
      });
    }
    const isOutOfStockThumbnail =
      hasMatchingOutOfStockThumbnail && !hasMatchingInStockThumbnail;
    const isDifferentFromThumbnail =
      !!inStockVariantImage &&
      !!inStockVariantKey &&
      inStockVariantKey !== thumbnailMainKey;
    const needsVariantImageForContext =
      !!variantIdFromSearch || !thumbnailMainImage || isOutOfStockThumbnail;
    const shouldUseInStockVariantImage =
      isRenderableImageUrl(inStockVariantImage) &&
      isDifferentFromThumbnail &&
      needsVariantImageForContext;

    // Smart priority:
    // - Keep thumbnail as default main image (backup behavior)
    // - Only allow in-stock variant image when context requires it
    const mainImageToUse = isPortraitModeEnabled
      ? customPlpImage ||
        (shouldUseInStockVariantImage ? inStockVariantImage : null) ||
        mainImageFromSearch ||
        thumbnailMainImage ||
        defaultMainImage
      : (shouldUseInStockVariantImage ? inStockVariantImage : null) ||
        thumbnailMainImage ||
        defaultMainImage;

    // Backup hover behavior: ss_image_hover first, thumbnail fallback
    const hoverImageToUse = defaultHoverImage || thumbnailMainImage;

    // Final safety net: never render a GID as the visible image.
    // If main is still a GID but hover is a proper CDN URL, promote hover.
    const safeMainImage =
      /^gid:\/\//.test(mainImageToUse || "") &&
      isRenderableImageUrl(hoverImageToUse)
        ? hoverImageToUse
        : mainImageToUse;

    const safeHoverImage =
      isRenderableImageUrl(hoverImageToUse)
        ? hoverImageToUse
        : safeMainImage;

    const pendingGidAttr = pendingMediaGid
      ? ` data-pending-media-gid="${pendingMediaGid.replace(/"/g, "&quot;")}"`
      : "";
    const pendingHoverGidAttr = pendingHoverGid
      ? ` data-pending-hover-gid="${pendingHoverGid.replace(/"/g, "&quot;")}"`
      : "";


    return `
      <div class="main-product-card" data-product-id="${productId}" data-product-price="${productPrice}">
        <div class="wishlist-button">
          <button
            class="wk-button wk-floating"
            data-product-handle="${productHandle}"
            data-variant-id="${varinatid}"
            type="button"
          >
            <span class="wk-icon">
              <svg viewBox="0 0 64 64">
                <path d="M32.012,59.616c-1.119-.521-2.365-1.141-3.707-1.859a79.264,79.264,0,0,1-11.694-7.614C6.316,42,.266,32.6.254,22.076,0.244,12.358,7.871,4.506,17.232,4.5a16.661,16.661,0,0,1,11.891,4.99l2.837,2.889,2.827-2.9a16.639,16.639,0,0,1,11.874-5.02h0c9.368-.01,17.008,7.815,17.021,17.539,0.015,10.533-6.022,19.96-16.312,28.128a79.314,79.314,0,0,1-11.661,7.63C34.369,58.472,33.127,59.094,32.012,59.616Z"><path>
              </svg>
            </span>
            <span class="wk-text">Add to Wishlist</span>
          </button>
        </div>
        <a href="${
          variantIdFromSearch
            ? `${baseUrl.split("?")[0]}?variant=${variantIdFromSearch}`
            : product?.url
        }" class="product-card__link" data-base-url="${baseUrl}" onmousedown="if(typeof IntelliSuggest !== 'undefined' && IntelliSuggest.clickItem) { try { IntelliSuggest.clickItem(this, { siteId: 'ccwfx6', intellisuggestData: '${product.intellisuggestData || ''}', intellisuggestSignature: '${product.intellisuggestSignature || ''}' }); } catch(e) { console.error('IntelliSuggest click tracking error:', e); } }">
          <div class="product-card__imagenew"${imageAspectRatioStyle}>
            <img${imageAspectRatioStyle} data-hover-image="${safeHoverImage}" src="${safeMainImage}" data-original-image="${safeMainImage}"${pendingGidAttr}${pendingHoverGidAttr}
                 alt="${product.name || "Product image"}"
                 loading="lazy">
             <div class="product-card__badges-row">
              ${isOnSaleAndAvailable ? `<span class="product-card__badge">SALE</span>` : ""}
              ${isFreeShipping ? `<span class="product-card__badge product-card__badge--free-shipping">Free Shipping</span>` : ""}
              ${customBadgeHtml}
            </div>
            ${promoBadgeHtml}
          </div>

          <div class="product-card__content">
            <div class="product-card__brand">${
              product.mfield_new_my_fields_brand_name || product.brand || ""
            }</div>
            <h5 class="product-card__title">${title || "Product title"}</h5>
            ${this.generateSizeForFurniture(product)}
            ${isRug ? this.generateSizeText(product) : ""}
            ${
              isPriceUponRequest
                ? `<div class="product-card__cta product-card__cta--contact">
                     Price Upon Request
                   </div>`
                : `<div class="product-card__price">
                     ${this.generatePriceHTML(product, { showMobileFreeShipping: isFreeShipping })}
                   </div>`
            }
            ${this.generateCustomizeButton(product)}
           
            ${this.generateSwatchesHTML(variantData, product, variantId)}
            ${this.generateAddSampleButton(product)}
          </div>
        </a>
      </div>
    `;
  },

  // Generate multiple cards for custom rug flow products
  isCustomCarpetPlp: function () {
    const collectionHandle =
      Object.values(this.currentWrapperData)?.[0]?.collectionHandle || "";
    return [
      "quick-ship-custom-carpet",
      "custom-sized-carpet",
      "custom-sized-rugs",
    ].includes(collectionHandle);
  },

  getParsedProductVariants: function (product) {
    let combinedVariantsString = "";
    if (Array.isArray(product?.variants)) {
      if (product.variants.length > 1) {
        combinedVariantsString = product.variants.join("");
      } else if (product.variants.length === 1) {
        combinedVariantsString = product.variants[0] || "";
      }
    } else if (product?.variants?.[0]) {
      combinedVariantsString = product.variants[0];
    }
    if (!combinedVariantsString) return [];
    const parsed = this.parseHtmlEncodedJsonString(combinedVariantsString) || [];
    return Array.isArray(parsed) ? parsed : [];
  },

  findVariantForCustomRugOption: function (variants, optionLabel) {
    const normalizeSwatchText = (value) =>
      String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
    const viceVersaCompare = (a, b) => {
      const aTitle = normalizeSwatchText(a);
      const bTitle = normalizeSwatchText(b);
      if (!aTitle || !bTitle) return false;
      return aTitle.includes(bTitle) || bTitle.includes(aTitle);
    };

    return (
      variants.find(
        (variant) =>
          viceVersaCompare(variant?.title, optionLabel) ||
          viceVersaCompare(variant?.option1, optionLabel) ||
          viceVersaCompare(variant?.option2, optionLabel) ||
          viceVersaCompare(
            variant?.mfield_new_my_fields_filter_color,
            optionLabel
          ) ||
          viceVersaCompare(variant?.mfield_my_fields_color, optionLabel)
      ) || null
    );
  },

  getVariantMediaGalleryFirstImage: function (variant, fallbackUrl) {
    return this.getVariantMediaGalleryImageAtIndex(variant, 0, fallbackUrl);
  },

  parseVariantMediaGalleryEntries: function (variant) {
    const galleryRaw = variant?.mfield_custom_media_gallery;
    if (!galleryRaw) return [];
    return String(galleryRaw)
      .split(",")
      .map((entry) => entry.trim().replace(/^["']|["']$/g, ""))
      .filter(Boolean);
  },

  galleryEntryToImage: function (entry, fallbackUrl) {
    if (!entry) return { url: fallbackUrl || null, pendingGid: null };

    if (/^gid:\/\/shopify\/MediaImage\//.test(entry)) {
      return { url: fallbackUrl || null, pendingGid: entry };
    }

    const isLikelyUrl =
      /^https?:\/\//.test(entry) ||
      entry.startsWith("//") ||
      entry.includes("/");

    if (isLikelyUrl) {
      return { url: entry, pendingGid: null };
    }

    return { url: fallbackUrl || null, pendingGid: null };
  },

  getVariantMediaGalleryImageAtIndex: function (variant, index, fallbackUrl) {
    const entries = this.parseVariantMediaGalleryEntries(variant);
    const entry = entries[index];
    if (!entry) {
      return { url: fallbackUrl || null, pendingGid: null };
    }
    return this.galleryEntryToImage(entry, fallbackUrl);
  },

  // Custom carpet: gallery index 0 is often the swatch; lifestyle uses variant id in filename.
  getCustomCarpetPlpGalleryImage: function (variant, fallbackUrl) {
    const entries = this.parseVariantMediaGalleryEntries(variant);
    if (!entries.length) {
      return { url: fallbackUrl || null, pendingGid: null };
    }

    if (entries.length === 1) {
      return this.galleryEntryToImage(entries[0], fallbackUrl);
    }

    const variantId = String(variant?.id || variant?.variant_id || "")
      .replace(/\D/g, "");

    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      if (!/^gid:\/\//.test(entry) && variantId && entry.includes(variantId)) {
        return this.galleryEntryToImage(entry, fallbackUrl);
      }
    }

    // Convention: index 0 = swatch, index 1 = lifestyle room shot (e.g. 43406552006794_1.jpg)
    return this.getVariantMediaGalleryImageAtIndex(variant, 1, fallbackUrl);
  },

  prefetchCustomCarpetHoverGids: async function (results) {
    if (!this.isCustomCarpetPlp() || !Array.isArray(results) || !results.length) {
      return;
    }

    const gids = new Set();
    results.forEach((product) => {
      if (!product.mfield_custom_custom_rug_flow || !product?.ss_swatches) return;

      const parsedVariants = this.getParsedProductVariants(product);
      const variantData = this.processVariants(
        product,
        product.ss_id,
        product.url,
        true
      );

      variantData.options.forEach((optionLabel) => {
        const matchedVariant = this.findVariantForCustomRugOption(
          parsedVariants,
          optionLabel
        );
        const galleryImage = this.getCustomCarpetPlpGalleryImage(
          matchedVariant,
          ""
        );
        if (galleryImage?.pendingGid) {
          gids.add(galleryImage.pendingGid);
        }
      });
    });

    await Promise.all(
      [...gids].map((gid) => this.resolveMediaGidToUrl(gid))
    );
  },

  generateCustomRugFlowCards: function (product) {
    const variantData = this.processVariants(
      product,
      product.ss_id,
      product.url,
      (processSwatchImages = true)
    );
    const cards = [];
    const isCustomCarpetPlp = this.isCustomCarpetPlp();
    const parsedVariants = isCustomCarpetPlp
      ? this.getParsedProductVariants(product)
      : [];

    // Generate cards for each variant
    variantData.options.forEach((variant, index) => {
      // Create a copy of the product with variant-specific data
      const variantProduct = { ...product };

      // Update product details for this variant
      variantProduct.name = `${product.name
        .replace(/\s?-\s?\d.*$/, "")
        .trim()} - ${variant}`;
      variantProduct.ss_id = variantData.map[variant];
      const fallbackImage =
        variantData.images[variant] || product.thumbnailImageUrl;

      if (isCustomCarpetPlp) {
        const matchedVariant = this.findVariantForCustomRugOption(
          parsedVariants,
          variant
        );
        const galleryImage = this.getCustomCarpetPlpGalleryImage(
          matchedVariant,
          fallbackImage
        );

        // Default: swatch. Hover: lifestyle from custom.media_gallery.
        variantProduct.thumbnailImageUrl = fallbackImage;

        if (galleryImage?.pendingGid) {
          const cachedHover = this.gidUrlCache[galleryImage.pendingGid];
          if (cachedHover) {
            variantProduct.ss_image_hover = cachedHover;
          } else {
            variantProduct.ss_image_hover = fallbackImage;
            variantProduct._customCarpetPlpHoverImage = {
              url: fallbackImage,
              pendingGid: galleryImage.pendingGid,
            };
          }
        } else if (
          galleryImage?.url &&
          galleryImage.url !== fallbackImage
        ) {
          variantProduct.ss_image_hover = galleryImage.url;
        } else {
          variantProduct.ss_image_hover =
            variantData.images[variant] || product.ss_image_hover;
        }
      } else {
        variantProduct.thumbnailImageUrl = fallbackImage;
        variantProduct.ss_image_hover =
          variantData.images[variant] || product.ss_image_hover;
      }

      // Get variant-specific price if available
      if (product.variant_price) {
        const prices = product.variant_price.split("|");
        if (prices[index]) {
          variantProduct.price = prices[index].trim();
        }
      }

      // Update variant ID for wishlist button
      if (product.variant_id) {
        const variantIds = product.variant_id.split("|");
        if (variantIds[index]) {
          variantProduct.uid = variantIds[index].trim();
        }
      }

      if (variantData?.urls?.[variant]) {
        variantProduct.url = variantData.urls[variant];
      }

      // Generate card for this variant
      cards.push(this.generateRegularProductCard(variantProduct));
    });

    return cards.join("");
  },

  processVariants: function (product, variantId, baseUrl, processSwatchImages) {
    // Combine all variants array elements if Searchspring splits the JSON
    // (Mirror logic from Master-live so we don't rely only on variants[0])
    let combinedVariantsString = "";
    if (Array.isArray(product?.variants)) {
      if (product.variants.length > 1) {
        // Multiple elements: join them (Searchspring split the JSON)
        combinedVariantsString = product.variants.join("");
      } else if (product.variants.length === 1) {
        // Single element: use directly (no need to join)
        combinedVariantsString = product.variants[0] || "";
      }
    } else if (product?.variants?.[0]) {
      // Fallback: if not array but has first element
      combinedVariantsString = product.variants[0];
    }

    const filterVariants = this.parseHtmlEncodedJsonString(combinedVariantsString);
    const variants = filterVariants.filter((item) => item.image);
    const parsedSwatches =
      this.parseHtmlEncodedJsonString(product.ss_swatches) || [];
    const swatchesData = parsedSwatches.filter((item) => item.available) || [];
    const colorsMetaFieldData = product.variant_mfield_my_fields_color;    

    // product.name === "Sonoma Platform Bed" && console.log("variants", variants);
    const hasDuplicateOption1 = variants.some((v, _, arr) => arr.filter(x => x.option1 === v.option1).length > 3);

    const isFurniture = Object.values(this.currentWrapperData)?.at(0)?.collectionHandle === "furniture" || product.template_suffix === "furniture_new"

    let testvariantOptions = [];
    let testvariantMap = {};
    let testvariantImages = {};
    let testvariantSwatchImages = {};
    let testvariantColors = {};
    let testvariantUrls = {};
    let testVariantTitles = {};

    let tvariantOptions = [];
    let tvariantMap = {};
    let tvariantImages = {};
    let tvariantSwatchImages = {};
    let tvariantColors = {};
    let tvariantUrls = {};
    let tVariantTitles = {};

    const normalizeSwatchText = (value) =>
      String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

    const viceVersaCompare = (a, b) => {
      const aTitle = normalizeSwatchText(a);
      const bTitle = normalizeSwatchText(b);
      if (!aTitle || !bTitle) return false;
      return aTitle.includes(bTitle) || bTitle.includes(aTitle);
    };

    if (swatchesData.length > 0) {
      swatchesData.forEach((swatch) => {
        const swatchTitle = swatch.color;
        const variant =
          variants.find((variant) => {
            return (
              viceVersaCompare(variant?.title, swatchTitle) ||
              viceVersaCompare(variant?.option1, swatchTitle) ||
              viceVersaCompare(variant?.option2, swatchTitle) ||
              viceVersaCompare(
                variant?.mfield_new_my_fields_filter_color,
                swatchTitle
              ) ||
              viceVersaCompare(variant?.mfield_my_fields_color, swatchTitle)
            );
          }) || {};
        if (!variant?.id) return;
        if (!this.isVariantInStock(variant)) return;

        const formatterTitle = (title) =>
          title ? title.toLowerCase().split(" ").join("-") : "";
        const colorData = (
          variant?.mfield_new_my_fields_filter_color ||
          variant?.mfield_my_fields_color
        )?.replace(/&quot;/g, '"');
        const variantTitle =
          formatterTitle(variant?.option1) ||
          formatterTitle(variant?.option2) ||
          formatterTitle(variant?.title) ||
          formatterTitle(swatch?.color);

        testvariantOptions.push(swatchTitle);
        testvariantMap[swatchTitle] = swatchTitle;
        testvariantImages[swatchTitle] = swatch.image;
        testvariantSwatchImages[
          swatchTitle
        ] = variant?.mfield_custom_swatch || `//abchome.com/cdn/shop/files/${variantTitle
          .toLowerCase()
          .replace(/\s+/g, "-")}_64x64.png`;
        testvariantColors[swatchTitle] = colorData;
        testvariantUrls[swatchTitle] =
          swatch.url || `${baseUrl.split("?")[0]}?variant=${variant.id}`;
        testVariantTitles[swatchTitle] = [
          variant?.option1,
          variant?.option2,
          variant?.title,
          swatch?.color,
        ];
      });
    }

    if (colorsMetaFieldData?.length > 0) {
      colorsMetaFieldData.forEach((swatch) => {
        const swatchTitle = swatch;
        const variant =
          variants
          .find((item) => item.mfield_my_fields_color === swatchTitle) || {};

        if (!variant?.id || !this.isVariantInStock(variant)) return;

        const formatterTitle = (title) =>
          title ? title.toLowerCase().split(" ").join("-") : "";
        const colorData = variant?.mfield_my_fields_color?.replace(
          /&quot;/g,
          '"'
        );
        
        const variantTitle =
          formatterTitle(hasDuplicateOption1 ? variant?.option2 : variant?.option1) ||
          formatterTitle(variant?.option2) ||
          formatterTitle(variant?.title) ||
          formatterTitle(swatch?.color);

        const cleanedVariantTitle = variantTitle.replace(/[,/]/g, "").replace(/--+/g, "-");

        tvariantOptions.push(swatchTitle);
        tvariantMap[swatchTitle] = swatchTitle;
        tvariantImages[swatchTitle] = variant.image;
        tvariantSwatchImages[
          swatchTitle
         ] = variant?.mfield_custom_swatch || `//abchome.com/cdn/shop/files/${cleanedVariantTitle
          .toLowerCase()
          .replace(/\s+/g, "-")}_64x64.png`;
        tvariantColors[swatchTitle] = colorData;
        tvariantUrls[swatchTitle] =
          swatch.url || `${baseUrl.split("?")[0]}?variant=${variant.id}`;
        tVariantTitles[swatchTitle] = [
          variant?.option1,
          variant?.option2,
          variant?.title,
          swatch?.color,
        ];
      });
    }

    if (isFurniture) {
      return {
        options: tvariantOptions,
        map: tvariantMap,
        images: tvariantImages,
        colors: tvariantColors,
        urls: tvariantUrls,
        swatchImages: tvariantSwatchImages,
        titles: tVariantTitles,
      };
    }

    const useColorMetaFallback =
      testvariantOptions.length === 0 && tvariantOptions.length > 0;

    return useColorMetaFallback
      ? {
          options: tvariantOptions,
          map: tvariantMap,
          images: tvariantImages,
          colors: tvariantColors,
          urls: tvariantUrls,
          swatchImages: tvariantSwatchImages,
          titles: tVariantTitles,
        }
      : {
          options: testvariantOptions,
          map: testvariantMap,
          images: testvariantImages,
          colors: testvariantColors,
          urls: testvariantUrls,
          swatchImages: testvariantSwatchImages,
          titles: testVariantTitles,
        };
  },

  handleVariantSwatchHover: (event, element) => {
    // Don't apply hover effects on mobile
    if (window.matchMedia("(max-width: 768px)").matches) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const variantId = element.getAttribute("data-variant-id");
    const productCard = element.closest(".main-product-card");
    const link = productCard.querySelector(".product-card__link");
    const image = productCard.querySelector(".product-card__imagenew img");

    // Store original image if not already stored
    if (!image.hasAttribute("data-original-src")) {
      image.setAttribute("data-original-src", image.getAttribute("src"));
    }

    // Get the URL from the swatch data
    const variantUrl = element.getAttribute("data-url");
    if (variantUrl) {
      link.setAttribute("href", variantUrl);
    } else {
      const baseUrl = link.getAttribute("data-base-url");
      const newUrl = `${baseUrl.split("?")[0]}?variant=${variantId}`;
      link.setAttribute("href", newUrl);
    }

    // Update the product image on hover
    const hoverImage =
      element.getAttribute("data-hover-image") ||
      element.getAttribute("data-image");
    if (hoverImage && hoverImage !== "undefined") {
      image.setAttribute("src", hoverImage);
    }

    // Highlight the hovered swatch
    productCard.querySelectorAll(".new-swatch").forEach((swatch) => {
      swatch.classList.remove("swatch--active");
    });
    element.classList.add("swatch--active");
  },

  handleVariantSwatchLeave: (event, element) => {
    // Only apply leave effects on desktop
    if (window.matchMedia("(max-width: 768px)").matches) {
      return;
    }

    const productCard = element.closest(".main-product-card");
    const image = productCard.querySelector(".product-card__imagenew img");
    const originalSrc = image.getAttribute("data-original-src");

    if (originalSrc) {
      image.setAttribute("src", originalSrc);
    }

    // Optional: Reset the active swatch to the first one
    const swatches = productCard.querySelectorAll(".new-swatch");
    if (swatches.length > 0) {
      swatches.forEach((swatch) => swatch.classList.remove("swatch--active"));
      // swatches[0].classList.add("swatch--active");
    }
  },

  handleVariantSwatchClick: (event, element) => {
    // Only apply click effects on mobile
    if (!window.matchMedia("(max-width: 768px)").matches) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const variantId = element.getAttribute("data-variant-id");
    const productCard = element.closest(".main-product-card");
    const link = productCard.querySelector(".product-card__link");
    const image = productCard.querySelector(".product-card__imagenew img");

    // Store original image if not already stored
    if (!image.hasAttribute("data-original-src")) {
      image.setAttribute("data-original-src", image.getAttribute("src"));
    }

    // Get the URL from the swatch data
    const variantUrl = element.getAttribute("data-url");
    if (variantUrl) {
      link.setAttribute("href", variantUrl);
    } else {
      const baseUrl = link.getAttribute("data-base-url");
      const newUrl = `${baseUrl.split("?")[0]}?variant=${variantId}`;
      link.setAttribute("href", newUrl);
    }

    // Update the product image on click
    const hoverImage =
      element.getAttribute("data-hover-image") ||
      element.getAttribute("data-image");
    if (hoverImage) {
      image.setAttribute("src", hoverImage);
    }

    // Highlight the clicked swatch
    productCard.querySelectorAll(".new-swatch").forEach((swatch) => {
      swatch.classList.remove("swatch--active");
    });
    element.classList.add("swatch--active");
  },

  parseHtmlEncodedJsonString: (encoded) => {
    try {
      // Step 1: Decode HTML entities (&quot; → ", etc.)
      const htmlDecoded = encoded
        .replace(/&quot;/g, '"')
        .replace(/\\&quot;/g, '"') // inner escaped quotes like 35.5\" → 35.5"
        .replace(/\\\\/g, "\\"); // reduce double backslashes if any

      // Step 2: Parse into JS object/array
      return JSON.parse(htmlDecoded);
    } catch (e) {
      // console.error("❌ Failed to parse JSON:", e.message);
      return [];
    }
  },

  getMarketingBlocks(sectionId) {
    if (!sectionId) return [];

    if (!window.utils.parsedMarketingBlocks[sectionId]) {
      const raw =
        window.utils.currentWrapperData[sectionId]?.marketingBlocks || "[]";
      const parsed = window.utils.parseHtmlEncodedJsonString(raw);
      window.utils.parsedMarketingBlocks[sectionId] = Array.isArray(parsed)
        ? parsed
        : [];
    }

    return window.utils.parsedMarketingBlocks[sectionId];
  },

  indexMarketingBlocksByPosition(blocks = []) {
    return (blocks || []).reduce((acc, block) => {
      if (!block?.type || typeof block.type !== "string" || !block?.settings) {
        return acc;
      }
      const position = Number(block?.settings?.position);
      if (!Number.isFinite(position)) {
        return acc;
      }
      if (!acc[position]) {
        acc[position] = [];
      }
      acc[position].push(block);
      return acc;
    }, {});
  },

  getPageStartIndex(pagination, productsPerPage, fallbackPage = 1) {
    if (typeof pagination?.start === "number") {
      return pagination.start;
    }

    const currentPage =
      Number(pagination?.currentPage) ||
      Number(pagination?.page) ||
      Number(fallbackPage) ||
      1;

    const pageSize =
      Number(pagination?.resultsPerPage) ||
      Number(pagination?.pageSize) ||
      Number(productsPerPage) ||
      0;

    return Math.max(0, (currentPage - 1) * pageSize);
  },

  normalizeSearchApiUrl(baseUrl, options = {}) {
    const { stripFilterParams = false, stripSortParams = false } = options;

    try {
      const url = new URL(baseUrl);
      const params = new URLSearchParams(url.search);

      Array.from(params.keys()).forEach((key) => {
        if (key === "page" || key === "resultsPerPage") {
          params.delete(key);
          return;
        }

        if (stripFilterParams && key.startsWith("filter.")) {
          params.delete(key);
          return;
        }

        if (stripSortParams && key.startsWith("sort.")) {
          params.delete(key);
        }
      });

      const normalizedQuery = params.toString();
      return `${url.origin}${url.pathname}${
        normalizedQuery ? `?${normalizedQuery}` : ""
      }`;
    } catch (error) {
      return baseUrl;
    }
  },

  buildProductApiUrl(baseUrl, page, productsPerPage, options = {}) {
    const normalizedBaseUrl = this.normalizeSearchApiUrl(baseUrl, options);
    const separator = normalizedBaseUrl.includes("?") ? "&" : "?";
    return `${normalizedBaseUrl}${separator}page=${page}&resultsPerPage=${productsPerPage}`;
  },

  resetInfiniteScroll(sectionId, options = {}) {
    const controller = this.infiniteScrollControllers?.[sectionId];
    if (controller?.reset) {
      controller.reset(options);
    }
  },

  refreshInfiniteScroll(sectionId, options = {}) {
    const controller = this.infiniteScrollControllers?.[sectionId];
    if (controller?.refresh) {
      return controller.refresh(options);
    }
    return Promise.resolve();
  },

  resumeInfiniteScroll(sectionId) {
    const controller = this.infiniteScrollControllers?.[sectionId];
    if (controller?.resume) {
      controller.resume();
    }
  },

  composeProductsWithMarketingBlocks({
    results = [],
    marketingBlockMap = {},
    startIndex = 0,
  }) {
    const fragments = [];
    let absoluteIndex = startIndex;

    results.forEach((product) => {
      const blocks = marketingBlockMap[absoluteIndex];
      if (blocks && blocks.length) {
        blocks.forEach((block) => {
          fragments.push(window.utils.generateMarketingBlock(block));
        });
      }

      fragments.push(window.utils.generateProductCard(product));
      absoluteIndex += 1;
    });

    return fragments.join("");
  },

  renderPagination: (paginationContainer, pagination, loadProducts) => {
    const totalPages = pagination.totalPages;
    const currentPage = pagination.currentPage;

    let paginationHTML = `
      <button ${currentPage === 1 ? "disabled" : ""} data-page="${
      currentPage - 1
    }">
        &larr; Previous
      </button>
      <span>Page ${currentPage} of ${totalPages}</span>
      <button ${currentPage >= totalPages ? "disabled" : ""} data-page="${
      currentPage + 1
    }">
        Next &rarr;
      </button>
    `;

    paginationContainer.innerHTML = paginationHTML;

    // Add event listeners to pagination buttons
    paginationContainer.querySelectorAll("button").forEach((button) => {
      button.addEventListener("click", () => {
        const page = parseInt(button.dataset.page);
        if (!isNaN(page)) loadProducts(page);
      });
    });
  },

  populateSortingOptions: (sortingData) => {
    const dropdownOptions = document.querySelector(".dropdown-options");

    if (dropdownOptions) {
      dropdownOptions.innerHTML = "";
    }

    if (sortingData?.options?.length) {
      sortingData.options.forEach((option) => {
        const li = document.createElement("li");

        li.className = "sort__option";
        li.dataset.value = `${option.field}_${option.direction}`;
        li.textContent = option.label;

        dropdownOptions && dropdownOptions.appendChild(li);
      });
    }

    if (dropdownOptions?.firstChild) {
      dropdownOptions.firstChild.classList.add("active");
    }
  },

  loadProducts: async ({
    page = 1,
    baseUrl,
    productsPerPage,
    productGrid,
    paginationContainer,
    showLoading,
    hideLoading,
    callback = () => {},
    totalCountElement,
    filterParams,
    source,
    sectionId,
    updateMode = "replace",
  }) => {
    if (source === "filter-apply") {
      document.querySelector(".product-grid").style.visibility = "hidden";
    }
    showLoading();

    const options = {
      method: "GET",
      headers: { accept: "application/json" },
    };
    try {
      const isBestSelling =
        window.utils.sortParam === "sort.ga_unique_purchases=desc"
        ? "&sort.ga_unique_purchases=desc"
        : "";
      const apiUrl = `${window.utils.buildProductApiUrl(
        baseUrl,
        page,
        productsPerPage
      )}${isBestSelling}`;
      const response = await fetch(apiUrl, options);
      const urlSortParam = (apiUrl.match(/sort\.[^=]+=[^&]+/) || [null])[0];
      window.utils.sortParam = urlSortParam;
      const data = await response.json();

      const marketingBlocks = utils.getMarketingBlocks(sectionId);
      const marketingBlockMap =
        utils.indexMarketingBlocksByPosition(marketingBlocks);
      const startIndex = utils.getPageStartIndex(
        data.pagination,
        productsPerPage,
        page
      );

      if (data?.sorting?.options?.length) {
        utils.populateSortingOptions(data?.sorting);
      }

      if (data.results?.length) {
        if (utils.isCustomCarpetPlp()) {
          await utils.prefetchCustomCarpetHoverGids(data.results);
        }

        const gridHtml = utils.composeProductsWithMarketingBlocks({
          results: data.results,
          marketingBlockMap,
          startIndex,
        });

        if (source === "filter-apply") {
          document.querySelector(".product-grid").style.visibility = "visible";
        }

        if (updateMode === "append") {
          productGrid.insertAdjacentHTML("beforeend", gridHtml);
        } else {
          productGrid.innerHTML = gridHtml;
        }

        await utils.resolvePendingMediaGids(productGrid);

        if (data.results?.length) {
          utils.initPLPVariantSync(productGrid, data.results);
        }

        setTimeout(() => {
          utils.validateSwatchImages();
        }, 100);

        utils.paginationData = data.pagination;

        if (paginationContainer) {
          utils.renderPagination(
            paginationContainer,
            data.pagination,
            (newPage) => {
              utils.loadProducts({
                page: newPage,
                baseUrl,
                productsPerPage,
                productGrid,
                paginationContainer,
                showLoading,
                hideLoading,
                callback,
                totalCountElement: "",
                filterParams: "",
                marketingBlocks,
                sectionId,
              });
            }
          );
        }

        if (totalCountElement) {
          totalCountElement.style.display = "block";
          totalCountElement.textContent = `${data.pagination.totalResults} Items`;
        }

        callback(data);
      } else {
        productGrid.innerHTML =
          '<p class="no-products">No products found in this collection.</p>';
        if (paginationContainer) paginationContainer.innerHTML = "";
      }

      // Update URL without reload
      // const url = new URL(window.location);
      // url.searchParams.set("page", page);
      // window.history.replaceState({}, "", url);
    } catch (error) {
      console.error("Fetch error:", error);
      productGrid.innerHTML =
        '<p class="error-message">Error loading products. Please try again.</p>';
    } finally {
      if (sectionId && window.utils.infiniteScrollControllers?.[sectionId]) {
        window.utils.resumeInfiniteScroll(sectionId);
      }
      hideLoading();
    }
  },

  setupSorting: function (options) {
    const {
      sortSelector,
      baseUrl,
      siteId,
      collectionId,
      collectionHandle,
      paginationType,
      productGrid,
      paginationContainer,
      productsPerPage,
      showLoading,
      hideLoading,
      loadProducts,
      loadingOverlay,
      infiniteScrollingLoading,
      totalCountElement,
      sectionId,
    } = options;

    if (!sortSelector) return;

    const defaultSelected =
      sortSelector.querySelector(".dropdown-selected")?.textContent;

    // if(defaultSelected === "Featured"){
    //   window.utils.sortParam = "sort.ga_unique_purchases=desc";
    // }

    const dropdownOptions = sortSelector.querySelector(".dropdown-options");

    function toggleChevronDisplay(container) {
      const chevronDown = container.querySelector(".chevron-down");
      const chevronUp = container.querySelector(".chevron-up");

      const isDownVisible = chevronDown.style.display !== "none";
      chevronDown.style.display = isDownVisible ? "none" : "inline";
      chevronUp.style.display = isDownVisible ? "inline" : "none";
    }

    const toggleDropdown = () => {
      if (dropdownOptions.style.visibility === "visible") {
        dropdownOptions.style.visibility = "hidden";
        toggleChevronDisplay(sortSelector);
      } else {
        dropdownOptions.style.visibility = "visible";
        toggleChevronDisplay(sortSelector);
      }
    };

    sortSelector.addEventListener("click", () => {
      toggleDropdown();
    });

    const closeOnClickOutside = (e) => {
      if (!sortSelector.contains(e.target)) {
        const chevronDown = sortSelector.querySelector(".chevron-down");
        const chevronUp = sortSelector.querySelector(".chevron-up");

        dropdownOptions.style.visibility = "hidden";
        chevronDown.style.display = "inline";
        chevronUp.style.display = "none";

        document.removeEventListener("click", closeOnClickOutside);
      }
    };

    // Handle selected item click
    sortSelector.addEventListener("click", (e) => {
      e.stopPropagation();

      document.addEventListener("click", closeOnClickOutside);
    });

    // Handle click on list items
    sortSelector
      .querySelector(".dropdown-options")
      .addEventListener("click", (e) => {
        const listItem = e.target.closest("li");
        if (!listItem || !listItem.dataset.value) return;

        const selectedText = listItem.textContent;
        sortSelector.querySelector(".dropdown-selected").textContent =
          selectedText;

        const sortValue = listItem.dataset.value;

        // ! need to map with search spring sort params
        switch (sortValue) {
          case "ga_unique_purchases_desc":
            window.utils.sortParam = "";
            break;
          case "featured":
            window.utils.sortParam = "sort.ss_boost=desc";
            break;
          case "ss_price_asc":
            window.utils.sortParam = "sort.ss_price=asc";
            break;
          case "ss_price_desc":
            window.utils.sortParam = "sort.ss_price=desc";
            break;
          case "ss_pct_off_desc":
            window.utils.sortParam = "sort.ss_pct_off=desc";
            break;
          case "variant_mfield_custom_item_revenue_asc":
            window.utils.sortParam =
              "sort.variant_mfield_custom_item_revenue=asc";
            break;
          case "mfield_analytics_product_revenue_ytd_desc":
            window.utils.sortParam =
              "sort.mfield_analytics_product_revenue_ytd=desc";
            break;
          default:
            window.utils.sortParam = "";
        }

        // Update active class
        Array.from(sortSelector.querySelectorAll("li")).forEach((li) => {
          li.classList.remove("active");
        });
        listItem.classList.add("active");

        // Persist sort selection in sessionStorage along with any existing filter data, keyed by collection
        const storedState = utils.loadSavedFilters(collectionHandle) || {
          filters: {},
          handle: collectionHandle,
        };
        storedState.sortParam = window.utils.sortParam;
        storedState.handle = collectionHandle;
        sessionStorage.setItem("plpActiveFilters", JSON.stringify(storedState));

        const globalUrl = window.utils.globalUrl(
          baseUrl,
          (page = 1),
          productsPerPage
        );
        const url = `${globalUrl}${window.utils.filterParams}${
          window.utils.sortParam && `&${window.utils.sortParam}`
        }`;

        if (paginationType === "infinite_scroll") {
          utils.refreshInfiniteScroll(sectionId, {
            baseUrl,
          });
        } else {
          loadProducts({
            page: 1,
            baseUrl: url,
            productsPerPage,
            productGrid,
            paginationContainer,
            showLoading,
            hideLoading,
            callback: () => {
              if (paginationType === "infinite_scroll") {
                utils.resumeInfiniteScroll(sectionId);
              }
            },
            totalCountElement,
            filterParams: window.utils.filterParams,
            sectionId,
          });
        }

        const scrollToTop = () =>
          window.scrollTo({ top: 0, behavior: "instant" });
        scrollToTop();

        // Reflect current filters + sort in the URL
        (() => {
          const newUrl = new URL(
            window.location.pathname,
            window.location.origin
          );
          let search = "?page=1";
          if (window.utils.filterParams)
            search += `${window.utils.filterParams}`;
          if (window.utils.sortParam) search += `&${window.utils.sortParam}`;
          newUrl.search = search;
          window.history.pushState({}, "", newUrl);
        })();
      });
  },

  setupInfiniteScroll: ({
    baseUrl,
    productsPerPage,
    productGrid,
    loadingOverlay,
    infiniteScrollingLoading,
    loadProducts,
    totalCountElement,
    sortSelector,
    sectionId,
    loadMoreAfterCount,
    initialPage = 1,
  }) => {
    // CHANGED: accept initial page from URL for infinite scroll restore
    initialPage = Math.max(1, Number(initialPage) || 1);
    const requestedProductsPerPage = Math.max(1, Number(productsPerPage) || 1);
    const maxSearchspringResultsPerPage = 24;
    const effectiveProductsPerPage = Math.min(
      requestedProductsPerPage,
      maxSearchspringResultsPerPage
    );
    const apiResultsPerPage = effectiveProductsPerPage;
    const sentinelId = `infinite-scroll-sentinel-${sectionId}`;
    const loadMoreWrapper = document.getElementById(
      `infinite-scroll-load-more-wrapper-${sectionId}`
    );
    const loadMoreButton = document.getElementById(
      `infinite-scroll-load-more-${sectionId}`
    );
    const hasManualLoadMoreUi = Boolean(loadMoreButton);
    const normalizedLoadMoreThreshold = hasManualLoadMoreUi
      ? Number.isFinite(Number(loadMoreAfterCount)) &&
        Number(loadMoreAfterCount) > 0
        ? Number(loadMoreAfterCount)
        : 36
      : null;
    if (requestedProductsPerPage > maxSearchspringResultsPerPage) {
      console.warn(
        `[PLP] products_per_page=${requestedProductsPerPage} exceeds Searchspring limit (${maxSearchspringResultsPerPage}). Using ${effectiveProductsPerPage} for infinite scroll requests.`
      );
    }
    let isLoading = false;
    let observer;
    let effectiveBaseUrl = baseUrl;
    let bufferedResults = [];
    let bufferedIndex = 0;
    let batchRequestPage = 1;
    let totalRenderedProducts = 0;
    let totalResults = 0;
    let nextLoadMoreTriggerCount = normalizedLoadMoreThreshold;
    const nudgeIntersectionObserver = () => {
      const sentinel = document.getElementById(sentinelId);
      if (!observer || !sentinel) return;
      requestAnimationFrame(() => {
        const el = document.getElementById(sentinelId);
        if (observer && el) {
          observer.unobserve(el);
          observer.observe(el);
        }
      });
    };
    const marketingBlocks = utils.getMarketingBlocks(sectionId);
    const marketingBlockMap =
      utils.indexMarketingBlocksByPosition(marketingBlocks);

    const showLoading = () => (loadingOverlay.style.display = "block");
    const hideLoading = () => (loadingOverlay.style.display = "none");

    const hideLoadMoreButton = () => {
      if (!loadMoreWrapper) return;
      loadMoreWrapper.style.display = "none";
    };

    const showLoadMoreButton = () => {
      if (!loadMoreWrapper || !loadMoreButton) return;
      loadMoreWrapper.style.display = "flex";
      loadMoreButton.disabled = false;
    };

    const updateUrl = () => {
      const visualPage = Math.max(
        1,
        Math.ceil(totalRenderedProducts / Math.max(effectiveProductsPerPage, 1)) ||
          1
      );
      const url = new URL(window.location.href);
      url.searchParams.set("page", visualPage);
      window.history.pushState({}, "", url);
    };

    const updateInfiniteScrollPaginationData = () => {
      const visualCurrentPage = Math.max(
        1,
        Math.ceil(totalRenderedProducts / Math.max(effectiveProductsPerPage, 1)) ||
          1
      );
      const visualTotalPages =
        totalResults > 0
          ? Math.ceil(totalResults / Math.max(effectiveProductsPerPage, 1))
          : 1;

      utils.paginationData = {
        ...(utils.paginationData || {}),
        currentPage: visualCurrentPage,
        totalPages: visualTotalPages,
        totalResults,
        resultsPerPage: effectiveProductsPerPage,
      };
    };

    const clearBufferedResults = () => {
      bufferedResults = [];
      bufferedIndex = 0;
    };

    const getRemainingBufferedCount = () =>
      Math.max(0, bufferedResults.length - bufferedIndex);

    const hasReachedLoadMoreThreshold = () =>
      normalizedLoadMoreThreshold !== null &&
      totalRenderedProducts >= nextLoadMoreTriggerCount;

    const advanceLoadMoreThreshold = () => {
      if (normalizedLoadMoreThreshold === null) return;
      nextLoadMoreTriggerCount += normalizedLoadMoreThreshold;
    };

    const createSkeletonCards = (count) => {
      const skeletonHtml = Array(count)
        .fill(
          `
      <div class="skeleton-card">
        <div class="skeleton-image"></div>
        <div class="skeleton-text-line"></div>
        <div class="skeleton-text-line short"></div>
      </div>
    `).join('');
    
    const fragment = document.createDocumentFragment();
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = skeletonHtml;
    while (tempDiv.firstChild) {
      fragment.appendChild(tempDiv.firstChild);
    }
    return fragment;
  };

    const removeSkeletonCards = () => {
      const skeletonCards = productGrid.querySelectorAll(".skeleton-card");
      skeletonCards.forEach((card) => card.remove());
    };

    const refreshFacetsFromBatch = (data, requestPage) => {
      try {
        const filtersContainer = document.querySelector(".searchspring-filters");
        if (!filtersContainer) return;

        if (Array.isArray(data.facets)) {
          utils.renderFilters(data.facets, filtersContainer);
          return;
        }

        const currentUrl = `${window.utils.buildProductApiUrl(
          effectiveBaseUrl,
          requestPage,
          apiResultsPerPage,
          {
            stripFilterParams: true,
            stripSortParams: true,
          }
        )}${window.utils.filterParams}${
          window.utils.sortParam ? `&${window.utils.sortParam}` : ""
        }`;

        utils
          .loadFacets(currentUrl, null, null)
          .then((facets) => {
            utils.renderFilters(facets, filtersContainer);
          })
          .catch(() => {});
      } catch (_) {}
    };

    const renderChunk = async ({ results = [], replace = false }) => {
      if (!results.length) return;

      if (utils.isCustomCarpetPlp()) {
        await utils.prefetchCustomCarpetHoverGids(results);
      }

      const startIndex = totalRenderedProducts;
      const chunkHtml = utils.composeProductsWithMarketingBlocks({
        results,
        marketingBlockMap,
        startIndex,
      });

      if (replace) {
        productGrid.innerHTML = chunkHtml;
      } else {
        productGrid.insertAdjacentHTML("beforeend", chunkHtml);
      }

      await utils.resolvePendingMediaGids(productGrid);
      utils.initPLPVariantSync(productGrid, results);
      setTimeout(() => {
        window.utils.validateSwatchImages();
      }, 100);

      totalRenderedProducts += results.length;
      updateInfiniteScrollPaginationData();
      updateUrl();
    };

    const renderNextBufferedChunk = ({ replace = false, force = false } = {}) => {
      if (!force && hasReachedLoadMoreThreshold()) {
        if (totalRenderedProducts < totalResults) {
          showLoadMoreButton();
        } else {
          hideLoadMoreButton();
        }
        return false;
      }

      let nextChunkSize = Math.min(
        effectiveProductsPerPage,
        getRemainingBufferedCount()
      );
      if (!force && normalizedLoadMoreThreshold !== null) {
        const remainingUntilLoadMore =
          nextLoadMoreTriggerCount - totalRenderedProducts;
        nextChunkSize = Math.min(nextChunkSize, Math.max(0, remainingUntilLoadMore));
      }

      if (!nextChunkSize) {
        if (totalRenderedProducts < totalResults) {
          showLoadMoreButton();
        } else {
          hideLoadMoreButton();
        }
        return false;
      }

      const nextChunk = bufferedResults.slice(
        bufferedIndex,
        bufferedIndex + nextChunkSize
      );
      bufferedIndex += nextChunkSize;
      void renderChunk({ results: nextChunk, replace });

      if (totalRenderedProducts >= totalResults) {
        hideLoadMoreButton();
      } else if (hasReachedLoadMoreThreshold()) {
        showLoadMoreButton();
      } else {
        hideLoadMoreButton();
      }
      nudgeIntersectionObserver();
      return true;
    };

    // CHANGED: helper added to preload sequential infinite-scroll batches until the requested initial page is loaded
    const getCurrentVisualPage = () =>
      Math.max(
        1,
        Math.ceil(totalRenderedProducts / Math.max(effectiveProductsPerPage, 1)) ||
          1
      );

    const renderToInitialPage = async (targetPage) => {
      while (
        getCurrentVisualPage() < targetPage &&
        totalRenderedProducts < totalResults
      ) {
        if (getRemainingBufferedCount() > 0) {
          renderNextBufferedChunk({ force: true });
          continue;
        }

        await fetchBatch({
          requestPage: batchRequestPage + 1,
          replace: false,
        });
      }
    };

    const fetchBatch = ({
      requestPage,
      replace = false,
      onData = () => {},
    } = {}) => {
      const requestUrl = `${window.utils.buildProductApiUrl(
        effectiveBaseUrl,
        requestPage,
        apiResultsPerPage,
        {
          stripFilterParams: true,
          stripSortParams: true,
        }
      )}${
        window.utils.filterParams
      }${
        window.utils.sortParam ? `&${window.utils.sortParam}` : ""
      }`;
      isLoading = true;
      hideLoadMoreButton();
      if (loadMoreButton) {
        loadMoreButton.disabled = true;
      }

      if (replace) {
        showLoading();
        productGrid.innerHTML = "";
      } else {
        productGrid.appendChild(
          createSkeletonCards(
            Math.min(effectiveProductsPerPage, normalizedLoadMoreThreshold)
          )
        );
      }

      if (replace) {
        totalRenderedProducts = 0;
      }
      clearBufferedResults();

      // CHANGED: return the fetch promise so initial-page preloading can await the batch load
      return fetch(requestUrl)
        .then((response) => {
          return response.json();
        })
        .then((data) => {
          batchRequestPage = requestPage;
          totalResults = Number(data?.pagination?.totalResults) || 0;
          if (data?.sorting?.options?.length) {
            utils.populateSortingOptions(data?.sorting);
          }
          onData(data);
          refreshFacetsFromBatch(data, requestPage);

          if (!replace) {
            removeSkeletonCards();
          }

          if (!data.results?.length) {
            updateInfiniteScrollPaginationData();
            if (replace) {
              productGrid.innerHTML =
                '<p class="no-products">No products found in this collection.</p>';
            }
            hideLoadMoreButton();
            return;
          }

          bufferedResults = data.results;
          bufferedIndex = 0;
          try {
            renderNextBufferedChunk({ replace });
          } catch (renderError) {
            console.error("Error rendering batch products:", renderError);
            clearBufferedResults();
            if (!replace) {
              removeSkeletonCards();
            }
            throw renderError;
          }

          if (totalCountElement) {
            totalCountElement.style.display = "block";
            totalCountElement.textContent = `${totalResults} Items`;
          }
        })
        .catch((error) => {
          console.error("Error loading batch products:", error);
          clearBufferedResults();
          if (replace) {
            totalResults = 0;
            productGrid.innerHTML =
              '<p class="error-message">Error loading products. Please try again.</p>';
          } else {
            removeSkeletonCards();
          }
          hideLoadMoreButton();
        })
        .finally(() => {
          isLoading = false;
          updateInfiniteScrollPaginationData();
          hideLoading();
          if (loadMoreButton) {
            loadMoreButton.disabled = false;
          }
          nudgeIntersectionObserver();
        });
    };

    const setupIntersectionObserver = () => {
      const existingSentinel = document.getElementById(sentinelId);
      if (existingSentinel) {
        existingSentinel.remove();
      }

      const sentinel = document.createElement("div");
      sentinel.id = sentinelId;
      sentinel.style.height = "1px";
      sentinel.style.width = "100%";
      document.body.appendChild(sentinel);

      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !isLoading) {
            if (getRemainingBufferedCount() > 0) {
              renderNextBufferedChunk();
              return;
            }

            if (totalRenderedProducts < totalResults) {
              if (hasReachedLoadMoreThreshold()) {
                showLoadMoreButton();
              } else {
                fetchBatch({
                  requestPage: batchRequestPage + 1,
                  replace: false,
                });
              }
            }
          }
        },
        {
          root: null,
          rootMargin: "1300px 0px", // preload 1300px before
          threshold: 0,
        }
      );

      observer.observe(sentinel);
    };

    const resetController = ({ baseUrl: nextBaseUrl, isRefreshing = false } = {}) => {
      if (nextBaseUrl) {
        effectiveBaseUrl = nextBaseUrl;
      }
      batchRequestPage = 1;
      totalRenderedProducts = 0;
      totalResults = 0;
      nextLoadMoreTriggerCount = normalizedLoadMoreThreshold;
      clearBufferedResults();
      productGrid.innerHTML = "";
      hideLoadMoreButton();
      if (loadMoreButton) {
        loadMoreButton.disabled = false;
      }
      updateInfiniteScrollPaginationData();
      isLoading = !!isRefreshing;
    };

    utils.infiniteScrollControllers[sectionId] = {
      reset: resetController,
      // CHANGED: allow refresh to load the first page and then advance to the initial requested page
      refresh: async ({ baseUrl: nextBaseUrl, onData, initialPage = 1 } = {}) => {
        resetController({
          baseUrl: nextBaseUrl,
          isRefreshing: true,
        });
        await fetchBatch({
          requestPage: 1,
          replace: true,
          onData,
        });

        if (initialPage > 1) {
          await renderToInitialPage(initialPage);
        }
      },
      resume: () => {
        isLoading = false;
      },
      destroy: () => {
        if (observer) observer.disconnect();
        const sentinel = document.getElementById(sentinelId);
        if (sentinel) {
          sentinel.remove();
        }
        hideLoadMoreButton();
      },
    };

    if (loadMoreButton) {
      loadMoreButton.addEventListener("click", (event) => {
        event.preventDefault();
        if (isLoading || totalRenderedProducts >= totalResults) {
          hideLoadMoreButton();
          return;
        }

        hideLoadMoreButton();
        advanceLoadMoreThreshold();

        if (getRemainingBufferedCount() > 0) {
          renderNextBufferedChunk({ force: true });
          return;
        }

        fetchBatch({
          requestPage: batchRequestPage + 1,
          replace: false,
        });
      });
    }

    setupIntersectionObserver();
    // CHANGED: start infinite scroll with the initial URL page to restore current collection state
    utils.refreshInfiniteScroll(sectionId, { baseUrl, initialPage });

    // Cleanup method
    window.cleanupInfiniteScroll = () => {
      const controller = utils.infiniteScrollControllers?.[sectionId];
      if (controller?.destroy) {
        controller.destroy();
      }
    };
  },

  // Normalize theme/API filter labels (NBSP from SearchSpring vs regular spaces in customizer)
  normalizeFilterLabel: (label) => {
    if (label == null) return "";
    return String(label).replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();
  },

  // Whitelist display names → SearchSpring facet fields (fallback when labels differ)
  filterWhitelistFieldAliases: {
    "product type": [
      "ss_product_type",
      "mfield_new_my_fields_sub_category",
    ],
    "delivery time": ["variant_mfield_new_my_fields_lead_time"],
    "category": ["mfield_new_my_fields_main_category"],
    "brand": ["mfield_new_my_fields_brand_name"],
    "color": ["variant_mfield_new_my_fields_filter_color"],
    "price": ["ss_price"],
    "width": [
      "ss_all_width_filter",
      "variant_mfield_custom_width_filter",
    ],
  },

  resolveWhitelistFilterGroup: (whitelistLabel, groups) => {
    const normalized = utils
      .normalizeFilterLabel(whitelistLabel)
      .toLowerCase();
    if (!normalized) return null;

    const labelMatch = Array.from(groups.keys()).find(
      (g) =>
        utils.normalizeFilterLabel(g).toLowerCase() === normalized
    );
    if (labelMatch) return labelMatch;

    const aliasFields = utils.filterWhitelistFieldAliases[normalized];
    if (!aliasFields) return null;

    for (const [groupLabel, facetList] of groups) {
      const field = facetList[0]?.field;
      if (field && aliasFields.includes(field)) return groupLabel;
    }
    return null;
  },

  // Filter functionality
  renderFilters: (facets, filtersContainer) => {
    if (!facets || !filtersContainer) return;

    const filtersContent = filtersContainer.querySelector(
      ".searchspring-filters-content"
    );
    if (!filtersContent) return;

    const existingGroupStates = new Map();
    filtersContent
      .querySelectorAll(".collection-sidebar__group")
      .forEach((group) => {
        const heading = group.querySelector(".collection-sidebar__heading");
        if (!heading) return;

        const field = group.dataset.filterField;
        const label = heading.dataset.heading;
        const state = heading.classList.contains("closed") ? "closed" : "open";

        if (field) existingGroupStates.set(`field:${field}`, state);
        if (label) {
          existingGroupStates.set(
            `label:${utils.normalizeFilterLabel(label).toLowerCase()}`,
            state
          );
        }
      });

    let filtersHTML = "";

    // --- Dynamic grouping by label (preserve API order) ---
    const groups = new Map(); // label -> facets[]
    facets.forEach((facet) => {
      const label = facet.label || facet.field;
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label).push(facet);
    });

    // Build HTML with custom ordering if provided via data-show-filter-names
    let orderedLabels = [];
    const wrapperEl = document.querySelector(".searchspring-wrapper");

    if (wrapperEl && wrapperEl.dataset.showFilterNames) {
      orderedLabels = wrapperEl.dataset.showFilterNames
        .split(",")
        .map((s) => utils.normalizeFilterLabel(s))
        .filter(Boolean);
    } else if (wrapperEl && wrapperEl.dataset.showSearchPageFilterNames) {
      orderedLabels = wrapperEl.dataset.showSearchPageFilterNames
        .split(",")
        .map((s) => utils.normalizeFilterLabel(s))
        .filter(Boolean);
    }

    // Parse hidden filter labels (comma-separated) from wrapper attribute
    let hiddenFilterLabels = [];
    if (wrapperEl && wrapperEl.dataset.hiddenFilters) {
      hiddenFilterLabels = wrapperEl.dataset.hiddenFilters
        .split(",")
        .map((s) => utils.normalizeFilterLabel(s).toLowerCase())
        .filter(Boolean);
    }
    const hiddenFiltersSet = new Set(hiddenFilterLabels);

    // Parse collapsed filter labels (comma-separated) from wrapper attribute
    let collapsedFilterLabels = [];
    if (wrapperEl && wrapperEl.dataset.collapsedFilters) {
      collapsedFilterLabels = wrapperEl.dataset.collapsedFilters
        .split(",")
        .map((s) => utils.normalizeFilterLabel(s).toLowerCase())
        .filter(Boolean);
    }
    const collapsedFiltersSet = new Set(collapsedFilterLabels);

    const seen = new Set();
    const renderLabel = (label) => {
      const normalizedLabel = utils.normalizeFilterLabel(label).toLowerCase();
      if (hiddenFiltersSet.has(normalizedLabel)) return; // skip hidden filters
      if (!groups.has(label) || seen.has(label)) return;
      seen.add(label);
      const facetList = groups.get(label);
      const slug = label.toLowerCase().replace(/\s+/g, "-");
      const firstFacet = facetList[0];

      // === Hide filters with <= 1 option (except for certain fields) ===
      const exceptionFields = new Set([
        "ss_price", // price slider/range
        "variant_mfield_new_my_fields_filter_color", // color swatches
        // 'variant_mfield_new_my_fields_lead_time', // quickship consolidated
      ]);

      if (!exceptionFields.has(firstFacet.field)) {
        // Count values with non-zero count; fallback to total length if counts missing
        const totalOptions = facetList.reduce((sum, f) => {
          const valuesArr = f.values || [];
          const meaningful = valuesArr.filter((v) => (v.count ?? 1) > 0);
          return sum + meaningful.length;
        }, 0);
        // if (totalOptions <= 1) {
        //   return; // Skip rendering this filter group
        // }
      }

      // Special case: render Quickship filter directly without collapsible heading
      // if (label.toLowerCase() === 'quickship') {
      //   filtersHTML += `
      //     <div class="collection-sidebar__group quickship-group" data-filter-field="${firstFacet.field}" data-group="${slug.toLowerCase()}">
      //       ${facetList.map((facet) => utils.renderFilterOptions(facet, { label })).join('')}
      //     </div>`;
      //   return;
      // }

      const isMobileViewport = window.innerWidth <= 768;
      const isCollapsed = collapsedFiltersSet.has(normalizedLabel);
      const existingState =
        existingGroupStates.get(`field:${firstFacet.field}`) ||
        existingGroupStates.get(`label:${normalizedLabel}`);
      const shouldOpen = existingState
        ? existingState === "open"
        : !(isMobileViewport || isCollapsed);
      const headingStateClass = shouldOpen ? "open" : "closed";
      const contentDisplayStyle = shouldOpen ? "block" : "none";

      filtersHTML += `
        <div class="collection-sidebar__group" data-filter-field="${
          firstFacet.field
        }" data-group="${slug.toLowerCase()}">
          <button type="button" class="collection-sidebar__heading ${headingStateClass} " data-heading="${label}">
            <span data-sidebar-title>${label}</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10">\n  <path d="M.357 2.753l4.39 4.39a.357.357 0 0 0 .506 0l4.39-4.39" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>\n</svg>

          </button>
          <div class="collection-sidebar__content" style="display: ${contentDisplayStyle}; padding-bottom: 0px;">
            ${facetList
              .map((facet) => utils.renderFilterOptions(facet, { label }))
              .join("")}
          </div>
        </div>`;
    };

    // First render labels specified by merchant in the desired order
    orderedLabels.forEach((lbl) => {
      const matchKey = utils.resolveWhitelistFilterGroup(lbl, groups);
      if (matchKey) renderLabel(matchKey);
    });

    // Render any remaining groups only when merchant has NOT provided an explicit list
    if (orderedLabels.length === 0) {
      groups.forEach((_v, lbl) => {
        if (!seen.has(lbl)) renderLabel(lbl);
      });
    }

    filtersContent.innerHTML = filtersHTML;

    // ! this is a hack to remove the desktop-only filters on mobile
    if (window.innerWidth <= 768) {
      document.querySelector(".desktop-only").innerHTML = "";
    }
  },

    // For commonly used filter layout - map a common filter button group to Searchspring facet data
  getCommonFilterFacets: (group, facets) => {
    if (!group || !Array.isArray(facets)) return [];

    const normalizedGroup = utils
      .normalizeFilterLabel(group)
      .toLowerCase()
      .replace(/_/g, " ");

    const exactMatches = facets.filter((facet) => {
      const label = utils.normalizeFilterLabel(facet.label || "").toLowerCase();
      const field = String(facet.field || "").toLowerCase().trim();
      return label === normalizedGroup || field === normalizedGroup;
    });
    if (exactMatches.length) return exactMatches;

    const looseMatches = facets.filter((facet) => {
      const label = String(facet.label || "").toLowerCase();
      const field = String(facet.field || "").toLowerCase();
      return label.includes(normalizedGroup) || field.includes(normalizedGroup);
    });
    if (looseMatches.length) return looseMatches;

    const fieldMap = {
      price: "ss_price",
      brand: "mfield_new_my_fields_brand_name",
      size: "variant_mfield_custom_rug_size_bucket_filter",
      standard_sizes: "variant_mfield_custom_rug_size_bucket_filter",
      category: "mfield_new_my_fields_main_category",
      product_type: "ss_product_type",
      delivery_time: "variant_mfield_new_my_fields_lead_time",
      style: "mfield_custom_primary_style",
      color: "variant_mfield_new_my_fields_filter_color",
      sale_bucket: "variant_mfield_custom_sale_bucket",
    };

    const mappedField = fieldMap[group.toLowerCase()];
    if (mappedField) {
      return facets.filter((facet) => facet.field === mappedField);
    }

    return [];
  },

  // For commonly used filter layout - build HTML for the dropdown content of a button
  renderCommonlyUsedFilterDropdown: (group, facets) => {
    const facetList = utils.getCommonFilterFacets(group, facets);
    if (!facetList || facetList.length === 0) {
      return `<div class="custom-filter-dropdown-empty">No filter options available.</div>`;
    }

    return facetList
      .map(
        (facet) => `
        <div class="custom-filter-dropdown-group" data-filter-field="${facet.field}">
          ${facet.label ? `<div class="custom-filter-dropdown-heading">${facet.label}</div>` : ""}
          ${utils.renderFilterOptions(facet, { label: facet.label })}
        </div>`
      )
      .join("");
  },

  normalizeCommonFilterDropdownInputs: (dropdown, group) => {
    if (!dropdown) return;

    dropdown.querySelectorAll("[id]").forEach((element, index) => {
      const oldId = element.id;
      const newId = `${oldId}--common-${group}-${index}`;
      element.id = newId;

      dropdown
        .querySelectorAll(`label[for="${oldId.replace(/"/g, '\\"')}"]`)
        .forEach((label) => {
          label.setAttribute("for", newId);
        });
    });
  },

  // For commonly used filter layout - attach button/dropdown event handling and sync filter selections
  // For commonly used filter layout - setup event listeners for dropdown filters including dimension inputs for pricing
  setupCommonlyUsedFilterDropdowns: (
    facets,
    activeFilters,
    activeFiltersContainer,
    options
  ) => {
    if (utils.commonFilterDropdownsInitialized) return;
    utils.commonFilterDropdownsInitialized = true;

    if (!Array.isArray(facets)) {
      facets = [];
    }

    const closeAll = () => {
      document.querySelectorAll(".filter-dropdown").forEach((dropdown) => {
        dropdown.classList.remove("active");
        dropdown.style.removeProperty("--common-filter-dropdown-top");
      });
      document
        .querySelectorAll(".custom_commonly_used_filter_button.is-active")
        .forEach((button) => {
          button.classList.remove("is-active");
        });
    };

    const positionCommonFilterDropdown = (dropdown, button) => {
      if (!dropdown || !button) return;

      if (!window.matchMedia("(max-width: 768px)").matches) {
        dropdown.style.removeProperty("--common-filter-dropdown-top");
        return;
      }

      const controls = button.closest(".filter-toggle-container") || button.closest(".collection-controls");
      const referenceRect = (controls || button).getBoundingClientRect();
      dropdown.style.setProperty("--common-filter-dropdown-top", `${Math.round(referenceRect.bottom + 8)}px`);
    };

    const updateOpenCommonFilterDropdown = () => {
      const dropdown = document.querySelector(".filter-dropdown.active");
      if (!dropdown) return;

      const button = Array.from(
        document.querySelectorAll(".custom_commonly_used_filter_button.is-active")
      ).find((activeButton) => activeButton.dataset.group === dropdown.dataset.group);

      positionCommonFilterDropdown(dropdown, button);
    };

    document.addEventListener("click", (event) => {
      const button = event.target.closest(".custom_commonly_used_filter_button");
      const item = event.target.closest(".commonly-used-filter-item");

      if (!button && !item) {
        closeAll();
        return;
      }

      if (!button) {
        return;
      }

      event.preventDefault();
      const group = button.dataset.group;
      const dropdown = document.querySelector(`.filter-dropdown[data-group="${group}"]`);
      if (!dropdown) return;

      const isOpen = dropdown.classList.contains("active");
      closeAll();
      if (isOpen) {
        return;
      }

      const latestFacets = Array.isArray(utils.commonFilterFacets) && utils.commonFilterFacets.length
        ? utils.commonFilterFacets
        : facets;
      dropdown.innerHTML = utils.renderCommonlyUsedFilterDropdown(group, latestFacets);
      utils.normalizeCommonFilterDropdownInputs(dropdown, group);
      utils.syncFilterUIWithActiveFilters(null, activeFilters);
      dropdown.classList.add("active");
      button.classList.add("is-active");
      positionCommonFilterDropdown(dropdown, button);
    });

    window.addEventListener("scroll", updateOpenCommonFilterDropdown, { passive: true });
    window.addEventListener("resize", updateOpenCommonFilterDropdown);

    document.addEventListener("change", (event) => {
      const input = event.target.closest(".filter-item__input");
      if (!input || !event.target.closest(".filter-dropdown")) return;

      const field = input.dataset.field;
      const value = input.dataset.value;

      if (input.checked) {
        if (!activeFilters.has(field)) {
          activeFilters.set(field, []);
        }

        if (value.includes(",")) {
          const values = value.split(",");
          activeFilters.set(field, [
            ...new Set([...activeFilters.get(field), ...values]),
          ]);
        } else if (!activeFilters.get(field).includes(value)) {
          activeFilters.get(field).push(value);
        }
      } else {
        if (activeFilters.has(field)) {
          let values = activeFilters.get(field);

          if (value.includes(",")) {
            const valuesToRemove = value.split(",");
            values = values.filter((v) => !valuesToRemove.includes(v));
          } else {
            const index = values.indexOf(value);
            if (index > -1) {
              values.splice(index, 1);
            }
          }

          if (values.length === 0) {
            activeFilters.delete(field);
          } else {
            activeFilters.set(field, values);
          }
        }
      }

      utils.updateActiveFiltersDisplay(activeFiltersContainer, activeFilters, options);
      utils.applyFilters(activeFilters, options);
      utils.syncFilterUIWithActiveFilters(null, activeFilters);
    });

    const startDropdownDrag = (handle, dimensionFilter, event) => {
      event.preventDefault();
      const slider = dimensionFilter.querySelector(".dimension-slider");
      const sliderFill = dimensionFilter.querySelector(".dimension-slider-fill");
      const minHandle = dimensionFilter.querySelector(
        '.dimension-slider-handle[data-position="min"]'
      );
      const maxHandle = dimensionFilter.querySelector(
        '.dimension-slider-handle[data-position="max"]'
      );
      const rangeName = dimensionFilter
        .querySelector(".dimension-input")
        .dataset.dimension;
      const minInput = dimensionFilter.querySelector(
        '.dimension-input[data-position="min"]'
      );
      const maxInput = dimensionFilter.querySelector(
        '.dimension-input[data-position="max"]'
      );
      const dimension = handle.dataset.position;

      const sliderMin = parseFloat(slider.dataset.min) || 0;
      const sliderMax = parseFloat(slider.dataset.max) || 1;
      const rangeSpan = sliderMax - sliderMin;

      const moveListener = (moveEvt) => {
        const clientX = moveEvt.touches
          ? moveEvt.touches[0].clientX
          : moveEvt.clientX;
        const rect = slider.getBoundingClientRect();
        let percent = ((clientX - rect.left) / rect.width) * 100;
        percent = Math.min(Math.max(percent, 0), 100);

        const otherPercent = parseFloat(
          (dimension === "min" ? maxHandle : minHandle).style.left
        );
        if (dimension === "min" && percent > otherPercent) percent = otherPercent;
        if (dimension === "max" && percent < otherPercent) percent = otherPercent;

        handle.style.left = `${percent}%`;

        if (dimension === "min") {
          sliderFill.style.left = `${percent}%`;
          sliderFill.style.width = `${otherPercent - percent}%`;
        } else {
          sliderFill.style.width = `${percent - otherPercent}%`;
        }

        const value = Math.round(sliderMin + (percent / 100) * rangeSpan);
        if (dimension === "min") {
          minInput.value = value;
        } else {
          maxInput.value = value;
        }
      };

      const endListener = () => {
        document.removeEventListener("mousemove", moveListener);
        document.removeEventListener("touchmove", moveListener);
        document.removeEventListener("mouseup", endListener);
        document.removeEventListener("touchend", endListener);
        minInput.dispatchEvent(new Event("change", { bubbles: true }));
      };

      document.addEventListener("mousemove", moveListener);
      document.addEventListener("touchmove", moveListener, { passive: false });
      document.addEventListener("mouseup", endListener);
      document.addEventListener("touchend", endListener);
    };

    document.addEventListener("mousedown", (event) => {
      const handle = event.target.closest(".dimension-slider-handle");
      const dropdown = event.target.closest(".filter-dropdown");
      if (!handle || !dropdown) return;
      startDropdownDrag(handle, handle.closest(".dimension-filter"), event);
    });

    document.addEventListener(
      "touchstart",
      (event) => {
        const handle = event.target.closest(".dimension-slider-handle");
        const dropdown = event.target.closest(".filter-dropdown");
        if (!handle || !dropdown) return;
        startDropdownDrag(handle, handle.closest(".dimension-filter"), event);
      },
      { passive: false }
    );

    document.addEventListener("change", (event) => {
      const input = event.target.closest(".dimension-input");
      if (!input || !event.target.closest(".filter-dropdown")) return;

      const field = input.dataset.field;
      const dimensionFilter = input.closest(".dimension-filter");
      const slider = dimensionFilter.querySelector(".dimension-slider");

      const sliderMin = parseFloat(slider.dataset.min) || 0;
      const sliderMax = parseFloat(slider.dataset.max) || 0;

      const minInput = dimensionFilter.querySelector(
        '.dimension-input[data-position="min"]'
      );
      const maxInput = dimensionFilter.querySelector(
        '.dimension-input[data-position="max"]'
      );

      let minValue = parseFloat(minInput.value.replace(/[^0-9.]/g, ""));
      let maxRaw = String(maxInput.value);
      let maxNumericPart = parseFloat(maxRaw.replace(/[^0-9.]/g, ""));

      if (isNaN(minValue)) minValue = sliderMin;
      minValue = Math.max(sliderMin, Math.min(minValue, sliderMax));

      let maxValue;
      if (/\+$/.test(maxRaw.trim())) {
        maxValue = sliderMax;
      } else {
        maxValue = isNaN(maxNumericPart) ? sliderMax : maxNumericPart;
        maxValue = Math.min(Math.max(maxValue, minValue), sliderMax);
      }

      minInput.value = minValue;
      maxInput.value = maxValue;

      const rangeValue = `${minValue}-${maxValue}`;
      const defaultRange = `${sliderMin}-${sliderMax}`;

      if (rangeValue !== defaultRange) {
        activeFilters.set(field, [rangeValue]);
      } else {
        activeFilters.delete(field);
      }

      utils.updateActiveFiltersDisplay(activeFiltersContainer, activeFilters, options);
      utils.applyFilters(activeFilters, options);
      utils.syncFilterUIWithActiveFilters(null, activeFilters);
    });

    document.addEventListener("click", (event) => {
      const btn = event.target.closest(".show-more-btn");
      if (!btn || !event.target.closest(".filter-dropdown")) return;

      const list = btn.closest(".filter-drawer__list");
      if (!list) return;

      const hiddenItems = list.querySelectorAll(".filter-item--hidden");
      const isCollapsed = btn.dataset.state === "collapsed";

      hiddenItems.forEach((li) => {
        li.style.display = isCollapsed ? "block" : "none";
      });

      btn.textContent = isCollapsed ? "View less" : "View more";
      btn.dataset.state = isCollapsed ? "expanded" : "collapsed";
    });

    document.addEventListener("click", (event) => {
      const swatch = event.target.closest(".filter-item__swatch");
      if (!swatch || !event.target.closest(".filter-dropdown")) return;

      const field = swatch.dataset.field;
      const value = swatch.dataset.value;

      swatch.classList.toggle("selected");

      if (!activeFilters.has(field)) {
        activeFilters.set(field, []);
      }
      const values = activeFilters.get(field);

      if (swatch.classList.contains("selected")) {
        if (!values.includes(value)) {
          values.push(value);
        }
      } else {
        const index = values.indexOf(value);
        if (index > -1) {
          values.splice(index, 1);
        }
      }

      if (values.length === 0) {
        activeFilters.delete(field);
      }

      utils.updateActiveFiltersDisplay(activeFiltersContainer, activeFilters, options);
      utils.applyFilters(activeFilters, options);
      utils.syncFilterUIWithActiveFilters(null, activeFilters);
    });
  },


  renderFilterOptions: (facet, filterConfig) => {
    // Exclude composite pipe-separated values (e.g. "Up to 20% Off|Up to 30% Off").
    // renderFilterOptions only shows the first segment for display, which duplicates single-bucket rows.
    const excludePipeSeparatedValues = new Set([
      "variant_mfield_custom_jewelry_metal_filter",
      "variant_mfield_custom_sale_bucket",
    ]);
    const facetValues = excludePipeSeparatedValues.has(facet.field)
      ? (facet.values || []).filter((v) => !String(v.value).includes("|"))
      : facet.values;

    // Quickship consolidated filter for lead time < 4 weeks
    // if (facet.field === "variant_mfield_new_my_fields_lead_time") {
    //   return utils.renderQuickshipFilter(facet);
    // }

    if (facet.field === "ss_price") {
      return utils.renderPriceFilter(facet);
    }

    if (facet.field === "variant_mfield_new_my_fields_filter_color") {
      return utils.renderColorFilter(facet);
    }

    if (facet.field === "variant_mfield_custom_rug_size_bucket_filter") {
      return utils.renderSizeFilter(facet);
    }

    if (facet.field === "variant_mfield_custom_width_filter") {
      return utils.renderWidthBucketFilter(facet);
    }

    // Width & Length rounded filters – use dropdown instead of slider
    if (
      facet.field === "mfield_custom_length_rounded_ft_filter" ||
      facet.field === "mfield_custom_width_rounded_ft_filter"
    ) {
      const dimType = facet.field.includes("width") ? "width" : "length";
      return utils.renderDropdownDimensionFilter(facet, dimType);
    }

    // === NEW: dedicated renderer for ss_all_width_filter (range buckets in inches) ===
    if (facet.field === "ss_all_width_filter") {
      return utils.renderWidthRangeFilter(facet);
    }

    // Standard checkbox filter using traditional Shopify filter-item structure
    const MAX_VISIBLE = 12;
    const itemsHTML = facetValues.map((value, idx) => {
      // Clean label for display

      let displayLabel = value.label != null ? value.label : value.value;
      // Ensure we are working with a string to avoid runtime errors
      if (typeof displayLabel !== "string") {
        try {
          displayLabel = JSON.stringify(displayLabel);
        } catch {
          displayLabel = String(displayLabel);
        }
      }
      let count = value.count;

      // Clean up label if it's in JSON format
      if (displayLabel.includes('["') || displayLabel.includes('"')) {
        try {
          const parsed = JSON.parse(displayLabel.replace(/\|/g, ","));
          displayLabel = Array.isArray(parsed) ? parsed.join(", ") : parsed;
        } catch (e) {
          displayLabel = displayLabel.replace(/\[|\]|"/g, "");
        }
      }

      // Clean up pipe-separated values for readability
      if (displayLabel.includes("|")) {
        displayLabel = displayLabel.split("|")[0];
      }

      // Clean up any category paths
      if (displayLabel.includes(">")) {
        displayLabel = displayLabel.split(">").pop().trim();
      }

      const isDisabled = count === 0 && !value.active;
      const inputId = `filter--${facet.field}--${displayLabel.replace(
        /[^a-zA-Z0-9]/g,
        ""
      )}-searchspring`;
      const hiddenClass = idx >= MAX_VISIBLE ? "filter-item--hidden" : "";

      return `
        <li class="filter-item filter-item-checkbox ${hiddenClass} ${
        isDisabled ? "filter-item--disabled" : ""
      }" data-type="checkbox" ${hiddenClass ? 'style="display:none;"' : ""}>
          <label 
            data-filter
            data-filter-item-label
            class="filter-item__content filter-item__content--checkbox no-transition ${
              isDisabled ? "filter-item__content--disabled" : ""
            }"
            for="${inputId}"
          >
            <input 
              type="checkbox" 
              name="${facet.field}"
              data-value-escaped="${encodeURIComponent(value.value)}"
              value="${value.value}"
              class="filter-item__input visually-hidden"
              id="${inputId}"
              data-field="${facet.field}"
              data-value="${value.value}"
              ${value.active ? "checked" : ""}
              ${isDisabled ? "disabled" : ""}
              data-filter="${facet.field}=${value.value}"
              data-filter-item
            >
            <div class="filter-item__checkbox">
              <svg class="icon icon-check" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="m1 3 2.5 2.5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <span class="filter-item__label type-body-regular">${displayLabel}</span>
            <span class="filter-item__count">${count}</span>
          </label>
        </li>`;
    });

    const needsShowMore = facetValues.length > MAX_VISIBLE;
    const showMoreHTML = needsShowMore
      ? `<li class="filter-show-more" ><a  class="reset-all show-more-btn" style="cursor: pointer;" data-state="collapsed">View more</a></li>`
      : "";

    return `
      <ul class="filter-drawer__list">
        ${itemsHTML.join("")}
        ${showMoreHTML}
      </ul>
    `;
  },

  renderColorFilter: (facet) => {
    // Map of color names to hex codes for fallback
    const colorMap = {
      Black: "#000000",
      Blue: "#1e5aaa",
      Brown: "#8B4513",
      Gold: "#D4AF37",
      Green: "#2e8a4a",
      Grey: "#AAAAAA",
      Multi: "linear-gradient(135deg, #ff5e62, #2980B9, #1abc9c, #FFDD00)",
      Natural: "#E5DCC5",
      Orange: "#FFA500",
      Pink: "#FF7B9C",
      Purple: "#800080",
      Red: "#E63946",
      Silver: "#C0C0C0",
      White: "#FFFFFF",
      Yellow: "#F7DC6F",
      Cream: "#FFFDD0",
      Beige: "#F5F5DC",
    };

    const colorOptions = facet.values.flatMap(({ value: raw, count }) => {
      // raw === '["White"]' or maybe '["Blue"]|["White"]'
      const parts = raw.split("|");
      const colors = [];

      for (const part of parts) {
        try {
          const arr = JSON.parse(part);
          if (Array.isArray(arr)) colors.push(...arr);
        } catch {
          const cleaned = part.replace(/[\[\]"]/g, "");
          if (cleaned) colors.push(cleaned);
        }
      }

      return colors.length ? [{ value: raw, color: colors[0], count }] : [];
    });

    // Dedupe by color, keeping highest count
    const unique = {};
    for (const opt of colorOptions) {
      const key = opt.color.toLowerCase();
      if (!unique[key] || unique[key].count < opt.count) {
        unique[key] = opt;
      }
    }

    // Render only the colors we actually saw, in the order of colorMap
    return `
		<div class="color-filters">
		  ${Object.keys(colorMap)
        .map((colorName) => {
          const opt = unique[colorName.toLowerCase()];
          if (!opt) return "";

          const fileName = colorName.toLowerCase().replace(/\s+/g, "");
          const bgImg = `url(//abchome.com/cdn/shop/files/${fileName}.png)`;
          const bgColor = colorMap[colorName];

          return `
				<div
				  class="filter-item__swatch"
				  style="background-image: ${bgImg}; background-color: ${bgColor};"
				  tabindex="0"
				  aria-label="${colorName}"
				  data-color="${colorName}"
				  data-field="${facet.field}"
				  data-value='${opt.value.replace(/[\[\]"]/g, "")}'
				></div>
			  `;
        })
        .join("")}
		</div>
	  `;
  },

  renderSizeFilter: (facet) => {
    const lookup = {};

    facet.values.forEach((facetValue) => {
      // Skip values that contain multiple sizes separated by a pipe (e.g., "5x8|8x10")
      if (String(facetValue.value).includes("|")) return;
      const parts = String(facetValue.value).split("|");
      parts.forEach((token) => {
        const size = token.trim();
        if (!size) return;
        if (!lookup[size]) {
          lookup[size] = { size, count: 0, values: new Set() };
        }
        lookup[size].count += facetValue.count;
        lookup[size].values.add(facetValue.value);
      });
    });

    const sizeOptions = Object.values(lookup).filter((o) => o.count > 0);

    // Sort sizes for a user-friendly order:
    //  1) Numeric sizes ascending by area (width*length) – e.g., 4x6 < 5x8
    //  2) Non-numeric labels (Runner, Round, Small, Oversized, etc.) alphabetically afterwards
    const parseArea = (s) => {
      const match = s.match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)/i);
      if (!match) return null;
      return parseFloat(match[1]) * parseFloat(match[2]);
    };

    sizeOptions.sort((a, b) => {
      const areaA = parseArea(a.size);
      const areaB = parseArea(b.size);

      if (areaA !== null && areaB !== null) {
        return areaA - areaB;
      }
      if (areaA !== null) return -1;
      if (areaB !== null) return 1;
      return a.size.localeCompare(b.size);
    });

    return `
      <ul class="filter-drawer__list">
        ${sizeOptions
          .map((opt) => {
            const joinedValues = Array.from(opt.values).join(",");
            const isDisabled = opt.count === 0;
            const inputId = `filter--${facet.field}--${opt.size.replace(
              /[^a-zA-Z0-9]/g,
              ""
            )}--searchspring`;

            // Determine active state (if any of the underlying facet values is active)
            const isActive = facet.values.some(
              (fv) => fv.active && fv.value.split("|").includes(opt.size)
            );

            return `
              <li class="filter-item filter-item-checkbox ${
                isDisabled ? "filter-item--disabled" : ""
              }" data-type="checkbox">
                <label
                  data-filter
                  data-filter-item-label
                  class="filter-item__content filter-item__content--checkbox no-transition ${
                    isDisabled ? "filter-item__content--disabled" : ""
                  }"
                  for="${inputId}"
                >
                  <input
                    type="checkbox"
                    name="${facet.field}"
                    data-value-escaped="${encodeURIComponent(joinedValues)}"
                    value="${joinedValues}"
                    class="filter-item__input visually-hidden"
                    id="${inputId}"
                    data-field="${facet.field}"
                    data-value="${joinedValues}"
                    ${isActive ? "checked" : ""}
                    ${isDisabled ? "disabled" : ""}
                    data-filter="${facet.field}=${joinedValues}"
                    data-filter-item
                  >
                  <div class="filter-item__checkbox">
                    <svg class="icon icon-check" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="m1 3 2.5 2.5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                  <span class="filter-item__label type-body-regular">${
                    opt.size
                  }</span>
                  <span class="filter-item__count">${opt.count}</span>
                </label>
              </li>`;
          })
          .join("")}
      </ul>`;
  },

  renderDimensionFilter: (facet, dimensionType, minDefault, maxDefault) => {
    // Determine min & max dynamically from facet values when available
    let computedMin = Number.POSITIVE_INFINITY;
    let computedMax = Number.NEGATIVE_INFINITY;

    if (facet && Array.isArray(facet.values)) {
      facet.values.forEach((v) => {
        // Searchspring may provide either discrete or range facet objects
        if (v.low !== undefined && v.high !== undefined) {
          // Range style value
          const low = v.low === "*" ? 0 : parseFloat(v.low);
          const high = v.high === "*" ? parseFloat(v.low) : parseFloat(v.high);
          if (!isNaN(low)) computedMin = Math.min(computedMin, low);
          if (!isNaN(high)) computedMax = Math.max(computedMax, high);
        } else if (v.value !== undefined) {
          // Discrete value – can be pipe-separated list (e.g. "3|4")
          String(v.value)
            .split("|")
            .forEach((part) => {
              const num = parseFloat(part);
              if (!isNaN(num)) {
                computedMin = Math.min(computedMin, num);
                computedMax = Math.max(computedMax, num);
              }
            });
        }
      });
    }

    // Fallback to provided defaults when facet did not yield usable data
    if (!isFinite(computedMin)) computedMin = minDefault ?? 0;
    if (!isFinite(computedMax) || computedMax === 0) {
      computedMax = maxDefault ?? computedMin + 1;
    }

    // Label & formatting helpers
    const isPrice = dimensionType === "price";
    const unitLabel = isPrice ? "$" : "Ft.";
    const maxDisplay = isPrice ? `${computedMax}` : `${computedMax}`;

    // Build slider / input UI
    return `
      <div class="dimension-filter">
        <div class="dimension-slider" data-min="${computedMin}" data-max="${computedMax}">
          <div class="dimension-slider-fill" style="left: 0%; width: 100%;"></div>
          <div class="dimension-slider-handle" style="left: 0%;" data-position="min"></div>
          <div class="dimension-slider-handle" style="left: 100%;" data-position="max"></div>
        </div>
        <div class="dimension-inputs">
          <div class="dimension-input-group">
            <input 
              type="text" 
              class="dimension-input" 
              id="${dimensionType}-min" 
              value="${computedMin}" 
              data-field="${facet.field}" 
              data-dimension="${dimensionType}" 
              data-position="min"
            >
            <span class="dimension-label">${unitLabel}</span>
          </div>
          <span class="dimension-to">to</span>
          <div class="dimension-input-group">
            <input 
              type="text" 
              class="dimension-input" 
              id="${dimensionType}-max" 
              value="${maxDisplay}" 
              data-field="${facet.field}" 
              data-dimension="${dimensionType}" 
              data-position="max"
            >
            <span class="dimension-label">${unitLabel}</span>
          </div>
        </div>
      </div>
    `;
  },

  renderPriceFilter: (facet) => {
    return window.utils.renderDimensionFilter(facet, "price");
  },

  updateClassBasedOnWidth: () => {
    const productGrid = document.querySelector(".product-grid");
    if (!productGrid) {
      console.warn("Product grid not found!");
      return;
    }

    const swatches = document.querySelectorAll(
      ".product-card__swatches .new-swatch"
    );
    // const gridWidth = productGrid.offsetWidth;
    // const shouldBeLarge = gridWidth > 600;

    swatches.forEach((swatch) => {
      const isSidebarActive = swatch.classList.contains("active");

      if (isSidebarActive) {
        swatch.classList.remove("active");
      } else {
        swatch.classList.add("active");
      }
    });
  },
  buildFilterParams: (activeFilters) => {
    let str = "";
    activeFilters.forEach((values, field) => {
      values.forEach((value) => {
        if (
          field === "ss_price" ||
          field === "mfield_custom_length_rounded_ft_filter" ||
          field === "mfield_custom_width_rounded_ft_filter" ||
          field === "ss_all_width_filter"
        ) {
          const [min, max] = String(value).split("-");
          if (min && min !== "0") str += `&filter.${field}.low=${min}`;
          if (max && max !== "*") str += `&filter.${field}.high=${max}`;
        } else {
          str += `&filter.${field}=${encodeURIComponent(value)}`;
        }
      });
    });
    return str;
  },

  getFiltersFromUrl: () => {
    const params = new URLSearchParams(window.location.search);
    const active = new Map();
    const rangeBuffer = new Map();
    let sortParam = "";

    params.forEach((val, key) => {
      if (key.startsWith("filter.")) {
        const m = key.match(/^filter\.(.+?)(?:\.(low|high))?$/);
        if (!m) return;
        const [, field, part] = m;
        if (part) {
          if (!rangeBuffer.has(field)) rangeBuffer.set(field, {});
          rangeBuffer.get(field)[part] = val;
        } else {
          if (!active.has(field)) active.set(field, new Set());
          active.get(field).add(decodeURIComponent(val));
        }
      } else if (key.startsWith("sort.")) {
        sortParam = `${key}=${decodeURIComponent(val)}`;
      }
    });

    // consolidate range fields
    rangeBuffer.forEach((parts, field) => {
      const min = parts.low || "0";
      const max = parts.high || "*";
      const rangeVal = `${min}-${max}`;
      if (!active.has(field)) active.set(field, new Set());
      active.get(field).add(rangeVal);
    });

    // flatten Sets to Arrays for compatibility with rest of code
    const flattened = new Map();
    active.forEach((set, field) => {
      flattened.set(field, Array.from(set));
    });

    window.utils.filterParams = window.utils.buildFilterParams(flattened);
    window.utils.sortParam = sortParam;
    return flattened;
  },

  setDimensionFiltersFromActive: (activeFilters) => {
    const dimensionFields = [
      "ss_price",
      "mfield_custom_length_rounded_ft_filter",
      "mfield_custom_width_rounded_ft_filter",
      "ss_all_width_filter",
    ];

    activeFilters.forEach((values, field) => {
      if (!dimensionFields.includes(field)) return;
      values.forEach((value) => {
        const [min, max] = String(value).split("-");

        // dropdowns (if present)
        const minSelects = document.querySelectorAll(
          `.dimension-dropdown[data-field="${field}"][data-position="min"]`
        );
        const maxSelects = document.querySelectorAll(
          `.dimension-dropdown[data-field="${field}"][data-position="max"]`
        );
        minSelects.forEach((minSelect) => {
          minSelect.value = min;
        });
        maxSelects.forEach((maxSelect) => {
          maxSelect.value =
            max === "*"
              ? maxSelect.options[maxSelect.options.length - 1].value
              : max;
        });

        // slider inputs (if present)
        document
          .querySelectorAll(
            `.dimension-input[data-field="${field}"][data-position="min"]`
          )
          .forEach((minInput) => {
            minInput.value = min;
          });
        document
          .querySelectorAll(
            `.dimension-input[data-field="${field}"][data-position="max"]`
          )
          .forEach((maxInput) => {
            maxInput.value = max === "*" ? "" : max;
          });
      });
    });
  },

  setupFiltering: (options) => {
    const {
      sectionId,
      filtersContainer,
      activeFiltersContainer,
      clearAllBtn,
      filterToggleBtn,
      baseUrl,
      cleanBaseUrl,
      siteId,
      collectionHandle,
      productGrid,
      paginationContainer,
      productsPerPage,
      showLoading,
      hideLoading,
      loadProducts,
      totalCountElement,
      paginationType,
      setupInfiniteScroll,
      loadingOverlay,
      infiniteScrollingLoading,
    } = options;
    const baseForFacets = cleanBaseUrl || baseUrl;

    // 1️⃣  Build initial state from the URL (e.g. ?filter.color=Blue&sort.ss_boost=desc)
    let activeFilters = utils.getFiltersFromUrl();

    let currentFacets = [];

    // 2️⃣  Fall back to session-storage state ONLY if the URL did not specify anything
    if (activeFilters.size === 0) {
      const savedState =
        utils.loadSavedFilters && utils.loadSavedFilters(collectionHandle);
      if (savedState && savedState.filters) {
        Object.entries(savedState.filters).forEach(([field, values]) => {
          activeFilters.set(field, values);
        });
        if (savedState.sortParam) {
          window.utils.sortParam = savedState.sortParam;
        }
        window.utils.filterParams = utils.buildFilterParams(activeFilters);
      }
    }

    // Find the reset all button in filter toggle area
    const resetAllBtn = document.getElementById(`reset-all-${sectionId}`);
    // Hide the "Reset all" link by default; it will be shown when filters become active
    if (resetAllBtn) {
      resetAllBtn.style.display = "none";
    }

    // Toggle filter visibility
    if (filterToggleBtn) {
      filterToggleBtn.addEventListener("click", (e) => {
        e.preventDefault();

        utils.updateClassBasedOnWidth();
        filtersContainer.classList.toggle("active");
        filterToggleBtn.classList.toggle("active");
      });
    }

    // Clear all filters - traditional clear button
    const traditionalClearBtn = filtersContainer.querySelector(
      ".searchspring-clear-all"
    );
    if (traditionalClearBtn) {
      traditionalClearBtn.addEventListener("click", (e) => {
        e.preventDefault();
        activeFilters.clear();

        // Clear sessionStorage to prevent state restoration on navigation
        try {
          sessionStorage.removeItem("plpActiveFilters");
        } catch (err) {
          console.error("Error clearing session storage", err);
        }

        // Clear global filter params
        window.utils.filterParams = "";

        // Clear URL parameters - remove all filter.* params, keep sort and page
        const url = new URL(window.location.href);
        const paramsToRemove = [];
        url.searchParams.forEach((value, key) => {
          if (key.startsWith("filter.")) {
            paramsToRemove.push(key);
          }
        });
        paramsToRemove.forEach((key) => url.searchParams.delete(key));
        window.history.replaceState({}, "", url);

        // Uncheck all filter checkboxes (document-wide: desktop + mobile)
        document.querySelectorAll("[data-filter-item]").forEach((checkbox) => {
          checkbox.checked = false;
          checkbox.removeAttribute("checked");
        });
        document.querySelectorAll(".filter-item__swatch").forEach((swatch) =>
          swatch.classList.remove("selected")
        );

        // Update active filters display
        utils.updateActiveFiltersDisplay(
          activeFiltersContainer,
          activeFilters,
          options
        );

        // Update clear button state
        traditionalClearBtn.classList.remove("active");

        // Reload products only (cleanBaseUrl so API gets no filter params)
        utils.applyFilters(activeFilters, {
          baseUrl,
          cleanBaseUrl: cleanBaseUrl || baseUrl,
          siteId,
          collectionHandle,
          sectionId,
          productGrid,
          paginationContainer,
          productsPerPage,
          showLoading,
          hideLoading,
          loadProducts,
          totalCountElement,
          filtersContainer,
          paginationType,
          setupInfiniteScroll,
          loadingOverlay,
          infiniteScrollingLoading,
        });

        // Reset all dimension sliders
        window.utils.resetAllDimensionFilters();

        // After applyFilters completes and renderFilters runs, ensure all checkboxes stay unchecked
        setTimeout(() => {
          const allCheckboxesAfterRender = document.querySelectorAll("[data-filter-item]");
          allCheckboxesAfterRender.forEach((checkbox) => {
            checkbox.checked = false;
            checkbox.removeAttribute("checked");
          });
        }, 100);
      });
    }

    // Legacy clear button support
    if (clearAllBtn) {
      clearAllBtn.addEventListener("click", () => {
        activeFilters.clear();

        // Clear sessionStorage to prevent state restoration on navigation
        try {
          sessionStorage.removeItem("plpActiveFilters");
        } catch (err) {
          console.error("Error clearing session storage", err);
        }

        // Clear global filter params
        window.utils.filterParams = "";

        // Clear URL parameters - remove all filter.* params, keep sort and page
        const url = new URL(window.location.href);
        const paramsToRemove = [];
        url.searchParams.forEach((value, key) => {
          if (key.startsWith("filter.")) {
            paramsToRemove.push(key);
          }
        });
        paramsToRemove.forEach((key) => url.searchParams.delete(key));
        window.history.replaceState({}, "", url);

        // Uncheck all filter checkboxes (document-wide: desktop + mobile)
        document.querySelectorAll("[data-filter-item]").forEach((checkbox) => {
          checkbox.checked = false;
          checkbox.removeAttribute("checked");
        });
        document.querySelectorAll(".filter-item__swatch").forEach((swatch) =>
          swatch.classList.remove("selected")
        );

        utils.updateActiveFiltersDisplay(
          activeFiltersContainer,
          activeFilters,
          options
        );
        utils.applyFilters(activeFilters, {
          baseUrl,
          cleanBaseUrl: cleanBaseUrl || baseUrl,
          siteId,
          collectionHandle,
          sectionId,
          productGrid,
          paginationContainer,
          productsPerPage,
          showLoading,
          hideLoading,
          loadProducts,
          totalCountElement,
          filtersContainer,
          paginationType,
          setupInfiniteScroll,
          loadingOverlay,
          infiniteScrollingLoading,
        });

        // Reset all dimension sliders
        window.utils.resetAllDimensionFilters();

        // After applyFilters completes and renderFilters runs, ensure all checkboxes stay unchecked
        setTimeout(() => {
          document.querySelectorAll("[data-filter-item]").forEach((checkbox) => {
            checkbox.checked = false;
            checkbox.removeAttribute("checked");
          });
        }, 100);
      });
    }

    // Reset all button in the filter toggle area
    if (resetAllBtn) {
      resetAllBtn.addEventListener("click", (e) => {
        e.preventDefault();

        // Clear active filters map
        activeFilters.clear();

        // Clear sessionStorage to prevent state restoration on navigation
        try {
          sessionStorage.removeItem("plpActiveFilters");
        } catch (err) {
          console.error("Error clearing session storage", err);
        }

        // Clear global filter params
        window.utils.filterParams = "";

        // Clear URL parameters - remove all filter.* params, keep sort and page
        const url = new URL(window.location.href);
        const paramsToRemove = [];
        url.searchParams.forEach((value, key) => {
          if (key.startsWith("filter.")) {
            paramsToRemove.push(key);
          }
        });
        paramsToRemove.forEach((key) => url.searchParams.delete(key));
        window.history.replaceState({}, "", url);

        // Uncheck all filter checkboxes (document-wide: desktop + mobile)
        document.querySelectorAll("[data-filter-item]").forEach((checkbox) => {
          checkbox.checked = false;
          checkbox.removeAttribute("checked");
        });
        document.querySelectorAll(".filter-item__swatch").forEach((swatch) =>
          swatch.classList.remove("selected")
        );

        // Update UI displays
        utils.updateActiveFiltersDisplay(
          activeFiltersContainer,
          activeFilters,
          options
        );

        // Apply cleared filters (cleanBaseUrl so API gets full collection count)
        utils.applyFilters(activeFilters, {
          baseUrl,
          cleanBaseUrl: cleanBaseUrl || baseUrl,
          siteId,
          collectionHandle,
          sectionId,
          productGrid,
          paginationContainer,
          productsPerPage,
          showLoading,
          hideLoading,
          loadProducts,
          totalCountElement,
          filtersContainer,
          paginationType,
          setupInfiniteScroll,
          loadingOverlay,
          infiniteScrollingLoading,
        });

        // Reset dimension sliders
        window.utils.resetAllDimensionFilters();

        // After applyFilters completes and renderFilters runs, ensure all checkboxes stay unchecked
        // This handles the case where API returns facets with active: true based on URL
        setTimeout(() => {
          const allCheckboxesAfterRender = document.querySelectorAll("[data-filter-item]");
          allCheckboxesAfterRender.forEach((checkbox) => {
            checkbox.checked = false;
            checkbox.removeAttribute("checked");
          });
        }, 100);
      });
    }

    // Load initial facets (use clean base so we get full unfiltered facet set)
    utils.loadFacets(baseForFacets, siteId, collectionHandle).then((facets) => {
      currentFacets = facets;
      utils.renderFilters(facets, filtersContainer);
      utils.setupFilterEventListeners(
        filtersContainer,
        activeFilters,
        activeFiltersContainer,
        {
          baseUrl,
          cleanBaseUrl: cleanBaseUrl || baseUrl,
          siteId,
          collectionHandle,
          sectionId,
          productGrid,
          paginationContainer,
          productsPerPage,
          showLoading,
          hideLoading,
          loadProducts,
          totalCountElement,
          filtersContainer,
          paginationType,
          setupInfiniteScroll,
          loadingOverlay,
          infiniteScrollingLoading,
        }
      );

      // === NEW: bring UI & product grid in sync with any restored filters ===
      utils.syncFilterUIWithActiveFilters(filtersContainer, activeFilters);

      // For commonly used filter layout - initialize button dropdown handling after facets load
      utils.commonFilterFacets = currentFacets;
      utils.setupCommonlyUsedFilterDropdowns(
        currentFacets,
        activeFilters,
        activeFiltersContainer,
        options
      );

      // Also sync sliders / dimension dropdowns
      utils.setDimensionFiltersFromActive(activeFilters);

      if (activeFilters.size > 0) {
        utils.updateActiveFiltersDisplay(
          activeFiltersContainer,
          activeFilters,
          options
        );
        utils.syncFilterUIWithActiveFilters(filtersContainer, activeFilters);
        utils.applyFilters(activeFilters, options);
      }
    });
  },

  loadFacets: async (baseUrl, siteId, collectionHandle) => {
    try {
      // Facets are already included in the main search response
      // Add required parameters: page and resultsPerPage
      const separator = baseUrl.includes('?') ? '&' : '?';
      const facetsUrl = `${baseUrl}${separator}page=1&resultsPerPage=1`;
      const response = await fetch(facetsUrl);
      const data = await response.json();
      return data.facets || [];
    } catch (error) {
      console.error("Error loading facets:", error);
      return [];
    }
  },

  setupFilterEventListeners: (
    filtersContainer,
    activeFilters,
    activeFiltersContainer,
    options
  ) => {
    // Filter group toggle using traditional Shopify structure
    filtersContainer.addEventListener("click", (e) => {
      if (e.target.closest(".collection-sidebar__heading")) {
        const filterGroup = e.target.closest(".collection-sidebar__group");
        const content = filterGroup.querySelector(
          ".collection-sidebar__content"
        );
        const heading = filterGroup.querySelector(
          ".collection-sidebar__heading"
        );
        const chevronIcon = heading.querySelector(".icon-chevron-down");

        const isExpanded = !heading.classList.contains("closed");

        // Ensure the content element is prepared for animation
        content.style.overflow = "hidden";
        if (!content.style.transition) {
          content.style.transition = "max-height 0.35s ease"; // slightly shorter & smoother
        }

        if (isExpanded) {
          // Collapse with smoother animation
          const startHeight = content.scrollHeight + "px";
          content.style.maxHeight = startHeight;
          // Use RAF to ensure start height is applied before animating to 0
          requestAnimationFrame(() => {
            content.style.maxHeight = "0px";
          });

          heading.classList.add("closed");
          heading.classList.remove("open");

          const onCollapseEnd = () => {
            // Ensure collapsed state retains without triggering additional reflow
            content.style.maxHeight = "0px";

            content.removeEventListener("transitionend", onCollapseEnd);
          };
          content.addEventListener("transitionend", onCollapseEnd, {
            once: true,
          });

          if (chevronIcon) {
            chevronIcon.style.transform = "rotate(0deg)";
          }
        } else {
          // Expand with smoother animation
          heading.classList.remove("closed");
          heading.classList.add("open");

          // Prepare the element for measuring
          content.style.display = "block";
          content.style.maxHeight = "0px";

          // Wait for next frame then animate to full height
          requestAnimationFrame(() => {
            const targetHeight = content.scrollHeight + "px";
            content.style.maxHeight = targetHeight;
          });

          const onExpandEnd = () => {
            // Clean-up to allow natural sizing
            content.style.maxHeight = "";
            content.style.overflow = "";
            content.removeEventListener("transitionend", onExpandEnd);
          };
          content.addEventListener("transitionend", onExpandEnd, {
            once: true,
          });

          if (chevronIcon) {
            chevronIcon.style.transform = "rotate(180deg)";
          }
        }
      }
    });

    // Checkbox filters - only reload products, not filters
    filtersContainer.addEventListener("change", (e) => {
      if (
        e.target.type === "checkbox" &&
        e.target.hasAttribute("data-filter-item")
      ) {
        const field = e.target.dataset.field;
        const value = e.target.dataset.value;

        if (e.target.checked) {
          if (!activeFilters.has(field)) {
            activeFilters.set(field, []);
          }

          // Handle comma-separated values (for size filter)
          if (value.includes(",")) {
            const values = value.split(",");
            activeFilters.set(field, [
              ...new Set([...activeFilters.get(field), ...values]),
            ]);
          } else if (!activeFilters.get(field).includes(value)) {
            activeFilters.get(field).push(value);
          }
        } else {
          if (activeFilters.has(field)) {
            const values = activeFilters.get(field);

            if (value.includes(",")) {
              // Handle comma-separated values – remove all underlying selections
              const valuesToRemove = value.split(",");
              activeFilters.set(
                field,
                values.filter((v) => !valuesToRemove.includes(v))
              );
            } else {
              const index = values.indexOf(value);
              if (index > -1) {
                values.splice(index, 1);
              }
            }

            if (activeFilters.get(field).length === 0) {
              activeFilters.delete(field);
            }
          }
        }

        utils.updateActiveFiltersDisplay(
          activeFiltersContainer,
          activeFilters,
          options
        );
        utils.syncFilterUIWithActiveFilters(filtersContainer, activeFilters);
        utils.applyFilters(activeFilters, options);
      }
    });

    // Color filters
    filtersContainer.addEventListener("click", (e) => {
      if (e.target.classList.contains("filter-item__swatch")) {
        const field = e.target.dataset.field;
        const value = e.target.dataset.value;
        const color = e.target.dataset.color;

        e.target.classList.toggle("selected");

        if (e.target.classList.contains("selected")) {
          if (!activeFilters.has(field)) {
            activeFilters.set(field, []);
          }
          if (!activeFilters.get(field).includes(value)) {
            activeFilters.get(field).push(value);
          }
        } else {
          if (activeFilters.has(field)) {
            const values = activeFilters.get(field);
            const index = values.indexOf(value);
            if (index > -1) {
              values.splice(index, 1);
            }
            if (values.length === 0) {
              activeFilters.delete(field);
            }
          }
        }

        utils.updateActiveFiltersDisplay(
          activeFiltersContainer,
          activeFilters,
          options
        );
        utils.syncFilterUIWithActiveFilters(filtersContainer, activeFilters);
        utils.applyFilters(activeFilters, options);
      }
    });

    // === New: Slider handle dragging ===
    const startDrag = (handle, dimensionFilter, e) => {
      e.preventDefault();
      const slider = dimensionFilter.querySelector(".dimension-slider");
      const sliderFill = dimensionFilter.querySelector(
        ".dimension-slider-fill"
      );
      const minHandle = dimensionFilter.querySelector(
        '.dimension-slider-handle[data-position="min"]'
      );
      const maxHandle = dimensionFilter.querySelector(
        '.dimension-slider-handle[data-position="max"]'
      );
      const dimension = handle.dataset.position; // min or max
      const field =
        dimensionFilter.querySelector(".dimension-input").dataset.field; // any input
      const rangeName =
        dimensionFilter.querySelector(".dimension-input").dataset.dimension;
      const minInput = document.getElementById(`${rangeName}-min`);
      const maxInput = document.getElementById(`${rangeName}-max`);

      const sliderMin = parseFloat(slider.dataset.min) || 0;
      const sliderMax = parseFloat(slider.dataset.max) || 1;
      const rangeSpan = sliderMax - sliderMin;

      const moveListener = (moveEvt) => {
        const clientX = moveEvt.touches
          ? moveEvt.touches[0].clientX
          : moveEvt.clientX;
        const rect = slider.getBoundingClientRect();
        let percent = ((clientX - rect.left) / rect.width) * 100;
        percent = Math.min(Math.max(percent, 0), 100);

        // prevent crossing handles
        const otherPercent = parseFloat(
          (dimension === "min" ? maxHandle : minHandle).style.left
        );
        if (dimension === "min" && percent > otherPercent)
          percent = otherPercent;
        if (dimension === "max" && percent < otherPercent)
          percent = otherPercent;

        handle.style.left = `${percent}%`;

        if (dimension === "min") {
          sliderFill.style.left = `${percent}%`;
          sliderFill.style.width = `${otherPercent - percent}%`;
        } else {
          sliderFill.style.width = `${percent - otherPercent}%`;
        }

        // Calculate value & update input
        const value = Math.round(sliderMin + (percent / 100) * rangeSpan);
        if (dimension === "min") {
          minInput.value = value;
        } else {
          maxInput.value = value;
        }
      };

      const endListener = () => {
        document.removeEventListener("mousemove", moveListener);
        document.removeEventListener("touchmove", moveListener);
        document.removeEventListener("mouseup", endListener);
        document.removeEventListener("touchend", endListener);
        minInput.dispatchEvent(new Event("blur", { bubbles: true })); // trigger apply logic
      };

      document.addEventListener("mousemove", moveListener);
      document.addEventListener("touchmove", moveListener, { passive: false });
      document.addEventListener("mouseup", endListener);
      document.addEventListener("touchend", endListener);
    };

    filtersContainer.addEventListener("mousedown", (e) => {
      if (e.target.classList.contains("dimension-slider-handle")) {
        startDrag(e.target, e.target.closest(".dimension-filter"), e);
      }
    });
    filtersContainer.addEventListener(
      "touchstart",
      (e) => {
        if (e.target.classList.contains("dimension-slider-handle")) {
          startDrag(e.target, e.target.closest(".dimension-filter"), e);
        }
      },
      { passive: false }
    );
    // === End drag support ===

    // Dimension inputs
    filtersContainer.addEventListener("input", (e) => {
      if (e.target.classList.contains("dimension-input")) {
        const field = e.target.dataset.field;
        const dimension = e.target.dataset.dimension;
        const position = e.target.dataset.position;

        // Update slider position
        const minInput = document.getElementById(`${dimension}-min`);
        const maxInput = document.getElementById(`${dimension}-max`);
        const dimensionFilter = e.target.closest(".dimension-filter");
        const slider = dimensionFilter.querySelector(".dimension-slider");
        const sliderFill = dimensionFilter.querySelector(
          ".dimension-slider-fill"
        );
        const minHandle = dimensionFilter.querySelector(
          '.dimension-slider-handle[data-position="min"]'
        );
        const maxHandle = dimensionFilter.querySelector(
          '.dimension-slider-handle[data-position="max"]'
        );

        // Dynamic range based on slider dataset
        const sliderMin = parseFloat(slider.dataset.min) || 0;
        const sliderMax = parseFloat(slider.dataset.max) || 1; // avoid divide by zero
        const rangeSpan = sliderMax - sliderMin;

        const minValueRaw = minInput.value.replace(/[^0-9.]/g, ""); // strip non-numeric
        const maxValueRaw = maxInput.value.replace(/[^0-9.]/g, "");
        const minValue = parseFloat(minValueRaw) || sliderMin;
        const maxValue = parseFloat(maxValueRaw) || sliderMax;

        if (position === "min") {
          const minPercent = Math.min(
            Math.max(((minValue - sliderMin) / rangeSpan) * 100, 0),
            100
          );
          minHandle.style.left = `${minPercent}%`;
          sliderFill.style.left = `${minPercent}%`;
          sliderFill.style.width = `${Math.max(0, 100 - minPercent)}%`;
        } else {
          const maxPercent = Math.min(
            Math.max(((maxValue - sliderMin) / rangeSpan) * 100, 0),
            100
          );
          maxHandle.style.left = `${maxPercent}%`;
          sliderFill.style.width = `${Math.max(
            0,
            maxPercent - parseFloat(sliderFill.style.left)
          )}%`;
        }
      }
    });

    // Apply dimension filters on blur
    filtersContainer.addEventListener(
      "blur",
      (e) => {
        if (e.target.classList.contains("dimension-input")) {
          const field = e.target.dataset.field;
          const dimension = e.target.dataset.dimension;

          const minInput = document.getElementById(`${dimension}-min`);
          const maxInput = document.getElementById(`${dimension}-max`);

          const dimensionFilter = e.target.closest(".dimension-filter");
          const slider = dimensionFilter.querySelector(".dimension-slider");
          const sliderMin = parseFloat(slider.dataset.min) || 0;
          const sliderMax = parseFloat(slider.dataset.max) || 0;

          let minValue = parseFloat(minInput.value.replace(/[^0-9.]/g, ""));
          let maxRaw = maxInput.value;
          let maxNumericPart = parseFloat(maxRaw.replace(/[^0-9.]/g, ""));

          // Clamp values within slider range
          if (isNaN(minValue)) minValue = sliderMin;
          minValue = Math.max(sliderMin, Math.min(minValue, sliderMax));

          let maxValue;
          if (/\+$/.test(maxRaw.trim())) {
            maxValue = sliderMax; // always numeric for UI
          } else {
            maxValue = isNaN(maxNumericPart) ? sliderMax : maxNumericPart;
            maxValue = Math.min(Math.max(maxValue, minValue), sliderMax);
          }

          // Update inputs with clamped values
          minInput.value = minValue;
          maxInput.value = maxValue;

          // Update slider handles positions to reflect clamped values
          const minPercent =
            ((minValue - sliderMin) / (sliderMax - sliderMin)) * 100;
          const maxPercent =
            maxValue === "*"
              ? 100
              : ((maxValue - sliderMin) / (sliderMax - sliderMin)) * 100;

          const minHandle = dimensionFilter.querySelector(
            '.dimension-slider-handle[data-position="min"]'
          );
          const maxHandle = dimensionFilter.querySelector(
            '.dimension-slider-handle[data-position="max"]'
          );
          const sliderFill = dimensionFilter.querySelector(
            ".dimension-slider-fill"
          );
          if (minHandle && maxHandle && sliderFill) {
            minHandle.style.left = `${minPercent}%`;
            maxHandle.style.left = `${maxPercent}%`;
            sliderFill.style.left = `${minPercent}%`;
            sliderFill.style.width = `${maxPercent - minPercent}%`;
          }

          // Guard: only push filter when changed from defaults
          const defaultRange = `${sliderMin}-${sliderMax}`;
          const userRange = `${minValue}-${maxValue}`;

          if (userRange !== defaultRange) {
            if (!activeFilters.has(field)) {
              activeFilters.set(field, []);
            }

            // Remove any existing range values for this field
            activeFilters.set(
              field,
              activeFilters.get(field).filter((v) => !v.includes("-"))
            );

            activeFilters.get(field).push(userRange);

            utils.updateActiveFiltersDisplay(
              activeFiltersContainer,
              activeFilters,
              options
            );
            utils.syncFilterUIWithActiveFilters(filtersContainer, activeFilters);
            utils.applyFilters(activeFilters, options);
          }
        }
      },
      true
    );

    // Show more / Show less toggle for long lists
    filtersContainer.addEventListener("click", (e) => {
      const btn = e.target.closest(".show-more-btn");
      if (!btn) return;

      const list = btn.closest(".filter-drawer__list");
      if (!list) return;

      const hiddenItems = list.querySelectorAll(".filter-item--hidden");
      const isCollapsed = btn.dataset.state === "collapsed";

      hiddenItems.forEach((li) => {
        li.style.display = isCollapsed ? "block" : "none";
      });

      btn.textContent = isCollapsed ? "View less" : "View more";
      btn.dataset.state = isCollapsed ? "expanded" : "collapsed";
    });

    // inside setupFilterEventListeners, after the checkbox/change listeners
    filtersContainer.addEventListener("change", (e) => {
      if (!e.target.classList.contains("dimension-dropdown")) return;

      const field = e.target.dataset.field;
      const container = e.target.closest(".dimension-filter-dropdown");
      const minSel = container.querySelector('[data-position="min"]');
      const maxSel = container.querySelector('[data-position="max"]');

      const minVal = minSel.value || 0; // API treats 0 as open low
      const maxVal = maxSel.value || "*"; // "*" signals open high

      // update activeFilters map kept in setupFiltering
      activeFilters.set(
        field,
        minSel.value || maxSel.value ? [`${minVal}-${maxVal}`] : []
      );

      utils.updateActiveFiltersDisplay(
        activeFiltersContainer,
        activeFilters,
        options
      );
      utils.syncFilterUIWithActiveFilters(filtersContainer, activeFilters);
      utils.applyFilters(activeFilters, options);
    });
  },

  updateActiveFiltersDisplay: (
    activeFiltersContainer,
    activeFilters,
    options = null,
    filtersMainContainer = null
  ) => {
    // Persist the most recent options object so that nested or subsequent calls
    // (which may omit the argument) can still access the data required by
    // utils.applyFilters.
    if (options) {
      window.utils.lastApplyOptions = options;
    }

    // Fallback – use the last saved options when none are provided.
    const persistedOptions = window.utils.lastApplyOptions || {};

    // Find both active filter containers - the traditional one and the bar
    const filtersContainer = activeFiltersContainer;
    const filtersBarContainer = document.querySelector(".active-filters-bar");

    // Prefer the collection actions-row active filter display when present.
    const traditionalActiveFilters =
      activeFiltersContainer ||
      document.querySelector(
        ".common-filters-actions-row .active-filters-display"
      ) ||
      document.querySelector(".active-filters-display");
    const traditionalActiveFiltersList = document.querySelector(
      ".active-filters-list"
    );

    if (!filtersContainer && !filtersBarContainer && !traditionalActiveFilters)
      return;

    // Update clear button state
    const clearBtn = document.querySelector(".searchspring-clear-all");
    if (clearBtn) {
      if (activeFilters.size > 0) {
        clearBtn.classList.add("active");
      } else {
        clearBtn.classList.remove("active");
      }
    }

    // Create HTML for filter tags
    let activeFiltersHTML = "";
    let resetButtonHTML = "";

    // If there are active filters, show the reset button
    if (activeFilters.size > 0) {
      resetButtonHTML = `
				<button class="active-filters-reset" data-action="reset-all">
					Reset all
				</button>
			`;
    }

    // Map facet fields to display names
    const facetLabels = {
      ss_price: "Price",
      vendor: "Brand",
      mfield_custom_primary_style: "Style",
      mfield_new_my_fields_sub_category: "Product Type",
      variant_mfield_new_my_fields_filter_color: "Color",
      variant_mfield_custom_rug_size_bucket_filter: "Size",
      variant_mfield_custom_width_filter: "Width",
      variant_mfield_custom_variant_width_rounded_ft_filter: "Width (ft.)",
      variant_mfield_custom_variant_length_rounded_ft_filter: "Length",
      mfield_custom_rug_pattern: "Pattern",
    };

    activeFilters.forEach((values, field) => {
      // Special handling for Quickship (lead time)
      const QUICKSHIP_SET = new Set(
        [
          "1-2 weeks",
          "1-3 weeks",
          "1-3 Weeks",
          "2-4 weeks",
          "3-4 weeks",
          "4 weeks",
        ].map((s) => s.toLowerCase())
      );

      const shown = new Set();
      const fieldLabel = facetLabels[field] || field;

      // Aggregate Quickship tag once per field
      // if (field === "variant_mfield_new_my_fields_lead_time") {
      //   const quickshipActive = values.filter((v) =>
      //     QUICKSHIP_SET.has(String(v).toLowerCase())
      //   );
      //   if (quickshipActive.length) {
      //     const aggregated = [
      // 	'1-2 weeks',
      // 	'1-3 weeks',
      // 	'1-3 Weeks',
      // 	'2-4 weeks',
      // 	'3-4 weeks',
      // 	'4 weeks',
      // ].join(',');
      //     const dedupeKeyAgg = `${field}__quickship`;
      //     if (!shown.has(dedupeKeyAgg)) {
      //       shown.add(dedupeKeyAgg);
      //       activeFiltersHTML += `
      //         <span class="active-filter-tag" data-field="${field}" data-value="${aggregated}">
      //           Quickship
      //           <button class="remove-filter" data-field="${field}" data-value="${aggregated}">×</button>
      //         </span>
      //       `;
      //     }
      //   }
      // }
      const widthBucketValues = [
        "under 20",
        "20 - 24",
        "25-29",
        "30-34",
        "35-39",
        "40-44",
        "45-49",
        "50-54",
        "55-59",
        "60-69",
        "70-79",
        "80-89",
        "90-99",
        "100-109",
        "110-119",
        "120-129",
        "130+",
      ];

      values.forEach((value) => {
        // Skip individual quickship values (already aggregated)
        // if (
        //   field === "variant_mfield_new_my_fields_lead_time" &&
        //   QUICKSHIP_SET.has(String(value).toLowerCase())
        // ) {
        //   return;
        // }

        let displayValue = value;

        // Format display values
        if (field === "ss_price") {
          // Price display
          let [min, max] = value.split("-");

          // Detect open-ended (max is * or contains +)
          const isOpenEnded = max === "*" || /\+$/.test(max);

          if (isOpenEnded) {
            // Show as "$min+" (no upper bound)
            displayValue = `$${min}+`;
          } else {
            // Otherwise standard min-max
            displayValue = `$${min} - $${max}`;
          }
        } else if (field === "variant_mfield_new_my_fields_filter_color") {
          // Color display - extract color name from JSON format
          try {
            // Handle formats like: ["Color"] or ["Color1"]|["Color2"]
            let colorName = value;

            // If it contains JSON-like brackets, try to parse
            if (value.includes("[") && value.includes("]")) {
              const parsed = JSON.parse(value.split("|")[0]);
              colorName = Array.isArray(parsed) ? parsed[0] : parsed;
            } else {
              // Simple cleanup for non-JSON formats
              colorName = value.replace(/\[|\]|"/g, "").split("|")[0];
            }

            displayValue = colorName;
          } catch (e) {
            // Fallback: just clean up the value
            displayValue = value.replace(/\[|\]|"/g, "").split("|")[0];
          }
        } else if (field === "variant_mfield_custom_width_filter") {
          // Map numeric widths to bucket label
          const bucketForNum = (n) => {
            if (isNaN(n)) return null;
            if (n < 20) return 'Under 20"';
            if (n < 25) return '20 - 24"';
            if (n < 30) return '25-29"';
            if (n < 35) return '30-34"';
            if (n < 40) return '35-39"';
            if (n < 45) return '40-44"';
            if (n < 50) return '45-49"';
            if (n < 55) return '50-54"';
            if (n < 60) return '55-59"';
            if (n < 70) return '60-69"';
            if (n < 80) return '70-79"';
            if (n < 90) return '80-89"';
            if (n < 100) return '90-99"';
            if (n < 110) return '100-109"';
            if (n < 120) return '110-119"';
            if (n < 130) return '120-129"';
            return '130+"';
          };

          const numeric = parseFloat(value);
          const label = bucketForNum(numeric);
          displayValue = label || `${value}"`;

          // Build aggregated list of all widths in this bucket
          const aggregated = values
            .filter((v) => bucketForNum(parseFloat(v)) === label)
            .join(",");

          value = aggregated; // use aggregated list as data-value for chip & removal
        } else if (
          field === "mfield_custom_length_rounded_ft_filter" ||
          field === "mfield_custom_width_rounded_ft_filter"
        ) {
          // Dimension display
          const [min, max] = value.split("-");
          displayValue = `${min} - ${max} ft`;
        } else if (value.includes("|")) {
          // Other pipe-separated values
          displayValue = value.split("|")[0];
        }

        // Clean up any category paths
        if (displayValue.includes(">")) {
          displayValue = displayValue.split(">").pop().trim();
        }

        // Avoid showing duplicates for Standard Sizes (size bucket filter)
        const dedupeKey = `${field}__${displayValue}`;
        if (!shown.has(dedupeKey)) {
          shown.add(dedupeKey);

          activeFiltersHTML += `
					<span class="active-filter-tag" data-field="${field}" data-value="${value}">
						${displayValue}
						<button class="remove-filter" data-field="${field}" data-value="${value}">×</button>
					</span>
				`;
        }
      });
    });

    const isMobile = window.innerWidth <= 768;

    // Update all containers
    if (filtersContainer) {
      filtersContainer.innerHTML = activeFiltersHTML;
      if (filtersContainer.classList.contains("active-filters-display")) {
        filtersContainer.style.display = activeFiltersHTML ? "flex" : "none";
      }
    }

    if (filtersBarContainer && !isMobile) {
      filtersBarContainer.innerHTML = activeFiltersHTML;
      filtersBarContainer.style.display = activeFiltersHTML ? "flex" : "none";
    }

    // Update traditional active filters display only on desktop
    if (!isMobile && traditionalActiveFiltersList) {
      traditionalActiveFiltersList.innerHTML =
        resetButtonHTML + activeFiltersHTML;
    }

    if (!isMobile && traditionalActiveFilters) {
      traditionalActiveFilters.style.display = activeFiltersHTML
        ? "flex"
        : "none";
    } else if (
      isMobile &&
      traditionalActiveFilters &&
      traditionalActiveFilters !== filtersContainer
    ) {
      traditionalActiveFilters.style.display = "none";
    }

    // Add event listeners for removing filters and reset button
    const addRemoveListeners = (container) => {
      if (!container) return;

      // Handle individual filter removal
      const removeButtons = container.querySelectorAll(".remove-filter");
      removeButtons.forEach((button) => {
        button.addEventListener("click", () => {
          const field = button.dataset.field;
          const value = button.dataset.value;

          if (activeFilters.has(field)) {
            const values = activeFilters.get(field);

            if (value.includes(",")) {
              // Handle comma-separated values – remove all underlying selections
              const valuesToRemove = value.split(",");
              activeFilters.set(
                field,
                values.filter((v) => !valuesToRemove.includes(v))
              );
            } else {
              const index = values.indexOf(value);
              if (index > -1) {
                values.splice(index, 1);
              }
            }

            if (activeFilters.get(field).length === 0) {
              activeFilters.delete(field);
            }

            // Update UI elements
            if (field === "variant_mfield_new_my_fields_filter_color") {
              // Update color swatch selection
              const colorSwatches = document.querySelectorAll(
                ".filter-item__swatch"
              );
              colorSwatches.forEach((swatch) => {
                if (swatch.dataset.value === value) {
                  swatch.classList.remove("selected");
                }
              });
            } else {
              // Update checkbox state
              const checkbox = document.querySelector(
                `input[data-field="${field}"][data-value="${value}"]`
              );
              if (checkbox) {
                checkbox.checked = false;
              }
            }

            utils.updateActiveFiltersDisplay(
              activeFiltersContainer,
              activeFilters,
              persistedOptions
            );
            utils.applyFilters(activeFilters, persistedOptions);
          }
        });
      });

      // Handle reset all button
      const resetButton = container.querySelector(".active-filters-reset");
      if (resetButton) {
        resetButton.addEventListener("click", () => {
          // Clear all active filters
          activeFilters.clear();

          // Clear sessionStorage to prevent state restoration on navigation
          try {
            sessionStorage.removeItem("plpActiveFilters");
          } catch (err) {
            console.error("Error clearing session storage", err);
          }

          // Clear global filter params
          window.utils.filterParams = "";

          // Clear URL parameters - remove all filter.* params, keep sort and page
          const url = new URL(window.location.href);
          const paramsToRemove = [];
          url.searchParams.forEach((value, key) => {
            if (key.startsWith("filter.")) {
              paramsToRemove.push(key);
            }
          });
          paramsToRemove.forEach((key) => url.searchParams.delete(key));
          window.history.replaceState({}, "", url);

          // Uncheck all checkboxes
          const checkboxes = document.querySelectorAll("[data-filter-item]");
          checkboxes.forEach((checkbox) => {
            checkbox.checked = false;
            checkbox.removeAttribute("checked");
          });

          // Deselect all color swatches
          const colorSwatches = document.querySelectorAll(
            ".filter-item__swatch"
          );
          colorSwatches.forEach((swatch) =>
            swatch.classList.remove("selected")
          );

          // Reset all dimension sliders
          window.utils.resetAllDimensionFilters();

          // Update display and apply filters
          utils.updateActiveFiltersDisplay(
            activeFiltersContainer,
            activeFilters,
            persistedOptions
          );
          utils.applyFilters(activeFilters, persistedOptions);

          // After applyFilters completes and renderFilters runs, ensure all checkboxes stay unchecked
          setTimeout(() => {
            const allCheckboxesAfterRender = document.querySelectorAll("[data-filter-item]");
            allCheckboxesAfterRender.forEach((checkbox) => {
              checkbox.checked = false;
              checkbox.removeAttribute("checked");
            });
          }, 100);
        });
      }
    };

    // Add listeners to all containers
    addRemoveListeners(filtersContainer);
    addRemoveListeners(filtersBarContainer);
    addRemoveListeners(traditionalActiveFiltersList);

    // Update Filter button count specific to section
    const countFilters = Array.from(activeFilters.values()).reduce(
      (sum, arr) => sum + arr.length,
      0
    );
    if (filtersContainer && filtersContainer.id) {
      const idSegments = filtersContainer.id.split("-");
      const sectionId = idSegments[idSegments.length - 1];
      if (sectionId) {
        const toggleBtn = document.getElementById(`filter-toggle-${sectionId}`);
        if (toggleBtn) {
          const labelSpan = toggleBtn.querySelector("span");
          if (labelSpan) {
            labelSpan.textContent =
              countFilters > 0 ? `Filter (${countFilters})` : "Filter";
          }
        }
      }
    }

    // Toggle visibility of mobile active filters wrapper if present
    if (filtersContainer) {
      const mobileWrapper = filtersContainer.closest(".mobile-active-filters");
      if (mobileWrapper) {
        mobileWrapper.style.display = activeFiltersHTML ? "block" : "none";
      }
    }

    // Toggle visibility of the "Reset all" link next to the filter toggle
    if (persistedOptions && persistedOptions.sectionId) {
      const resetBtn = document.getElementById(
        `reset-all-${persistedOptions.sectionId}`
      );
      if (resetBtn) {
        resetBtn.style.display = activeFilters.size > 0 ? "inline" : "none";
      }
    } else {
      // Fallback – toggle all reset-all links if sectionId is unavailable
      document
        .querySelectorAll(".reset-all:not(.show-more-btn)")
        .forEach((btn) => {
          btn.style.display = activeFilters.size > 0 ? "inline" : "none";
        });
    }

    // Additionally toggle any mobile reset links for this section (class .mobile-reset-all)
    document
      .querySelectorAll(
        `.mobile-reset-all#reset-all-${persistedOptions.sectionId}`
      )
      .forEach((btn) => {
        btn.style.display = activeFilters.size > 0 ? "inline" : "none";
      });

    // Hide the entire actions row when there are no active filters to avoid empty whitespace
    document.querySelectorAll(".common-filters-actions-row").forEach((row) => {
      row.style.display = activeFilters.size > 0 ? "" : "none";
    });

    const mobileActiveFiltersList = document.querySelector(
      ".mobile-active-filters-list"
    );
    const allActiveFilters = mobileActiveFiltersList
      ? mobileActiveFiltersList.querySelectorAll(".active-filter-tag")
      : [];
    const allActiveFiltersCount = allActiveFilters.length;
    // -------- mobile count badge ----------
    const mobileCountEls = document.querySelectorAll(
      ".active-filters-count-mobile"
    );
    if (allActiveFiltersCount > 0) {
      mobileCountEls.forEach((el) => {
        el.textContent = `(${allActiveFiltersCount})`;
      });
    } else {
      mobileCountEls.forEach((el) => {
        el.textContent = ``;
      });
    }
  },

  applyFilters: (activeFilters, options) => {
    const {
      baseUrl,
      cleanBaseUrl,
      productGrid,
      paginationContainer,
      productsPerPage,
      showLoading,
      hideLoading,
      loadProducts,
      totalCountElement,
      // NEW: needed to refresh facets after applying filters
      filtersContainer,
      siteId,
      collectionHandle,
      sectionId,
      paginationType,
    } = options;

    const baseForApi = cleanBaseUrl || baseUrl;

    let filterParams = ``;

    // Add active filters to URL
    activeFilters.forEach((values, field) => {
      values.forEach((value) => {
        if (
          field === "ss_price" ||
          field === "mfield_custom_length_rounded_ft_filter" ||
          field === "mfield_custom_width_rounded_ft_filter" ||
          field === "ss_all_width_filter"
        ) {
          // Handle numeric range fields
          const [min, max] = value.split("-");
          if (min && min !== "0") filterParams += `&filter.${field}.low=${min}`;
          if (max) {
            filterParams += `&filter.${field}.high=${max}`;
          }
          // If max is * (open ended) we omit high param so API treats it as open upper bound
        } else if (field === "variant_mfield_new_my_fields_filter_color") {
          filterParams += `&filter.${field}=${encodeURIComponent(value)}`;
        } else {
          filterParams += `&filter.${field}=${encodeURIComponent(value)}`;
        }
      });
    });

    const globalUrl = window.utils.globalUrl(
      baseForApi,
      (page = 1),
      productsPerPage
    );
    window.utils.filterParams = filterParams;
    const url = `${globalUrl}${filterParams}${
      window.utils.sortParam ? `&${window.utils.sortParam}` : ""
    }`;

    const scrollToTop = () => window.scrollTo({ top: 0, behavior: "instant" });
    scrollToTop();

    // Persist current filter and sort state
    utils.saveActiveFilters(activeFilters, options.collectionHandle || "");

    const handleFilterResponse = (data) => {
      // If API returned facets, re-render filters with latest counts immediately
      if (data && Array.isArray(data.facets)) {
        utils.commonFilterFacets = data.facets;
        utils.renderFilters(data.facets, filtersContainer);
        utils.syncFilterUIWithActiveFilters(filtersContainer, activeFilters);
        utils.setDimensionFiltersFromActive(activeFilters);
      } else {
        // Fallback: fetch facets using the same URL
        utils
          .loadFacets(url, siteId, collectionHandle)
          .then((facets) => {
            utils.commonFilterFacets = facets;
            utils.renderFilters(facets, filtersContainer);
            utils.syncFilterUIWithActiveFilters(filtersContainer, activeFilters);
            utils.setDimensionFiltersFromActive(activeFilters);
          })
          .catch((err) => console.error("Error refreshing facets:", err));
      }
    };

    if (paginationType === "infinite_scroll") {
      utils.refreshInfiniteScroll(sectionId, {
        baseUrl,
        onData: handleFilterResponse,
      });
    } else {
      // Apply filters and update facets instantly from the same response
      loadProducts({
        page: 1,
        baseUrl: url,
        productsPerPage,
        productGrid,
        paginationContainer,
        showLoading,
        hideLoading,
        callback: handleFilterResponse,
        totalCountElement,
        filterParams,
        source: "filter-apply",
        sectionId,
      });
    }

    (() => {
      const newUrl = new URL(window.location.pathname, window.location.origin);
      let search = "?page=1"; // always reset to page 1
      if (filterParams) search += `${filterParams}`; // e.g. &filter.color=Blue
      if (window.utils.sortParam) search += `&${window.utils.sortParam}`;
      newUrl.search = search;
      window.history.pushState({}, "", newUrl);
    })();
  },

  // === helper to reset a single dimension filter (price/width/length) ===
  resetDimensionFilter: (field) => {
    const inputs = document.querySelectorAll(
      `.dimension-input[data-field="${field}"]`
    );
    if (!inputs || inputs.length === 0) return;

    const dimensionFilter = inputs[0].closest(".dimension-filter");
    if (!dimensionFilter) return;

    const slider = dimensionFilter.querySelector(".dimension-slider");
    const sliderFill = dimensionFilter.querySelector(".dimension-slider-fill");
    const minHandle = dimensionFilter.querySelector(
      '.dimension-slider-handle[data-position="min"]'
    );
    const maxHandle = dimensionFilter.querySelector(
      '.dimension-slider-handle[data-position="max"]'
    );

    const sliderMin = parseFloat(slider.dataset.min) || 0;
    const sliderMax = parseFloat(slider.dataset.max) || 0;

    // Update input fields
    inputs.forEach((input) => {
      const dim = input.dataset.position; // min or max
      const isPrice = input.dataset.dimension === "price";
      if (dim === "min") {
        input.value = sliderMin;
      } else {
        input.value = sliderMax;
      }
    });

    // Reset slider visuals
    if (minHandle) minHandle.style.left = "0%";
    if (maxHandle) maxHandle.style.left = "100%";
    if (sliderFill) {
      sliderFill.style.left = "0%";
      sliderFill.style.width = "100%";
    }
  },

  // Reset all dimension filters on page
  resetAllDimensionFilters: () => {
    document.querySelectorAll(".dimension-input").forEach((input) => {
      window.utils.resetDimensionFilter(input.dataset.field);
    });
  },

  renderWidthBucketFilter: (facet) => {
    // We are no longer bucketing widths. We'll build a map of unique numeric widths and aggregate their counts.

    const buckets = []; // kept for backward compatibility but unused further down

    // Helper to extract numeric inches from a raw facet value token
    const toNumber = (token) => {
      const num = parseFloat(String(token).replace(/[^0-9.]/g, ""));
      return isNaN(num) ? null : num;
    };

    // Build a map of unique numeric widths -> aggregated { count, active }
    const widthMap = new Map();

    (facet.values || []).forEach((facetValue) => {
      if (!facetValue) return;

      const raw = String(facetValue.value || "");

      // Ignore multi-value tokens that contain the pipe separator
      if (raw.includes("|")) return;

      // Ignore tokens that include an inch symbol, e.g., '20"'
      if (raw.includes('"')) return;

      const n = toNumber(raw);
      if (n === null) return; // skip unparsable values

      const key = n; // numeric key; JS Map treats 20 and 20.0 the same

      const entry = widthMap.get(key) || {
        count: 0,
        active: false,
        // Prefer the first encountered raw string as a label; fallback later
        label: undefined,
      };

      entry.count += facetValue.count;
      if (facetValue.active) entry.active = true;

      // Capture a display label once (e.g., "36" vs 36). If none stored yet, use current raw.
      if (!entry.label) {
        entry.label = raw;
      }

      widthMap.set(key, entry);
    });

    // Convert widthMap into a sorted array of [num, data]
    const entries = Array.from(widthMap.entries()).sort((a, b) => a[0] - b[0]);

    // group entries in under 20 , 20 - 24, 25-29, 30-34, 35-39, 40-44, 45-49, 50-54, 55-59, 60-69 , 70-79 , 80-89, 90-99, 100-109, 110-119, 120-129, 130+

    const groupedEntries = entries.reduce((acc, [num, data]) => {
      const key =
        num < 20
          ? "under 20"
          : num < 25
          ? "20 - 24"
          : num < 30
          ? "25-29"
          : num < 35
          ? "30-34"
          : num < 40
          ? "35-39"
          : num < 45
          ? "40-44"
          : num < 50
          ? "45-49"
          : num < 55
          ? "50-54"
          : num < 60
          ? "55-59"
          : num < 70
          ? "60-69"
          : num < 80
          ? "70-79"
          : num < 90
          ? "80-89"
          : num < 100
          ? "90-99"
          : num < 110
          ? "100-109"
          : num < 120
          ? "110-119"
          : num < 130
          ? "120-129"
          : "130+";
      if (!acc[key]) acc[key] = [];
      acc[key].push([num, data]);
      return acc;
    }, {});

    const groupDisplay = Object.entries(groupedEntries).map(([key, value]) => {
      return {
        label: key,
        count: value.reduce((sum, [num, data]) => sum + data.count, 0),
        active: value.some(([num, data]) => data.active),
        values: value.map(([num, data]) => data.label || `${num}`), // collect underlying raw labels
      };
    });

    // Desired display order
    const displayOrder = [
      "under 20",
      "20 - 24",
      "25-29",
      "30-34",
      "35-39",
      "40-44",
      "45-49",
      "50-54",
      "55-59",
      "60-69",
      "70-79",
      "80-89",
      "90-99",
      "100-109",
      "110-119",
      "120-129",
      "130+",
    ];

    groupDisplay.sort(
      (a, b) => displayOrder.indexOf(a.label) - displayOrder.indexOf(b.label)
    );

    const MAX_VISIBLE = 12;

    const itemsHTML = groupDisplay
      .map(({ label, count, active, values }, idx) => {
        const joinedValues = values.join(","); // what will actually be sent to Searchspring
        const isDisabled = count === 0;
        const inputId = `filter--${facet.field}--${label.replace(
          /[^a-zA-Z0-9]/g,
          ""
        )}--searchspring`;
        const hiddenClass = idx >= MAX_VISIBLE ? "filter-item--hidden" : "";
        // Friendly label for UI
        const displayLabel =
          label.toLowerCase() === "under 20" ? 'Under 20"' : `${label}"`;

        return `
          <li class="filter-item filter-item-checkbox ${hiddenClass} ${
          isDisabled ? "filter-item--disabled" : ""
        }" data-type="checkbox" ${hiddenClass ? 'style="display:none;"' : ""}>
            <label
              data-filter
              data-filter-item-label
              class="filter-item__content filter-item__content--checkbox no-transition ${
                isDisabled ? "filter-item__content--disabled" : ""
              }"
              for="${inputId}"
            >
              <input
                type="checkbox"
                name="${facet.field}"
                data-value-escaped="${encodeURIComponent(joinedValues)}"
                value="${joinedValues}"
                class="filter-item__input visually-hidden"
                id="${inputId}"
                data-field="${facet.field}"
                data-value="${joinedValues}"
                ${active ? "checked" : ""}
                ${isDisabled ? "disabled" : ""}
                data-filter="${facet.field}=${joinedValues}"
                data-filter-item
              >
              <div class="filter-item__checkbox">
                <svg class="icon icon-check" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="m1 3 2.5 2.5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <span class="filter-item__label type-body-regular">${displayLabel}</span>
              <span class="filter-item__count">${count}</span>
            </label>
          </li>`;
      })
      .join("");

    const needsShowMore = groupDisplay.length > MAX_VISIBLE;
    const showMoreHTML = needsShowMore
      ? `<li class="filter-show-more"><a class="reset-all show-more-btn" style="cursor: pointer;" data-state="collapsed">View more</a></li>`
      : "";

    return `
      <ul class="filter-drawer__list">
        ${itemsHTML}
        ${showMoreHTML}
      </ul>
    `;
  },

  // Quickship special filter – consolidate lead time < 4 weeks into a single chip
  renderQuickshipFilter: (facet) => {
    // Define which lead-time strings qualify for Quickship (case-insensitive)
    const QS_VALUES = [
      "1-2 weeks",
      "1-3 weeks",
      "1-3 Weeks",
      "2-4 weeks",
      "3-4 weeks",
      "4 weeks",
    ];

    const quickshipValues = facet.values.filter((v) =>
      QS_VALUES.some((q) => q.toLowerCase() === String(v.value).toLowerCase())
    );

    const count = quickshipValues.reduce((sum, v) => sum + v.count, 0);
    const isActive = quickshipValues.some((v) => v.active);

    const valuesStr = QS_VALUES.join(",");
    const inputId = `filter--${facet.field}--quickship-searchspring`;
    const isDisabled = count === 0;

    return `
      <ul class="filter-drawer__list quickship-filter-list">
        <li class="filter-item filter-item-chip ${
          isDisabled ? "filter-item--disabled" : ""
        }" data-type="chip">
          <label
            data-filter
            data-filter-item-label
            class="filter-item__content filter-item__content--chip no-transition ${
              isDisabled ? "filter-item__content--disabled" : ""
            }"
            for="${inputId}"
          >
            
            <div class="filter-item__chip" tabindex="0" style="margin-left: 24px;" aria-label="Quickship">Quickship
            <input
              type="checkbox"
              name="${facet.field}"
              data-value-escaped="${encodeURIComponent(valuesStr)}"
              value="${valuesStr}"
              class="filter-item__input visually-hidden"
              id="${inputId}"
              data-field="${facet.field}"
              data-value="${valuesStr}"
              ${isActive ? "checked" : ""}
              ${isDisabled ? "disabled" : ""}
              data-filter="${facet.field}=${valuesStr}"
              data-filter-item
            >
            </div>
          </label>
        </li>
      </ul>
    `;
  },

  renderDropdownDimensionFilter: function (facet, dimensionType) {
    const numericVals = Array.from(
      new Set(
        (facet.values || [])
          .map((v) => parseFloat(v.value))
          .filter((n) => !isNaN(n))
      )
    ).sort((a, b) => a - b);

    if (!numericVals.length) return "";

    const optionHTML = numericVals
      .map((n) => `<option value="${n}">${n}</option>`)
      .join("");

    const reverseOptionHTML = numericVals
      .reverse()
      .map((n) => `<option value="${n}">${n}</option>`)
      .join("");

    return `
      <div class="dimension-filter-dropdown">
        <div class="dimension-inputs">
          
          <div class="dimension-input-group">
          <span class="dimension-label">Ft.</span>
            <select class="dimension-dropdown" data-field="${facet.field}" data-dimension="${dimensionType}" data-position="min">
              ${optionHTML}
            </select>
            <span class="select-arrow">▼</span>
            
          </div>
          <span class="dimension-to">to</span>
          <div class="dimension-input-group">
          <span class="dimension-label">Ft.</span>
            <select class="dimension-dropdown" data-field="${facet.field}" data-dimension="${dimensionType}" data-position="max">
               ${reverseOptionHTML}
            </select>
            <span class="select-arrow">▼</span>
            
          </div>
        </div>
      </div>
    `;
  },

  saveActiveFilters: function (activeFilters, collectionHandle = "") {
    try {
      const filtersObj = {};
      if (activeFilters && typeof activeFilters.forEach === "function") {
        activeFilters.forEach((vals, field) => {
          filtersObj[field] = vals;
        });
      }
      sessionStorage.setItem(
        "plpActiveFilters",
        JSON.stringify({
          handle: collectionHandle || window.location.pathname,
          filters: filtersObj,
          sortParam: window.utils.sortParam || "",
        })
      );
    } catch (err) {
      console.error("Error saving filter state", err);
    }
  },

  loadSavedFilters: function (currentHandle = "") {
    try {
      const raw = sessionStorage.getItem("plpActiveFilters");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const handleToCompare = currentHandle || window.location.pathname;
      if (parsed && parsed.handle === handleToCompare) return parsed;
      // handle mismatch – clear stale state and ignore.
      sessionStorage.removeItem("plpActiveFilters");
      return null;
    } catch (err) {
      console.error("Error loading filter state", err);
      return null;
    }
  },

  syncFilterUIWithActiveFilters: function (filtersContainer, activeFilters) {
    if (!(activeFilters instanceof Map)) return;

    const findElementsByFieldAndValue = (selector, field, value) => {
      const fieldStr = String(field ?? "");
      const valueStr = String(value ?? "");

      // Query by field only, then match value via dataset/getAttribute to avoid CSS selector escaping issues.
      const fieldSelector =
        typeof CSS !== "undefined" && typeof CSS.escape === "function"
          ? `${selector}[data-field="${CSS.escape(fieldStr)}"]`
          : `${selector}[data-field="${fieldStr.replace(/"/g, '\\"')}"]`;

      const matches = document.querySelectorAll(fieldSelector);
      return Array.from(matches).filter(
        (el) => (el.getAttribute("data-value") || "") === valueStr
      );
    };

    // 1) Document-wide clear so desktop + mobile stay in sync (Reset shows cleared state)
    document.querySelectorAll("[data-filter-item]").forEach((el) => {
      el.checked = false;
      el.removeAttribute("checked");
    });
    document.querySelectorAll(".filter-item__swatch").forEach((swatch) => {
      swatch.classList.remove("selected");
    });

    // 2) Apply only activeFilters (document-wide so both sidebar and drawer match)
    activeFilters.forEach((values, field) => {
      values.forEach((value) => {
        findElementsByFieldAndValue("input", field, value).forEach(
          (checkbox) => {
            checkbox.checked = true;
          }
        );

        findElementsByFieldAndValue(
          ".filter-item__swatch",
          field,
          value
        ).forEach((swatch) => {
          swatch.classList.add("selected");
        });
      });
    });
  },

  // === NEW helper: render checkboxes for ss_all_width_filter ===
  renderWidthRangeFilter: (facet) => {
    const MAX_VISIBLE = 12;

    const itemsHTML = (facet.values || [])
      .map((v, idx) => {
        const label = v.label || `${v.low}-${v.high}`;
        const isDisabled = v.count === 0 && !v.active;
        const inputId = `filter--${facet.field}--${label.replace(
          /[^a-zA-Z0-9]/g,
          ""
        )}--searchspring`;
        const hiddenClass = idx >= MAX_VISIBLE ? "filter-item--hidden" : "";

        // store low-high in value
        const valStr = `${v.low}-${v.high}`;

        return `
        <li class="filter-item filter-item-checkbox ${hiddenClass} ${
          isDisabled ? "filter-item--disabled" : ""
        }" data-type="checkbox" ${hiddenClass ? 'style="display:none;"' : ""}>
          <label data-filter data-filter-item-label class="filter-item__content filter-item__content--checkbox no-transition ${
            isDisabled ? "filter-item__content--disabled" : ""
          }" for="${inputId}">
            <input type="checkbox" name="${
              facet.field
            }" data-value-escaped="${encodeURIComponent(
          valStr
        )}" value="${valStr}" class="filter-item__input visually-hidden" id="${inputId}" data-field="${
          facet.field
        }" data-value="${valStr}" ${v.active ? "checked" : ""} ${
          isDisabled ? "disabled" : ""
        } data-filter="${facet.field}=${valStr}" data-filter-item>
            <div class="filter-item__checkbox"><svg class="icon icon-check" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="m1 3 2.5 2.5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div>
            <span class="filter-item__label type-body-regular">${label}</span>
            <span class="filter-item__count">${v.count}</span>
          </label>
        </li>`;
      })
      .join("");

    const needsShowMore = (facet.values || []).length > MAX_VISIBLE;
    const showMoreHTML = needsShowMore
      ? `<li class="filter-show-more"><a class="reset-all show-more-btn" style="cursor: pointer;" data-state="collapsed">View more</a></li>`
      : "";

    return `<ul class="filter-drawer__list">${itemsHTML}${showMoreHTML}</ul>`;
  },

  // Initialize Add Sample button handlers for PLP
  initSampleButtons: function() {
    document.addEventListener('click', async (event) => {
      const button = event.target.closest('.custom-sample-button');
      if (!button) return;

      // If button is inside product card (PLP), handle it
      const isInProductCard = button.closest('.product-card__add-sample');
      if (!isInProductCard) return; // Let PDP handler in custom.js handle PDP buttons

      // Prevent multiple clicks
      if (button.disabled || button.dataset.processing === 'true') {
        return;
      }

      event.stopPropagation();
      event.preventDefault();

      const sampleGid = button.dataset.sampleGid;
      const productColor = button.dataset.productColor;
      const originalButtonText = button.textContent;
      
      button.textContent = 'Adding...';
      button.disabled = true;
      button.dataset.processing = 'true';

      if (!sampleGid) {
        button.textContent = originalButtonText;
        button.disabled = false;
        button.dataset.processing = 'false';
        return;
      }

      try {
        // Step 1: Get product handle from GID
        let sampleHandle = await utils.fetchProductHandleFromGid(sampleGid);
        
        // Step 1b: If GraphQL fails, try fallback patterns
        if (!sampleHandle) {
          const productHandle = button.dataset.productHandle;
          const fallbackHandles = [
            `${productHandle}-swatch`,
            `${productHandle.replace('-custom-carpet', '')}-swatch`,
            productHandle.replace('custom-carpet', 'swatch')
          ];
          
          // Try each fallback handle
          for (const testHandle of fallbackHandles) {
            try {
              const testResponse = await fetch(`/products/${testHandle}.js`);
              if (testResponse.ok) {
                sampleHandle = testHandle;
                break;
              }
            } catch (e) {
              // Continue to next pattern
            }
          }
          
          if (!sampleHandle) {
            button.closest('.product-card__add-sample')?.remove();
            return;
          }
        }

        // Step 2: Fetch product data
        const response = await fetch(`/products/${sampleHandle}.js`);
        if (!response.ok) {
          button.closest('.product-card__add-sample')?.remove();
          return;
        }
        const sampleProduct = await response.json();

        if (!sampleProduct?.variants?.length) {
          button.closest('.product-card__add-sample')?.remove();
          return;
        }

        // Step 3: Match variant by color
        let matchedVariant = null;
        if (productColor) {
          matchedVariant = sampleProduct.variants.find(v => 
            v.available && v.title.toLowerCase().includes(productColor.toLowerCase())
          );
        }
        if (!matchedVariant) {
          matchedVariant = sampleProduct.variants.find(v => v.available);
        }
        if (!matchedVariant) {
          button.textContent = 'Unavailable';
          button.disabled = true;
          button.dataset.processing = 'false';
          return;
        }

        // Step 4: Check if sample already in cart
        const currentCartResponse = await fetch('/cart.js');
        const currentCart = await currentCartResponse.json();
        
        const alreadyInCart = currentCart.items?.some(item => 
          item.variant_id === matchedVariant.id
        );
        
        if (alreadyInCart) {
          button.textContent = '✓ Added';
          setTimeout(() => {
            button.textContent = originalButtonText;
            button.disabled = false;
            button.dataset.processing = 'false';
          }, 2000);
          return;
        }

        // Step 5: Add to cart
        const cartResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: matchedVariant.id, quantity: 1 }]
          })
        });

        if (!cartResponse.ok) {
          const errorData = await cartResponse.json();
          throw new Error(errorData.description || 'Failed to add to cart');
        }

        // Step 6: Trigger theme's cart update event
        document.dispatchEvent(new CustomEvent('cart:update'));
        window.dispatchEvent(new CustomEvent('cart:update'));
        
        const cartUpdatedEvent = new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cartOpen: true }
        });
        document.dispatchEvent(cartUpdatedEvent);

        // Success state
        button.textContent = '✓ Added';
        setTimeout(() => {
          button.textContent = originalButtonText;
          button.disabled = false;
          button.dataset.processing = 'false';
        }, 2000);

      } catch (error) {
        console.error("Sample add error:", error);
        button.textContent = 'Try Again';
        setTimeout(() => {
          button.textContent = originalButtonText;
          button.disabled = false;
          button.dataset.processing = 'false';
        }, 2000);
      }
    });
  },
};

// Initialize sample buttons when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    utils.initSampleButtons();
  });
} else {
  utils.initSampleButtons();
}
